import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Charts
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area } from 'recharts';

// UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CheckCircle, CheckCircle2, Loader2, Settings, Users, Globe, Trash2, Plus, Download, Send, GraduationCap, Plane, AlertCircle, Mic, Square, Play, Pause, X, FileText, ExternalLink, MapPin, Edit, Eye, MessageCircle, MessageSquare, BarChart3, TrendingUp, Clock, Calendar, Phone, Menu, Home, Search, Mail, Newspaper, RefreshCw, BookOpen, ChevronLeft, ChevronRight, Sparkles, Link2, Bot } from "lucide-react";

// Refactored Components
import TrafficDashboard from "@/components/admin/TrafficDashboard";
import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import VisitorTracker from "@/components/shared/VisitorTracker";
import FloatingWhatsApp from "@/components/shared/FloatingWhatsApp";
import BlogPage from "@/components/pages/BlogPage";
import ContactPage from "@/components/pages/ContactPage";
import NewsletterSection from "@/components/shared/NewsletterSection";

// Custom Hooks
import useContentProtection from "@/hooks/useContentProtection";

// WhatsApp Icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Restore admin auth header globally on page load (survives refresh)
const __cstAdminToken = (typeof window !== 'undefined') ? sessionStorage.getItem("cst_admin_token") : null;
if (__cstAdminToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${__cstAdminToken}`;
}

// Hero background - image (video can be added later)
const HERO_IMAGE = "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-4.0.3&auto=format&fit=crop&w=2074&q=80";

// Theme 2 Background Image
const THEME2_BG = "https://customer-assets.emergentagent.com/job_eduvisor/artifacts/3zctmidk_BG.png";

// Country Flags - Use flagcdn.com for actual flag images
const getCountryFlagUrl = (countryName, countryPages = {}) => {
  // First check if country page has custom flag
  const slug = countryName.toLowerCase().replace(/ /g, "-").replace(/'/g, "");
  if (countryPages[slug]?.flag_image) {
    return countryPages[slug].flag_image;
  }
  
  // Map country names to ISO codes for flagcdn
  const countryCodeMap = {
    "UK": "gb",
    "Denmark": "dk",
    "Netherlands": "nl",
    "Finland": "fi",
    "Canada": "ca",
    "Australia": "au",
    "New Zealand": "nz",
    "Lithuania": "lt",
    "Hungary": "hu",
    "USA": "us",
    "Germany": "de",
    "Ireland": "ie",
    "France": "fr",
    "Sweden": "se",
    "Norway": "no",
    "Italy": "it",
    "Spain": "es",
    "Poland": "pl",
    "Austria": "at",
    "Belgium": "be",
    "Switzerland": "ch",
    "Portugal": "pt",
    "Czech Republic": "cz",
    "Japan": "jp",
    "South Korea": "kr",
    "China": "cn",
    "Singapore": "sg",
    "Malaysia": "my",
    "Cyprus": "cy",
    "Estonia": "ee",
    "Latvia": "lv",
    "Slovenia": "si",
    "Croatia": "hr",
    "Slovakia": "sk",
    "Romania": "ro",
    "Bulgaria": "bg",
    "Greece": "gr",
    "Malta": "mt",
    "Luxembourg": "lu",
    "Iceland": "is",
    "India": "in",
    "Bangladesh": "bd",
    "Pakistan": "pk",
    "Nepal": "np",
    "Sri Lanka": "lk",
    "Thailand": "th",
    "Vietnam": "vn",
    "Philippines": "ph",
    "Indonesia": "id",
    "United Arab Emirates": "ae",
    "Saudi Arabia": "sa",
    "Qatar": "qa",
    "Kuwait": "kw",
    "Bahrain": "bh",
    "Oman": "om",
    "Turkey": "tr",
    "Russia": "ru",
    "Ukraine": "ua",
    "Brazil": "br",
    "Mexico": "mx",
    "Argentina": "ar",
    "Chile": "cl",
    "Colombia": "co",
    "South Africa": "za",
    "Nigeria": "ng",
    "Kenya": "ke",
    "Egypt": "eg",
    "Morocco": "ma"
  };
  
  const code = countryCodeMap[countryName];
  if (code) {
    return `https://flagcdn.com/w80/${code}.png`;
  }
  return null;
};

// Legacy emoji flags as fallback
const countryFlagsEmoji = {
  "UK": "🇬🇧",
  "Denmark": "🇩🇰",
  "Netherlands": "🇳🇱",
  "Finland": "🇫🇮",
  "Canada": "🇨🇦",
  "Australia": "🇦🇺",
  "New Zealand": "🇳🇿",
  "Lithuania": "🇱🇹",
  "Hungary": "🇭🇺",
  "USA": "🇺🇸",
  "Germany": "🇩🇪",
  "Ireland": "🇮🇪",
  "France": "🇫🇷",
  "Sweden": "🇸🇪",
  "Norway": "🇳🇴"
};

// Component to render country flag
const CountryFlag = ({ country, countryPages, size = "w-8 h-6" }) => {
  const flagUrl = getCountryFlagUrl(country, countryPages);
  
  if (flagUrl) {
    return (
      <img 
        src={flagUrl} 
        alt={`${country} flag`} 
        className={`${size} object-cover rounded-sm shadow-sm`}
        onError={(e) => {
          // Fallback to emoji if image fails
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'inline';
        }}
      />
    );
  }
  
  return <span className="text-2xl">{countryFlagsEmoji[country] || "🌍"}</span>;
};


// Translations
const translations = {
  en: {
    heroTitle: "Discover Your Study Abroad Opportunities!",
    heroSubtitle: "Check your eligibility for UG/PG programs worldwide based on your academic level, GPA/CGPA & IELTS/PTE score",
    checkEligibility: "Check Your Study Eligibility",
    fullName: "Full Name",
    phone: "Phone",
    email: "Email",
    academicLevel: "Academic Level",
    subjectGroup: "Subject Group",
    yearOfPassing: "Year of Passing",
    gpa: "GPA",
    cgpa: "CGPA",
    applyingFor: "Applying For",
    englishTest: "English Proficiency Test",
    ieltsBand: "IELTS Overall Band",
    pteScore: "PTE Overall Score",
    oietcScore: "OIETC Band (0-12)",
    checkBtn: "Check Eligibility",
    checking: "Checking... Hold a second",
    congratulations: "Congratulations! You are eligible for:",
    suggestions: "Suggestion & Note (Why not eligible)",
    contactConsultant: "Contact Our Consultant",
    sendMessage: "Send Message",
    sending: "Sending...",
    writeMessage: "Write your message...",
    studyGap: "Calculated study gap",
    years: "years",
    yes: "No Test",
    no: "No",
    selectOption: "Select...",
    resetForm: "Check Again",
    resetFormTooltip: "Clear form and start over",
    thankYouTitle: "Thank You!",
    thankYouMessage: "We have received your message. Our consultant will contact you soon, In Sha Allah.",
    thankYouBlogText: "Meanwhile, explore our informative blogs and news to learn more about studying abroad.",
    visitBlog: "Visit News & Blog"
  },
  bn: {
    heroTitle: "আপনার বিদেশে পড়ার সুযোগ আবিষ্কার করুন!",
    heroSubtitle: "আপনার একাডেমিক লেভেল, GPA/CGPA এবং IELTS/PTE স্কোরের ভিত্তিতে বিশ্বব্যাপী UG/PG প্রোগ্রামগুলির জন্য আপনার যোগ্যতা যাচাই করুন",
    checkEligibility: "আপনার স্টাডি এলিজিবিলিটি চেক করুন",
    fullName: "পুরো নাম",
    phone: "ফোন",
    email: "ইমেইল",
    academicLevel: "একাডেমিক লেভেল",
    subjectGroup: "সাবজেক্ট গ্রুপ",
    yearOfPassing: "পাশের সাল",
    gpa: "জিপিএ",
    cgpa: "সিজিপিএ",
    applyingFor: "আবেদনের জন্য",
    englishTest: "ইংরেজি দক্ষতা পরীক্ষা",
    ieltsBand: "IELTS Overall Band",
    pteScore: "PTE Overall Score",
    oietcScore: "OIETC ব্যান্ড (০-১২)",
    checkBtn: "এলিজিবিলিটি চেক করুন",
    checking: "চেক করা হচ্ছে... অপেক্ষা করুন",
    congratulations: "অভিনন্দন! আপনি নিম্নলিখিত দেশগুলোতে এলিজিবল:",
    suggestions: "পরামর্শ ও নোট (কেন যোগ্য নন)",
    contactConsultant: "আমাদের কন্সালট্যান্টের সাথে যোগাযোগ করুন",
    sendMessage: "মেসেজ পাঠান",
    sending: "পাঠানো হচ্ছে...",
    writeMessage: "আপনার মেসেজ লিখুন...",
    studyGap: "ক্যালকুলেটেড স্টাডি গ্যাপ",
    years: "বছর",
    yes: "টেস্ট নেই",
    no: "না",
    selectOption: "নির্বাচন করুন...",
    resetForm: "আবার চেক করুন",
    resetFormTooltip: "ফর্ম খালি করে নতুন করে শুরু করুন",
    thankYouTitle: "ধন্যবাদ!",
    thankYouMessage: "আপনার মেসেজ পেয়েছি। আমাদের কন্সালট্যান্ট শীঘ্রই আপনার সাথে যোগাযোগ করবেন, ইনশাআল্লাহ।",
    thankYouBlogText: "এর মধ্যে, বিদেশে পড়াশোনা সম্পর্কে আরও জানতে আমাদের তথ্যবহুল ব্লগ ও নিউজ পড়ুন।",
    visitBlog: "নিউজ ও ব্লগ দেখুন"
  }
};

// Function to translate suggestion reasons to Bengali
const translateSuggestion = (reason, isBangla) => {
  if (!isBangla) return reason;
  
  // Translate common phrases
  let translated = reason;
  
  // Gap limit translations
  if (reason.includes("the study gap limit of")) {
    const match = reason.match(/the study gap limit of (\d+) year\(s\) is exceeded/);
    if (match) {
      translated = `স্টাডি গ্যাপ ${match[1]} বছরের বেশি হয়ে গেছে`;
    }
  }
  
  // IELTS translations
  if (reason.includes("an English proficiency test (IELTS/PTE) is required")) {
    translated = "ইংরেজি দক্ষতা পরীক্ষা (IELTS/PTE) প্রয়োজন";
  }
  if (reason.includes("the minimum IELTS band required is")) {
    const match = reason.match(/the minimum IELTS band required is ([\d.]+)/);
    if (match) {
      translated = `ন্যূনতম IELTS ব্যান্ড ${match[1]} প্রয়োজন`;
    }
  }
  
  // PTE translations
  if (reason.includes("PTE is not accepted, IELTS is required")) {
    translated = "PTE গ্রহণযোগ্য নয়, IELTS প্রয়োজন";
  }
  if (reason.includes("the minimum PTE score required is")) {
    const match = reason.match(/the minimum PTE score required is (\d+)/);
    if (match) {
      translated = `ন্যূনতম PTE স্কোর ${match[1]} প্রয়োজন`;
    }
  }
  
  // GPA/CGPA translations
  if (reason.includes("the minimum required GPA is")) {
    const match = reason.match(/the minimum required GPA is ([\d.]+)/);
    if (match) {
      translated = `ন্যূনতম GPA ${match[1]} প্রয়োজন`;
    }
  }
  if (reason.includes("the minimum required CGPA is")) {
    const match = reason.match(/the minimum required CGPA is ([\d.]+)/);
    if (match) {
      translated = `ন্যূনতম CGPA ${match[1]} প্রয়োজন`;
    }
  }
  
  // Subject group translation
  if (reason.includes("Subject group")) {
    const match = reason.match(/Subject group '(\w+)' is not allowed/);
    if (match) {
      const groupMap = {
        'Science': 'সায়েন্স',
        'Commerce': 'কমার্স', 
        'Arts': 'আর্টস',
        'Others': 'অন্যান্য'
      };
      translated = `${groupMap[match[1]] || match[1]} সাবজেক্ট গ্রুপ অনুমোদিত নয়`;
    }
  }
  
  return translated;
};

// =============================================
// BLOG CAROUSEL SECTION COMPONENT
// =============================================
const BlogCarouselSection = ({ isBangla }) => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch latest blogs
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await axios.get(`${API}/blog-posts`);
        // Get blogs sorted by date, max 6 (show all blogs regardless of status)
        const latestBlogs = res.data
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 6);
        setBlogs(latestBlogs);
      } catch (err) {
        console.error("Failed to fetch blogs:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  // Auto-slide every 4 seconds
  useEffect(() => {
    if (blogs.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % blogs.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [blogs.length]);

  if (isLoading || blogs.length === 0) return null;

  const currentBlog = blogs[currentIndex];
  
  // Default fallback image
  const defaultImage = "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&q=80";
  const blogImage = currentBlog.feature_image && currentBlog.feature_image.trim() !== '' 
    ? currentBlog.feature_image 
    : defaultImage;

  return (
    <section className="py-16 relative overflow-hidden">
      {/* Beautiful Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-rose-50 via-white to-indigo-50"></div>
      
      {/* Modern Abstract Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Orbs - Soft & Elegant */}
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-gradient-to-br from-rose-200/40 to-pink-300/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -right-20 w-[500px] h-[500px] bg-gradient-to-tr from-indigo-200/40 to-purple-200/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-rose-100/20 to-indigo-100/20 rounded-full blur-3xl"></div>
        
        {/* Floating Gradient Circles */}
        <div className="absolute top-20 left-[15%] w-32 h-32 bg-gradient-to-br from-rose-300/20 to-transparent rounded-full animate-pulse" style={{animationDuration: '4s'}}></div>
        <div className="absolute bottom-32 right-[10%] w-24 h-24 bg-gradient-to-tl from-indigo-300/25 to-transparent rounded-full animate-pulse" style={{animationDuration: '3s'}}></div>
        <div className="absolute top-1/3 right-[20%] w-20 h-20 bg-gradient-to-br from-purple-300/20 to-transparent rounded-full animate-pulse" style={{animationDuration: '5s'}}></div>
        <div className="absolute bottom-1/4 left-[25%] w-16 h-16 bg-gradient-to-tr from-pink-300/25 to-transparent rounded-full"></div>
        
        {/* Decorative Lines */}
        <div className="absolute top-10 left-[5%] w-32 h-[2px] bg-gradient-to-r from-rose-300/40 to-transparent rotate-45"></div>
        <div className="absolute bottom-20 right-[8%] w-40 h-[2px] bg-gradient-to-l from-indigo-300/40 to-transparent -rotate-12"></div>
        <div className="absolute top-1/2 left-[3%] w-24 h-[2px] bg-gradient-to-r from-purple-300/30 to-transparent rotate-[30deg]"></div>
        
        {/* Small Accent Dots */}
        <div className="absolute top-16 right-[30%] w-3 h-3 bg-rose-400/30 rounded-full"></div>
        <div className="absolute bottom-24 left-[35%] w-2 h-2 bg-indigo-400/40 rounded-full"></div>
        <div className="absolute top-1/3 left-[12%] w-2 h-2 bg-purple-400/30 rounded-full"></div>
        <div className="absolute bottom-1/3 right-[15%] w-3 h-3 bg-pink-400/25 rounded-full"></div>
        
        {/* Ring Elements */}
        <div className="absolute top-24 right-[5%] w-20 h-20 border-2 border-rose-200/30 rounded-full"></div>
        <div className="absolute bottom-16 left-[8%] w-16 h-16 border-2 border-indigo-200/25 rounded-full"></div>
        <div className="absolute top-1/2 right-[25%] w-12 h-12 border border-purple-200/20 rounded-full"></div>
      </div>
      
      {/* Subtle Pattern Overlay */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, #6366f1 1px, transparent 1px)`,
        backgroundSize: '48px 48px'
      }}></div>
      
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10">
          <span className="inline-block bg-red-100 text-red-600 font-semibold text-sm uppercase tracking-wider px-4 py-2 rounded-full mb-4">
            {isBangla ? "📰 সর্বশেষ খবর" : "📰 Latest News"}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mt-2 bangla-text">
            {isBangla ? "নিউজ এবং ব্লগ" : "News & Blog"}
          </h2>
          <p className="text-gray-500 mt-3 max-w-xl mx-auto bangla-text">
            {isBangla ? "বিদেশে পড়াশোনার সর্বশেষ তথ্য ও গাইডলাইন" : "Latest updates and guides for studying abroad"}
          </p>
        </div>

        {/* Carousel Card */}
        <div className="relative max-w-4xl mx-auto">
          <div 
            className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden cursor-pointer transform transition-all duration-500 hover:shadow-3xl hover:scale-[1.02] border border-white/50"
            onClick={() => navigate(`/news/${currentBlog.slug}`)}
          >
            <div className="md:flex">
              {/* Image */}
              <div className="md:w-2/5 h-56 md:h-72 relative overflow-hidden">
                <img
                  src={blogImage}
                  alt={currentBlog.title}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                  onError={(e) => { e.target.src = defaultImage; }}
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-red-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
                    {currentBlog.category || "Blog"}
                  </span>
                </div>
              </div>
              
              {/* Content */}
              <div className="md:w-3/5 p-6 md:p-8 flex flex-col justify-center">
                <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-3 line-clamp-2 bangla-text hover:text-red-600 transition-colors">
                  {currentBlog.title}
                </h3>
                <p className="text-gray-500 mb-4 line-clamp-3 bangla-text leading-relaxed">
                  {currentBlog.excerpt || "Click to read more..."}
                </p>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-red-600 font-bold text-xs">{currentBlog.author?.charAt(0) || 'A'}</span>
                    </div>
                    <span className="text-gray-600 font-medium">{currentBlog.author}</span>
                  </div>
                  <span className="text-gray-400 bg-gray-100 px-3 py-1 rounded-full text-xs">
                    {new Date(currentBlog.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {blogs.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  idx === currentIndex 
                    ? 'bg-red-500 w-8' 
                    : 'bg-gray-300 hover:bg-red-300 w-2.5'
                }`}
              />
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={(e) => { e.stopPropagation(); setCurrentIndex((prev) => (prev - 1 + blogs.length) % blogs.length); }}
            className="absolute left-0 top-1/3 -translate-y-1/2 -translate-x-2 md:-translate-x-6 bg-white shadow-xl rounded-full p-3 hover:bg-red-50 hover:scale-110 transition-all duration-300 border border-gray-100"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrentIndex((prev) => (prev + 1) % blogs.length); }}
            className="absolute right-0 top-1/3 -translate-y-1/2 translate-x-2 md:translate-x-6 bg-white shadow-xl rounded-full p-3 hover:bg-red-50 hover:scale-110 transition-all duration-300 border border-gray-100"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Button
            onClick={() => navigate('/news')}
            className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-10 py-4 rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          >
            {isBangla ? "আরও ব্লগ পড়ুন →" : "Read More Blogs →"}
          </Button>
        </div>
      </div>
    </section>
  );
};

// Eligibility Form Component
const EligibilityForm = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => {
    // Get saved language from sessionStorage
    const saved = sessionStorage.getItem('selected_language');
    return saved || "en";
  });
  const [settings, setSettings] = useState({ enable_bangla_ui: false, disable_dev_tools: false, country_click_action: "modal", active_theme: "theme1" });
  const [countryPages, setCountryPages] = useState({});
  const [selectedCountryPage, setSelectedCountryPage] = useState(null);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [isThemeTransitioning, setIsThemeTransitioning] = useState(false); // For smooth theme transition
  const t = translations[lang];
  const isBangla = lang === "bn";
  
  // Save language to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem('selected_language', lang);
  }, [lang]);
  
  // Detect if page was manually refreshed (not navigation back)
  useEffect(() => {
    // Set a flag when navigating away
    const handleBeforeUnload = () => {
      // This only fires on actual page refresh/close, not SPA navigation
      sessionStorage.setItem('page_unloading', 'true');
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Check if this was a real page refresh
    const wasUnloading = sessionStorage.getItem('page_unloading');
    if (wasUnloading) {
      // Clear the flag and the form data only on real refresh
      sessionStorage.removeItem('page_unloading');
      sessionStorage.removeItem('eligibilityFormData');
      sessionStorage.removeItem('eligibilityResult');
    }
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  
  // Initialize form data from sessionStorage if available (for back navigation)
  const [formData, setFormData] = useState(() => {
    const saved = sessionStorage.getItem('eligibilityFormData');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return {
          full_name: "",
          phone: "",
          email: "",
          academic_level: "",
          subject_group: "",
          year_of_passing: "",
          gpa_cgpa: "",
          applying_for: "",
          english_test_type: "",
          ielts_band: "",
          pte_score: "",
          oietc_score: ""
        };
      }
    }
    return {
      full_name: "",
      phone: "",
      email: "",
      academic_level: "",
      subject_group: "",
      year_of_passing: "",
      gpa_cgpa: "",
      applying_for: "",
      english_test_type: "",
      ielts_band: "",
      pte_score: "",
      oietc_score: ""
    };
  });
  
  const [loading, setLoading] = useState(false);
  
  // Initialize result from sessionStorage if available (for back navigation)
  const [result, setResult] = useState(() => {
    const saved = sessionStorage.getItem('eligibilityResult');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return null;
      }
    }
    return null;
  });
  
  const [showContactModal, setShowContactModal] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [message, setMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  // Reset form function - clears all form data and result
  const resetForm = () => {
    const emptyForm = {
      full_name: "",
      phone: "",
      email: "",
      academic_level: "",
      subject_group: "",
      year_of_passing: "",
      gpa_cgpa: "",
      applying_for: "",
      english_test_type: "",
      ielts_band: "",
      pte_score: "",
      oietc_score: ""
    };
    setFormData(emptyForm);
    setResult(null);
    sessionStorage.removeItem('eligibilityFormData');
    sessionStorage.removeItem('eligibilityResult');
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Save form data to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem('eligibilityFormData', JSON.stringify(formData));
  }, [formData]);

  // Save result to sessionStorage when it changes
  useEffect(() => {
    if (result) {
      sessionStorage.setItem('eligibilityResult', JSON.stringify(result));
    }
  }, [result]);

  // Fetch settings on load
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    
    const fetchCountryPages = async () => {
      try {
        const res = await axios.get(`${API}/country-pages`);
        // Create a map of slug -> page data
        const pagesMap = {};
        res.data.forEach(page => {
          pagesMap[page.slug] = page;
        });
        setCountryPages(pagesMap);
      } catch (error) {
        console.error("Error fetching country pages:", error);
      }
    };
    
    fetchSettings();
    fetchCountryPages();
  }, []);

  // Disable dev tools based on settings
  useEffect(() => {
    if (settings.disable_dev_tools) {
      // Disable right-click context menu
      const handleContextMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };
      
      // Disable keyboard shortcuts
      const handleKeyDown = (e) => {
        // F12
        if (e.key === 'F12' || e.keyCode === 123) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        // Ctrl+Shift+I (Inspect)
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i' || e.keyCode === 73)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        // Ctrl+Shift+J (Console)
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'J' || e.key === 'j' || e.keyCode === 74)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        // Ctrl+U (View Source)
        if ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U' || e.keyCode === 85)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        // Ctrl+Shift+C (Inspect Element)
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'C' || e.key === 'c' || e.keyCode === 67)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        // Ctrl+S (Save)
        if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S' || e.keyCode === 83)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        // Ctrl+C (Copy) - block outside input fields
        if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C' || e.keyCode === 67) && !e.shiftKey) {
          const activeElement = document.activeElement;
          const isInputField = activeElement && (
            activeElement.tagName === 'INPUT' || 
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.isContentEditable
          );
          if (!isInputField) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }
        // Ctrl+A (Select All) - block outside input fields
        if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A' || e.keyCode === 65)) {
          const activeElement = document.activeElement;
          const isInputField = activeElement && (
            activeElement.tagName === 'INPUT' || 
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.isContentEditable
          );
          if (!isInputField) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }
        }
      };

      // Disable copy event
      const handleCopy = (e) => {
        const activeElement = document.activeElement;
        const isInputField = activeElement && (
          activeElement.tagName === 'INPUT' || 
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable
        );
        if (!isInputField) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      };

      // Disable cut event
      const handleCut = (e) => {
        const activeElement = document.activeElement;
        const isInputField = activeElement && (
          activeElement.tagName === 'INPUT' || 
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable
        );
        if (!isInputField) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      };

      // Disable text selection via CSS
      const style = document.createElement('style');
      style.id = 'disable-select-style';
      style.textContent = `
        body:not(input):not(textarea) {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          -webkit-touch-callout: none !important;
        }
        input, textarea, [contenteditable="true"] {
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
          user-select: text !important;
        }
        img {
          -webkit-user-drag: none !important;
          -khtml-user-drag: none !important;
          -moz-user-drag: none !important;
          -o-user-drag: none !important;
          user-drag: none !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(style);

      // Disable drag start
      const handleDragStart = (e) => {
        e.preventDefault();
        return false;
      };

      // Disable select start (for older browsers)
      const handleSelectStart = (e) => {
        const target = e.target;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && !target.isContentEditable) {
          e.preventDefault();
          return false;
        }
      };

      // Add all event listeners
      document.addEventListener('contextmenu', handleContextMenu, true);
      document.addEventListener('keydown', handleKeyDown, true);
      document.addEventListener('copy', handleCopy, true);
      document.addEventListener('cut', handleCut, true);
      document.addEventListener('dragstart', handleDragStart, true);
      document.addEventListener('selectstart', handleSelectStart, true);
      
      // Cleanup function
      return () => {
        document.removeEventListener('contextmenu', handleContextMenu, true);
        document.removeEventListener('keydown', handleKeyDown, true);
        document.removeEventListener('copy', handleCopy, true);
        document.removeEventListener('cut', handleCut, true);
        document.removeEventListener('dragstart', handleDragStart, true);
        document.removeEventListener('selectstart', handleSelectStart, true);
        const styleElement = document.getElementById('disable-select-style');
        if (styleElement) {
          styleElement.remove();
        }
      };
    }
  }, [settings.disable_dev_tools]);

  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const MAX_RECORDING_TIME = 120; // 2 minutes in seconds

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        // Use the same MIME type as recorded
        const mimeType = mediaRecorderRef.current.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(audioBlob);
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        console.log("Audio recorded, URL:", url, "MIME:", mimeType);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_TIME - 1) {
            stopRecording();
            return MAX_RECORDING_TIME;
          }
          return prev + 1;
        });
      }, 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access microphone. Please allow microphone permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const deleteRecording = () => {
    // Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    // Revoke the blob URL to free memory
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setIsPlaying(false);
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioUrl]);

  const togglePlayback = async () => {
    console.log("togglePlayback called, audioBlob:", audioBlob, "isPlaying:", isPlaying);
    
    if (!audioBlob) {
      toast.error("No audio to play");
      return;
    }
    
    if (isPlaying && audioRef.current) {
      // Pause current audio
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        // If we don't have an audio element yet or it's a new blob, create one
        if (!audioRef.current) {
          audioRef.current = new Audio();
        }
        
        // Create a fresh URL from the blob each time
        const freshUrl = URL.createObjectURL(audioBlob);
        audioRef.current.src = freshUrl;
        
        // Set up event handlers
        audioRef.current.onended = () => {
          console.log("Audio ended");
          setIsPlaying(false);
        };
        
        audioRef.current.onerror = (e) => {
          console.error("Audio error:", e, audioRef.current?.error);
          toast.error("Audio playback error");
          setIsPlaying(false);
        };
        
        // Load and play
        audioRef.current.load();
        
        await audioRef.current.play();
        console.log("Audio playing successfully");
        setIsPlaying(true);
        
      } catch (err) {
        console.error("Play failed:", err);
        toast.error("Could not play audio: " + err.message);
        setIsPlaying(false);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle country click
  const handleCountryClick = async (countryName) => {
    const slug = countryName.toLowerCase().replace(/ /g, "-").replace(/'/g, "");
    
    // Try to get from local state first, otherwise fetch directly
    let pageData = countryPages[slug];
    
    if (!pageData) {
      // Fetch directly from API
      try {
        const res = await axios.get(`${API}/country-pages/${slug}`);
        pageData = res.data;
      } catch (error) {
        console.error("Error fetching country page:", error);
      }
    }
    
    if (settings.country_click_action === "page") {
      // Navigate to country page with state
      navigate(`/country/${slug}`, { state: { fromEligibility: true } });
    } else {
      // Show modal
      if (pageData) {
        setSelectedCountryPage(pageData);
        setShowCountryModal(true);
      } else {
        toast.info(`No detailed page available for ${countryName} yet.`);
      }
    }
  };

  // Get YouTube embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 
      ? `https://www.youtube.com/embed/${match[2]}` 
      : url;
  };

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({});
  
  const handleInputChange = (field, value) => {
    // Validation logic for specific fields
    let error = null;
    
    if (field === "gpa_cgpa") {
      const numValue = parseFloat(value);
      const maxValue = formData.applying_for === "PG" ? 4.0 : 5.0;
      const fieldName = formData.applying_for === "PG" ? "CGPA" : "GPA";
      
      if (value && numValue > maxValue) {
        error = isBangla 
          ? `${fieldName} সর্বোচ্চ ${maxValue} হতে পারে। সঠিক মান দিন।`
          : `Invalid! ${fieldName} cannot exceed ${maxValue}. Please enter a valid score.`;
      }
    }
    
    if (field === "ielts_band") {
      const numValue = parseFloat(value);
      if (value && numValue > 9) {
        error = isBangla 
          ? "IELTS Band সর্বোচ্চ 9 হতে পারে। সঠিক মান দিন।"
          : "Invalid! IELTS Band cannot exceed 9. Please enter a valid score.";
      }
    }
    
    if (field === "pte_score") {
      const numValue = parseInt(value);
      if (value && numValue > 90) {
        error = isBangla 
          ? "PTE Score সর্বোচ্চ 90 হতে পারে। সঠিক মান দিন।"
          : "Invalid! PTE Score cannot exceed 90. Please enter a valid score.";
      }
    }
    
    // Update validation errors
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
    
    // Always update the form data
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for validation errors before submitting
    const hasErrors = Object.values(validationErrors).some(error => error !== null);
    if (hasErrors) {
      toast.error(isBangla ? "অনুগ্রহ করে সঠিক মান দিন।" : "Please correct the invalid values before submitting.");
      return;
    }
    
    // Additional validation checks
    const gpaValue = parseFloat(formData.gpa_cgpa);
    const maxGpa = formData.applying_for === "PG" ? 4.0 : 5.0;
    if (gpaValue > maxGpa) {
      toast.error(isBangla 
        ? `${formData.applying_for === "PG" ? "CGPA" : "GPA"} সর্বোচ্চ ${maxGpa} হতে পারে।`
        : `${formData.applying_for === "PG" ? "CGPA" : "GPA"} cannot exceed ${maxGpa}.`);
      return;
    }
    
    if (formData.english_test_type === "ielts" && parseFloat(formData.ielts_band) > 9) {
      toast.error(isBangla ? "IELTS Band সর্বোচ্চ 9 হতে পারে।" : "IELTS Band cannot exceed 9.");
      return;
    }
    
    if (formData.english_test_type === "pte" && parseInt(formData.pte_score) > 90) {
      toast.error(isBangla ? "PTE Score সর্বোচ্চ 90 হতে পারে।" : "PTE Score cannot exceed 90.");
      return;
    }
    
    // Validate OIETC score
    if (formData.english_test_type === "oietc" && parseFloat(formData.oietc_score) > 12) {
      toast.error(isBangla ? "OIETC Band সর্বোচ্চ 12 হতে পারে।" : "OIETC Band cannot exceed 12.");
      return;
    }
    
    setLoading(true);
    setResult(null);
    
    try {
      const payload = {
        ...formData,
        year_of_passing: parseInt(formData.year_of_passing),
        gpa_cgpa: parseFloat(formData.gpa_cgpa),
        ielts_band: formData.english_test_type === "ielts" ? parseFloat(formData.ielts_band) : null,
        pte_score: formData.english_test_type === "pte" ? parseInt(formData.pte_score) : null,
        oietc_score: formData.english_test_type === "oietc" ? parseFloat(formData.oietc_score) : null
      };
      
      const response = await axios.post(`${API}/check-eligibility`, payload);
      setResult(response.data);
    } catch (error) {
      console.error("Error checking eligibility:", error);
      toast.error("Error checking eligibility. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() && !audioBlob) return;
    
    setSendingMessage(true);
    try {
      // If there's an audio recording, upload it first
      let voiceMessageUrl = null;
      if (audioBlob) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', audioBlob, 'voice_message.webm');
        
        try {
          const uploadRes = await axios.post(`${API}/upload-voice`, formDataUpload, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          voiceMessageUrl = uploadRes.data.file_url;
        } catch (uploadError) {
          console.error("Error uploading voice:", uploadError);
          // Continue without voice if upload fails
        }
      }
      
      await axios.post(`${API}/contact`, {
        ...formData,
        year_of_passing: parseInt(formData.year_of_passing),
        gpa_cgpa: parseFloat(formData.gpa_cgpa),
        ielts_band: formData.english_test_type === "ielts" ? parseFloat(formData.ielts_band) : null,
        pte_score: formData.english_test_type === "pte" ? parseInt(formData.pte_score) : null,
        oietc_score: formData.english_test_type === "oietc" ? parseFloat(formData.oietc_score) : null,
        calculated_gap: result?.calculated_gap || 0,
        eligible_countries: result?.eligible_countries || [],
        suggestions: result?.suggestions || {},
        message: message,
        voice_message_url: voiceMessageUrl
      });
      
      // Show thank you modal instead of toast
      setShowContactModal(false);
      setShowThankYouModal(true);
      setMessage("");
      deleteRecording();
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error sending message. Please try again.");
    } finally {
      setSendingMessage(false);
    }
  };

  // Theme 2 Render
  if (settings.active_theme === "theme2") {
    return (
      <div 
        className={`min-h-screen relative ${isBangla ? 'bangla-text' : ''}`}
        style={{ 
          backgroundImage: `url(${THEME2_BG})`, 
          backgroundSize: 'cover', 
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Cinematic Overlay - warm sun glow like ChatGPT design */}
        <div 
          className="absolute inset-0 z-0 theme2-hero-overlay"
        ></div>

        {/* Theme Transition Overlay */}
        {isThemeTransitioning && (
          <div 
            className="fixed inset-0 z-[100] bg-white pointer-events-none"
            style={{
              animation: 'fadeInOut 0.5s ease-in-out'
            }}
          />
        )}

        {/* Main Content */}
        <div className={`flex flex-col items-center px-4 relative z-10 ${result ? 'pt-16 pb-0' : 'min-h-screen justify-center py-20'}`}>
          
          {/* Hero Text */}
          <div className="text-center mb-6 max-w-3xl">
            <h1 className={`text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-3 ${isBangla ? 'bangla-text' : ''}`}
                style={{ textShadow: '0 8px 28px rgba(0,0,0,0.35)', letterSpacing: '-0.02em' }}>
              Discover Your Study Abroad Opportunities!
            </h1>
            <p className={`text-base md:text-lg text-white/90 max-w-xl mx-auto ${isBangla ? 'bangla-text' : ''}`}
               style={{ lineHeight: 1.5 }}>
              {t.heroSubtitle}
            </p>
          </div>

          {/* Form Card - Glass Design */}
          <Card className="w-full max-w-4xl theme2-form-card border-0 overflow-hidden"
                style={{ borderRadius: '18px' }}>
            {/* Header */}
            <CardHeader className="theme2-form-header px-5 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                       style={{ background: 'rgba(239,68,68,.10)', border: '1px solid rgba(239,68,68,.18)' }}>
                    <GraduationCap className="h-5 w-5 text-red-500" />
                  </div>
                  <CardTitle className={`text-base font-bold text-gray-800 ${isBangla ? 'bangla-text' : ''}`}>
                    Check Your Study Eligibility
                  </CardTitle>
                </div>
                {/* Language Dropdown - Right Side */}
                {settings.enable_bangla_ui && (
                  <Select value={lang} onValueChange={setLang}>
                    <SelectTrigger className="w-[110px] h-9 text-sm border-gray-200 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="bn">বাংলা</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="p-6 md:p-8 bg-white">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Row 1: Name, Phone, Email */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className={`text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.fullName}</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => handleInputChange("full_name", e.target.value)}
                      placeholder="Enter your name"
                      className="border-gray-200 focus:border-red-400 rounded-xl"
                      required
                      data-testid="input-full-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={`text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.phone}</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      placeholder="01XXXXXXXXX"
                      className="border-gray-200 focus:border-red-400 rounded-xl"
                      required
                      data-testid="input-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={`text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.email}</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      placeholder="your@email.com"
                      className="border-gray-200 focus:border-red-400 rounded-xl"
                      required
                      data-testid="input-email"
                    />
                  </div>
                </div>

                {/* Row 2: Academic Level, Subject Group */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={`text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.academicLevel}</Label>
                    <Select value={formData.academic_level} onValueChange={(v) => handleInputChange("academic_level", v)}>
                      <SelectTrigger className="border-gray-200 rounded-xl" data-testid="select-academic-level">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="HSC">HSC</SelectItem>
                        <SelectItem value="Diploma">Diploma</SelectItem>
                        <SelectItem value="BSC">Bachelor (BSC/BA/BBA)</SelectItem>
                        <SelectItem value="MSC">Masters (MSC/MA/MBA)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className={`text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.subjectGroup}</Label>
                    <Select value={formData.subject_group} onValueChange={(v) => handleInputChange("subject_group", v)}>
                      <SelectTrigger className="border-gray-200 rounded-xl" data-testid="select-subject-group">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Science">Science</SelectItem>
                        <SelectItem value="Commerce">Commerce</SelectItem>
                        <SelectItem value="Arts">Arts</SelectItem>
                        <SelectItem value="Others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Row 3: GPA/CGPA, Year, Applying For */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className={`text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>
                      {formData.academic_level === "HSC" ? t.gpa : t.cgpa}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.gpa_cgpa}
                      onChange={(e) => handleInputChange("gpa_cgpa", e.target.value)}
                      placeholder={formData.academic_level === "HSC" ? "5.00" : "4.00"}
                      className="border-gray-200 focus:border-red-400 rounded-xl"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={`text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.yearOfPassing}</Label>
                    <Input
                      type="number"
                      value={formData.year_of_passing}
                      onChange={(e) => handleInputChange("year_of_passing", e.target.value)}
                      placeholder="2023"
                      className="border-gray-200 focus:border-red-400 rounded-xl"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={`text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.applyingFor}</Label>
                    <Select value={formData.applying_for} onValueChange={(v) => handleInputChange("applying_for", v)}>
                      <SelectTrigger className="border-gray-200 rounded-xl" data-testid="select-applying-for">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UG">UG (Bachelor)</SelectItem>
                        <SelectItem value="PG">PG (Master)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Row 4: English Test */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className={`text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.englishTest}</Label>
                    <Select value={formData.english_test_type} onValueChange={(v) => handleInputChange("english_test_type", v)}>
                      <SelectTrigger className="border-gray-200 rounded-xl" data-testid="select-english-test">
                        <SelectValue placeholder={isBangla ? "IELTS/PTE/OIETC নির্বাচন করুন..." : "Select IELTS/PTE/OIETC..."} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ielts">IELTS</SelectItem>
                        <SelectItem value="pte">PTE</SelectItem>
                        <SelectItem value="oietc">OIETC</SelectItem>
                        <SelectItem value="moi">MOI (Medium of Instruction)</SelectItem>
                        <SelectItem value="without_any">{isBangla ? "টেস্ট ছাড়াই" : "Without Any Test"}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.english_test_type === "ielts" && (
                    <div className="space-y-2">
                      <Label className="text-gray-700 font-medium">{t.ieltsBand}</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.ielts_band}
                        onChange={(e) => handleInputChange("ielts_band", e.target.value)}
                        placeholder="6.0"
                        className="border-gray-200 focus:border-red-400 rounded-xl"
                      />
                    </div>
                  )}
                  {formData.english_test_type === "pte" && (
                    <div className="space-y-2">
                      <Label className="text-gray-700 font-medium">{t.pteScore}</Label>
                      <Input
                        type="number"
                        value={formData.pte_score}
                        onChange={(e) => handleInputChange("pte_score", e.target.value)}
                        placeholder="58"
                        className="border-gray-200 focus:border-red-400 rounded-xl"
                      />
                    </div>
                  )}
                  {formData.english_test_type === "oietc" && (
                    <div className="space-y-2">
                      <Label className="text-gray-700 font-medium">{t.oietcScore}</Label>
                      <Input
                        type="number"
                        value={formData.oietc_score}
                        onChange={(e) => handleInputChange("oietc_score", e.target.value)}
                        placeholder="6.0"
                        min="0"
                        max="12"
                        step="0.5"
                        className="border-gray-200 focus:border-red-400 rounded-xl"
                      />
                    </div>
                  )}
                </div>

                {/* Submit Button - Coral Red */}
                <div className="pt-6">
                  <Button 
                    type="submit" 
                    className="w-full md:w-auto bg-gradient-to-r from-orange-400 to-red-500 hover:from-orange-500 hover:to-red-600 text-white px-12 py-6 text-lg font-bold rounded-full shadow-lg hover:shadow-xl transition-all"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Checking...
                      </span>
                    ) : (
                      <span className={`flex items-center gap-2 ${isBangla ? 'bangla-text' : ''}`}>
                        {t.checkBtn}
                      </span>
                    )}
                  </Button>
                  
                  {/* Privacy Note */}
                  <p className="text-xs text-gray-400 mt-4">
                    We don't share your data. Your privacy our priority.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Results Section with Glassy Wave Background - ChatGPT Design */}
        {result && (
          <div className="theme2-wave-section relative w-full">
            {/* Glass haze effect handled by CSS ::before and ::after */}
            
            {/* Content Area */}
            <div className="theme2-wave-content px-4 pb-12">
              <div className="max-w-4xl mx-auto">
                {/* Results Card - Glass */}
                <div className="theme2-results-card p-5 md:p-6">
                  {/* Study Gap */}
                  <p className="text-xs text-gray-500 mb-3">
                    {t.studyGap}: <span className="font-bold text-blue-600">{result.calculated_gap} {t.years}</span>
                  </p>

                  {/* Eligible Countries */}
                  {result.eligible_countries.length > 0 && (
                    <div className="mb-6">
                      <h3 className={`text-base font-semibold mb-4 flex items-center gap-2 ${isBangla ? 'bangla-text' : ''}`}
                          style={{ color: 'rgba(22,163,74,.95)' }}>
                        <span className="w-5 h-5 rounded-full flex items-center justify-center"
                              style={{ background: 'rgba(22,163,74,.14)', border: '1px solid rgba(22,163,74,.25)' }}>
                          <CheckCircle className="h-3 w-3" />
                        </span>
                        {t.congratulations}
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {result.eligible_countries.map((country, idx) => (
                          <button 
                            key={idx}
                            onClick={() => handleCountryClick(country)}
                            className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all hover:shadow-lg"
                            style={{
                              background: 'rgba(255,255,255,.76)',
                              border: '1px solid rgba(15,23,42,.08)',
                              boxShadow: '0 10px 24px rgba(0,0,0,.10)'
                            }}
                          >
                            <img 
                              src={getCountryFlagUrl(country, countryPages)} 
                              alt={`${country} flag`}
                              className="w-8 h-6 rounded object-cover flex-shrink-0"
                              style={{ background: 'rgba(15,23,42,.06)' }}
                              onError={(e) => e.target.src = 'https://flagcdn.com/w80/un.png'}
                            />
                            <div className="flex-1 text-left min-w-0">
                              <span className="font-bold text-sm text-gray-800 block truncate">{country}</span>
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <ExternalLink className="h-3 w-3" /> Click for details
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Not Eligible (Suggestions) */}
                  {result.suggestions && Object.keys(result.suggestions).length > 0 && (
                    <div className="mb-6">
                      <h3 className={`text-base font-semibold text-amber-600 mb-3 flex items-center gap-2 ${isBangla ? 'bangla-text' : ''}`}>
                        <AlertCircle className="h-5 w-5" />
                        {t.suggestions}
                      </h3>
                      <div className="space-y-2">
                        {Object.entries(result.suggestions).map(([country, reasons], idx) => (
                          <div key={idx} className="p-3 bg-amber-50/80 rounded-xl border border-amber-100">
                            <div className="flex items-center gap-2 mb-1">
                              <img 
                                src={getCountryFlagUrl(country, countryPages)} 
                                alt={`${country} flag`}
                                className="w-5 h-5 rounded object-cover"
                                onError={(e) => e.target.src = 'https://flagcdn.com/w80/un.png'}
                              />
                              <span className="font-bold text-sm text-gray-800">{country}</span>
                            </div>
                            <p className="text-amber-700 text-xs pl-7">
                              {reasons.map(r => translateSuggestion(r, isBangla)).join(" & ")}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100">
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => setShowContactModal(true)}
                        className="theme2-btn-dark h-10 px-4 font-bold text-sm"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {t.contactConsultant}
                      </Button>
                      <Button 
                        onClick={resetForm}
                        variant="outline"
                        className="theme2-btn-ghost h-10 px-4 text-sm bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 hover:from-blue-100 hover:to-indigo-100 text-blue-700"
                        title={t.resetFormTooltip}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        {t.resetForm}
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="theme2-btn-ghost h-10 px-4 text-sm">
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>
                      <Button variant="outline" className="theme2-btn-ghost h-10 px-4 text-sm" onClick={() => {
                        const shareUrl = `${API}/og/blog/${post.slug}`;
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          navigator.clipboard.writeText(shareUrl).then(() => {
                            toast.success("Share link copied! WhatsApp/Facebook এ share করুন");
                          }).catch(() => {
                            const ta = document.createElement('textarea');
                            ta.value = shareUrl;
                            document.body.appendChild(ta);
                            ta.select();
                            document.execCommand('copy');
                            document.body.removeChild(ta);
                            toast.success("Share link copied!");
                          });
                        } else {
                          const ta = document.createElement('textarea');
                          ta.value = shareUrl;
                          document.body.appendChild(ta);
                          ta.select();
                          document.execCommand('copy');
                          document.body.removeChild(ta);
                          toast.success("Share link copied!");
                        }
                      }}>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Copy Link
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Blog Carousel Section - Above Newsletter */}
        {settings.show_blog_carousel !== false && (
          <BlogCarouselSection isBangla={isBangla} />
        )}

        {/* Newsletter Section */}
        {settings.show_newsletter !== false && (
          <NewsletterSection isBangla={isBangla} />
        )}

        {/* Footer */}
        <Footer />

        {/* Contact Modal - reuse from Theme 1 */}
        <Dialog open={showContactModal} onOpenChange={(open) => {
          setShowContactModal(open);
          if (!open) {
            deleteRecording();
            setMessage("");
          }
        }}>
          <DialogContent className="sm:max-w-lg rounded-2xl">
            <DialogHeader>
              <DialogTitle className={`text-xl font-bold text-left ${isBangla ? 'bangla-text' : ''}`}>{t.contactConsultant}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Textarea
                placeholder={t.writeMessage}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="resize-none"
              />
              {/* Voice Recording Section */}
              <div className="border rounded-xl p-4 bg-gray-50">
                <Label className="text-sm font-medium text-gray-700 mb-3 block">🎤 Voice Message (Optional - Max 2 mins)</Label>
                {!audioUrl ? (
                  <div className="flex items-center gap-4">
                    {!isRecording ? (
                      <Button type="button" onClick={startRecording} className="bg-red-500 hover:bg-red-600 text-white rounded-full w-14 h-14 p-0">
                        <Mic className="h-6 w-6" />
                      </Button>
                    ) : (
                      <Button type="button" onClick={stopRecording} className="bg-gray-800 hover:bg-gray-900 text-white rounded-full w-14 h-14 p-0 animate-pulse">
                        <Square className="h-5 w-5" />
                      </Button>
                    )}
                    {isRecording && (
                      <div className="flex items-center gap-3">
                        <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                        <span className="text-red-600 font-semibold">Recording... {formatTime(recordingTime)} / {formatTime(MAX_RECORDING_TIME)}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg border">
                    <Button type="button" onClick={togglePlayback} className="bg-green-500 hover:bg-green-600 text-white rounded-full w-10 h-10 p-0">
                      {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                    </Button>
                    <div className="flex-1">
                      <span className="text-green-600 font-medium">Voice recorded</span>
                      <span className="text-gray-500 text-sm ml-2">({formatTime(recordingTime)})</span>
                    </div>
                    <Button type="button" onClick={deleteRecording} variant="ghost" className="text-red-500 hover:bg-red-50 rounded-full w-10 h-10 p-0">
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleSendMessage} disabled={sendingMessage || (!message.trim() && !audioBlob)} className="bg-red-500 hover:bg-red-600 rounded-xl">
                {sendingMessage ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                {t.sendMessage}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Thank You Modal */}
        <Dialog open={showThankYouModal} onOpenChange={setShowThankYouModal}>
          <DialogContent className="sm:max-w-md rounded-3xl p-0 overflow-hidden border-0 shadow-2xl">
            {/* Green Gradient Header - exact colors from screenshot */}
            <div className="bg-gradient-to-br from-[#009b39] to-[#014932] p-10 text-center text-white relative">
              {/* Glassy Circle with Checkmark */}
              <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-5 backdrop-blur-md border border-white/30 shadow-lg">
                <CheckCircle className="h-14 w-14 text-white drop-shadow-lg" />
              </div>
              <h2 className={`text-3xl font-bold mb-3 drop-shadow-md ${isBangla ? 'bangla-text' : ''}`}>
                {t.thankYouTitle}
              </h2>
              <p className={`text-white/95 text-base leading-relaxed ${isBangla ? 'bangla-text' : ''}`}>
                {t.thankYouMessage}
              </p>
            </div>
            {/* Bottom Section */}
            <div className="p-7 text-center bg-white">
              <div className="flex items-center justify-center gap-3 mb-5 text-gray-700">
                <BookOpen className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className={`text-sm leading-relaxed ${isBangla ? 'bangla-text' : ''}`}>
                  {t.thankYouBlogText}
                </p>
              </div>
              <Button 
                onClick={() => {
                  setShowThankYouModal(false);
                  navigate('/news');
                }}
                className={`bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-10 py-4 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all text-base ${isBangla ? 'bangla-text' : ''}`}
              >
                <FileText className="h-5 w-5 mr-2" />
                {t.visitBlog}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Country Modal */}
        <Dialog open={showCountryModal} onOpenChange={setShowCountryModal}>
          <DialogContent className="sm:max-w-3xl rounded-2xl max-h-[75vh] overflow-y-auto p-0 top-[58%]">
            {selectedCountryPage && (
              <>
                <div className="h-40 bg-cover bg-center relative" style={{ backgroundImage: `url(${selectedCountryPage.hero_image || 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800'})` }}>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-6 text-white">
                    <h2 className={`text-2xl font-bold flex items-center gap-2 ${isBangla ? 'bangla-text' : ''}`}>
                      <img 
                        src={getCountryFlagUrl(selectedCountryPage.country_name, countryPages)} 
                        alt={`${selectedCountryPage.country_name} flag`}
                        className="w-10 h-7 object-cover rounded shadow"
                        onError={(e) => e.target.src = 'https://flagcdn.com/w80/un.png'}
                      />
                      {isBangla && selectedCountryPage.hero_title_bn 
                        ? selectedCountryPage.hero_title_bn 
                        : (selectedCountryPage.hero_title || `Study in ${selectedCountryPage.country_name}`)}
                    </h2>
                  </div>
                </div>
                <div className="p-6">
                  {(isBangla ? selectedCountryPage.description_bn : selectedCountryPage.description) && (
                    <p className={`text-gray-600 mb-6 ${isBangla ? 'bangla-text' : ''}`}>
                      {isBangla && selectedCountryPage.description_bn ? selectedCountryPage.description_bn : selectedCountryPage.description}
                    </p>
                  )}
                  <div className="mb-6">
                    <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 text-gray-800 ${isBangla ? 'bangla-text' : ''}`}>
                      <FileText className="h-5 w-5 text-red-500" /> {isBangla ? 'প্রয়োজনীয় কাগজপত্র' : 'Document Checklist'}
                    </h3>
                    {selectedCountryPage.document_checklist?.length > 0 ? (
                      <div className="space-y-3">
                        {selectedCountryPage.document_checklist.map((item, index) => (
                          <div key={item.id || index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${item.required ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>{index + 1}</div>
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-800">{item.title}</h4>
                              {item.description && <p className="text-sm text-gray-500 mt-1">{item.description}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <p className={`text-gray-500 text-sm ${isBangla ? 'bangla-text' : ''}`}>{isBangla ? 'এখনো কোনো কাগজপত্র তালিকাভুক্ত করা হয়নি।' : 'No documents listed yet.'}</p>}
                  </div>
                  {(isBangla ? selectedCountryPage.additional_info_bn : selectedCountryPage.additional_info) && (
                    <div className="mb-6 p-4 bg-blue-50 rounded-xl">
                      <h3 className={`font-bold text-lg mb-2 text-gray-800 ${isBangla ? 'bangla-text' : ''}`}>
                        {isBangla ? 'অতিরিক্ত তথ্য' : 'Additional Info'}
                      </h3>
                      <p className={`text-gray-600 text-sm ${isBangla ? 'bangla-text' : ''}`}>
                        {isBangla && selectedCountryPage.additional_info_bn ? selectedCountryPage.additional_info_bn : selectedCountryPage.additional_info}
                      </p>
                    </div>
                  )}
                  <div className="flex gap-3 justify-end pt-4 border-t">
                    <Button variant="outline" onClick={() => { setShowCountryModal(false); navigate(`/country/${selectedCountryPage.slug}`); }} className={`rounded-xl ${isBangla ? 'bangla-text' : ''}`}>
                      <ExternalLink className="h-4 w-4 mr-2" /> {isBangla ? 'বিস্তারিত দেখুন' : 'View Full Page'}
                    </Button>
                    <Button onClick={() => setShowCountryModal(false)} className={`bg-red-500 hover:bg-red-600 rounded-xl ${isBangla ? 'bangla-text' : ''}`}>{isBangla ? 'বন্ধ করুন' : 'Close'}</Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Theme 1 (Original) Render
  
  return (
    <div className={`min-h-screen bg-gray-50 ${isBangla ? 'bangla-text' : ''}`}>

      {/* Hero Section with Background Image */}
      <div 
        className="hero-video-section h-[280px] sm:h-[320px] md:h-[360px] lg:h-[400px] relative"
        style={{ backgroundImage: `url(${HERO_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        {/* Hero Content */}
        <div className="hero-content h-full flex items-center">
          <div className="w-full max-w-7xl mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
            <div className="max-w-3xl">
              {/* Title row with Plane icon */}
              <div className="flex items-start gap-4 mb-3">
                <Plane className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 lg:h-12 lg:w-12 text-white flex-shrink-0 mt-1" />
                <h1 className={`text-2xl sm:text-3xl md:text-4xl lg:text-[42px] xl:text-5xl font-bold text-white leading-tight tracking-tight ${isBangla ? 'bangla-text' : ''}`}>
                  {t.heroTitle}
                </h1>
              </div>
              <p className={`text-sm sm:text-base md:text-lg lg:text-xl text-gray-200 leading-relaxed ml-12 sm:ml-[52px] md:ml-14 lg:ml-16 max-w-2xl ${isBangla ? 'bangla-text' : ''}`}>
                {t.heroSubtitle}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 -mt-16 relative z-20 pb-12">
        <Card className="glassy-card shadow-2xl border-0 rounded-2xl overflow-hidden" data-testid="eligibility-form-card">
          <CardHeader className="bg-white border-b border-gray-100 px-6 py-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg">
                  <GraduationCap className="h-6 w-6 text-red-500" />
                </div>
                <CardTitle className={`text-xl md:text-2xl font-bold text-gray-800 text-left ${isBangla ? 'bangla-text' : ''}`}>
                  {t.checkEligibility}
                </CardTitle>
              </div>
              {/* Language selector - only show if Bangla is enabled in settings */}
              {settings.enable_bangla_ui && (
                <Select value={lang} onValueChange={setLang}>
                  <SelectTrigger className="w-32" data-testid="language-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="bn" className="bangla-text">বাংলা</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="p-6 md:p-8 bg-white">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Row 1: Name, Phone, Email */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="fullName" className={`text-left block text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.fullName}</Label>
                  <Input
                    id="fullName"
                    data-testid="input-full-name"
                    value={formData.full_name}
                    onChange={(e) => handleInputChange("full_name", e.target.value)}
                    placeholder="Enter your name"
                    className="h-11 border-gray-200 focus:border-red-400 focus:ring-red-400"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className={`text-left block text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.phone}</Label>
                  <Input
                    id="phone"
                    data-testid="input-phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="01XXXXXXXXX"
                    className="h-11 border-gray-200 focus:border-red-400 focus:ring-red-400"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className={`text-left block text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.email}</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="input-email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="your@email.com"
                    className="h-11 border-gray-200 focus:border-red-400 focus:ring-red-400"
                    required
                  />
                </div>
              </div>

              {/* Row 2: Academic Level, Subject Group, Applying For */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <Label className={`text-left block text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.academicLevel}</Label>
                  <Select value={formData.academic_level} onValueChange={(v) => handleInputChange("academic_level", v)}>
                    <SelectTrigger className={`h-11 border-gray-200 ${isBangla ? 'bangla-text' : ''}`} data-testid="select-academic-level">
                      <SelectValue placeholder={t.selectOption} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HSC">HSC</SelectItem>
                      <SelectItem value="Diploma">Diploma</SelectItem>
                      <SelectItem value="BSC">Bachelor (BSC/BA/BBA)</SelectItem>
                      <SelectItem value="MSC">Masters (MSC/MA/MBA)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className={`text-left block text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.subjectGroup}</Label>
                  <Select value={formData.subject_group} onValueChange={(v) => handleInputChange("subject_group", v)}>
                    <SelectTrigger className={`h-11 border-gray-200 ${isBangla ? 'bangla-text' : ''}`} data-testid="select-subject-group">
                      <SelectValue placeholder={t.selectOption} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Science">Science</SelectItem>
                      <SelectItem value="Commerce">Commerce</SelectItem>
                      <SelectItem value="Arts">Arts</SelectItem>
                      <SelectItem value="Others">Others</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gpa" className={`text-left block text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>
                    {formData.academic_level === "HSC" ? t.gpa : t.cgpa}
                  </Label>
                  <Input
                    id="gpa"
                    type="number"
                    step="0.01"
                    data-testid="input-gpa-cgpa"
                    value={formData.gpa_cgpa}
                    onChange={(e) => handleInputChange("gpa_cgpa", e.target.value)}
                    placeholder={formData.academic_level === "HSC" ? "5.00" : "4.00"}
                    min="0"
                    max={formData.academic_level === "HSC" ? "5" : "4"}
                    className={`h-11 border-gray-200 focus:border-red-400 focus:ring-red-400 ${validationErrors.gpa_cgpa ? 'border-red-500 bg-red-50' : ''}`}
                    required
                  />
                  {validationErrors.gpa_cgpa && (
                    <p className={`text-red-500 text-sm mt-1 ${isBangla ? 'bangla-text' : ''}`}>
                      ⚠️ {validationErrors.gpa_cgpa}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 3: Year of Passing, Applying For, English Test */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="yearOfPassing" className={`text-left block text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.yearOfPassing}</Label>
                  <Input
                    id="yearOfPassing"
                    type="number"
                    data-testid="input-year-of-passing"
                    value={formData.year_of_passing}
                    onChange={(e) => handleInputChange("year_of_passing", e.target.value)}
                    placeholder="2023"
                    min="1990"
                    max="2030"
                    className="h-11 border-gray-200 focus:border-red-400 focus:ring-red-400"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className={`text-left block text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.applyingFor}</Label>
                  <Select value={formData.applying_for} onValueChange={(v) => handleInputChange("applying_for", v)}>
                    <SelectTrigger className={`h-11 border-gray-200 ${isBangla ? 'bangla-text' : ''}`} data-testid="select-applying-for">
                      <SelectValue placeholder={t.selectOption} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UG">UG (Bachelor)</SelectItem>
                      <SelectItem value="PG">PG (Masters)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className={`text-left block text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.englishTest}</Label>
                  <Select 
                    value={formData.english_test_type} 
                    onValueChange={(v) => handleInputChange("english_test_type", v)}
                  >
                    <SelectTrigger className={`h-11 border-gray-200 ${isBangla ? 'bangla-text' : ''}`} data-testid="select-english-test">
                      <SelectValue placeholder={isBangla ? "IELTS/PTE/OIETC নির্বাচন করুন..." : "Select IELTS/PTE/OIETC..."} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ielts">IELTS</SelectItem>
                      <SelectItem value="pte">PTE</SelectItem>
                      <SelectItem value="oietc">OIETC</SelectItem>
                      <SelectItem value="moi">MOI (Medium of Instruction)</SelectItem>
                      <SelectItem value="without_any">{isBangla ? "টেস্ট ছাড়াই" : "Without Any Test"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* IELTS Band (conditional) */}
              {formData.english_test_type === "ielts" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="ieltsBand" className={`text-left block text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.ieltsBand}</Label>
                    <Input
                      id="ieltsBand"
                      type="number"
                      step="0.5"
                      data-testid="input-ielts-band"
                      value={formData.ielts_band}
                      onChange={(e) => handleInputChange("ielts_band", e.target.value)}
                      placeholder="7.0"
                      min="0"
                      max="9"
                      className={`h-11 border-gray-200 focus:border-red-400 focus:ring-red-400 ${validationErrors.ielts_band ? 'border-red-500 bg-red-50' : ''}`}
                    />
                    {validationErrors.ielts_band && (
                      <p className={`text-red-500 text-sm mt-1 ${isBangla ? 'bangla-text' : ''}`}>
                        ⚠️ {validationErrors.ielts_band}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* PTE Score (conditional) */}
              {formData.english_test_type === "pte" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="pteScore" className={`text-left block text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.pteScore}</Label>
                    <Input
                      id="pteScore"
                      type="number"
                      data-testid="input-pte-score"
                      value={formData.pte_score}
                      onChange={(e) => handleInputChange("pte_score", e.target.value)}
                      placeholder="65"
                      min="10"
                      max="90"
                      className={`h-11 border-gray-200 focus:border-red-400 focus:ring-red-400 ${validationErrors.pte_score ? 'border-red-500 bg-red-50' : ''}`}
                    />
                    {validationErrors.pte_score && (
                      <p className={`text-red-500 text-sm mt-1 ${isBangla ? 'bangla-text' : ''}`}>
                        ⚠️ {validationErrors.pte_score}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* OIETC Score (conditional) */}
              {formData.english_test_type === "oietc" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="oietcScore" className={`text-left block text-gray-700 font-medium ${isBangla ? 'bangla-text' : ''}`}>{t.oietcScore}</Label>
                    <Input
                      id="oietcScore"
                      type="number"
                      data-testid="input-oietc-score"
                      value={formData.oietc_score}
                      onChange={(e) => handleInputChange("oietc_score", e.target.value)}
                      placeholder="6.0"
                      min="0"
                      max="12"
                      step="0.5"
                      className="h-11 border-gray-200 focus:border-red-400 focus:ring-red-400"
                    />
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-2">
                <Button 
                  type="submit" 
                  className={`cta-button bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-10 py-6 text-lg font-semibold rounded-xl ${isBangla ? 'bangla-text' : ''}`}
                  disabled={loading}
                  data-testid="btn-check-eligibility"
                >
                  {loading ? (
                    <span className={`flex items-center gap-2 ${isBangla ? 'bangla-text' : ''}`}>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {t.checking}
                    </span>
                  ) : (
                    t.checkBtn
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-10 text-center shadow-2xl">
              <Loader2 className="h-14 w-14 animate-spin mx-auto text-red-500 mb-5" />
              <p className={`text-xl font-semibold text-gray-800 ${isBangla ? 'bangla-text' : ''}`}>{t.checking}</p>
            </div>
          </div>
        )}

        {/* Results Section */}
        {result && (
          <Card className="mt-8 shadow-xl border-0 rounded-2xl overflow-hidden" data-testid="eligibility-result-card">
            <CardContent className="p-6 md:p-8 bg-white">
              {/* Study Gap */}
              <p className={`text-gray-500 italic mb-6 text-left ${isBangla ? 'bangla-text' : ''}`} data-testid="calculated-gap">
                {t.studyGap}: <span className="font-semibold text-gray-700">{result.calculated_gap} {t.years}</span>
              </p>

              {/* Eligible Countries */}
              {result.eligible_countries.length > 0 && (
                <div className="mb-8">
                  <h3 className={`text-xl md:text-2xl font-bold text-green-600 mb-5 text-left flex items-center gap-2 ${isBangla ? 'bangla-text' : ''}`} data-testid="eligible-heading">
                    <CheckCircle className="h-7 w-7" />
                    {t.congratulations}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {result.eligible_countries.map((country, idx) => (
                      <button 
                        key={idx}
                        onClick={() => handleCountryClick(country)}
                        className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 hover:border-green-300 hover:shadow-lg transition-all cursor-pointer group"
                        data-testid={`eligible-country-${country}`}
                      >
                        <img 
                          src={getCountryFlagUrl(country, countryPages)} 
                          alt={`${country} flag`}
                          className="w-8 h-6 rounded object-cover flex-shrink-0 shadow-sm"
                          onError={(e) => e.target.src = 'https://flagcdn.com/w80/un.png'}
                        />
                        <div className="flex-1 text-left min-w-0">
                          <span className="text-gray-800 font-bold text-sm block truncate">{country}</span>
                          <span className="text-xs text-gray-400 flex items-center gap-1 group-hover:text-green-500 transition-colors">
                            <ExternalLink className="h-3 w-3" /> Click for details
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggestions */}
              {Object.keys(result.suggestions).length > 0 && (
                <div className="mb-8">
                  <h3 className={`text-lg md:text-xl font-bold text-gray-800 mb-5 text-left flex items-center gap-2 ${isBangla ? 'bangla-text' : ''}`} data-testid="suggestions-heading">
                    <AlertCircle className="h-6 w-6 text-amber-500" />
                    {t.suggestions}
                  </h3>
                  <div className="space-y-3 text-left">
                    {Object.entries(result.suggestions).map(([country, reasons], idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100 ${isBangla ? 'bangla-text' : ''}`}
                        data-testid={`suggestion-${country}`}
                      >
                        <img 
                          src={getCountryFlagUrl(country, countryPages)} 
                          alt={`${country} flag`}
                          className="w-6 h-5 rounded object-cover flex-shrink-0 mt-0.5"
                          onError={(e) => e.target.src = 'https://flagcdn.com/w80/un.png'}
                        />
                        <div>
                          <span className="font-bold text-gray-800">{country}:</span>{" "}
                          <span className="text-gray-600">
                            {reasons.map(r => translateSuggestion(r, isBangla)).join(isBangla ? " এবং " : " & ")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Consultant Button */}
              <div className="flex flex-wrap gap-3 text-left">
                <Button 
                  onClick={() => setShowContactModal(true)}
                  className={`bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg transition-all duration-300 ${isBangla ? 'bangla-text' : ''}`}
                  data-testid="btn-contact-consultant"
                >
                  {t.contactConsultant}
                </Button>
                <Button 
                  onClick={resetForm}
                  variant="outline"
                  className={`px-6 py-6 text-lg font-semibold rounded-xl border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-blue-700 transition-all duration-300 ${isBangla ? 'bangla-text' : ''}`}
                  title={t.resetFormTooltip}
                  data-testid="btn-reset-form"
                >
                  <RefreshCw className="h-5 w-5 mr-2" />
                  {t.resetForm}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Modal */}
        <Dialog open={showContactModal} onOpenChange={(open) => {
          setShowContactModal(open);
          if (!open) {
            deleteRecording();
            setMessage("");
          }
        }}>
          <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="contact-modal">
            <DialogHeader>
              <DialogTitle className={`text-xl font-bold text-left ${isBangla ? 'bangla-text' : ''}`}>{t.contactConsultant}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Text Message */}
              <Textarea
                placeholder={t.writeMessage}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className={`resize-none border-gray-200 focus:border-red-400 focus:ring-red-400 ${isBangla ? 'bangla-text' : ''}`}
                data-testid="textarea-message"
              />
              
              {/* Voice Message Section */}
              <div className="border rounded-xl p-4 bg-gray-50">
                <Label className="text-sm font-medium text-gray-700 mb-3 block text-left">
                  🎤 Voice Message (Optional - Max 2 mins)
                </Label>
                
                {!audioUrl ? (
                  // Recording Controls
                  <div className="flex items-center gap-4">
                    {!isRecording ? (
                      <Button
                        type="button"
                        onClick={startRecording}
                        className="bg-red-500 hover:bg-red-600 text-white rounded-full w-14 h-14 p-0 flex items-center justify-center shadow-lg"
                      >
                        <Mic className="h-6 w-6" />
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={stopRecording}
                        className="bg-gray-800 hover:bg-gray-900 text-white rounded-full w-14 h-14 p-0 flex items-center justify-center shadow-lg animate-pulse"
                      >
                        <Square className="h-5 w-5" />
                      </Button>
                    )}
                    
                    {isRecording && (
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                          <span className="text-red-600 font-semibold">Recording...</span>
                        </div>
                        <span className="text-gray-600 font-mono text-lg">
                          {formatTime(recordingTime)} / {formatTime(MAX_RECORDING_TIME)}
                        </span>
                      </div>
                    )}
                    
                    {!isRecording && !audioUrl && (
                      <span className="text-gray-500 text-sm">Click to record voice message</span>
                    )}
                  </div>
                ) : (
                  // Playback Controls
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg border">
                    <Button
                      type="button"
                      onClick={togglePlayback}
                      className="bg-green-500 hover:bg-green-600 text-white rounded-full w-10 h-10 p-0 flex items-center justify-center"
                    >
                      {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                    </Button>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600 font-medium">Voice recorded</span>
                        <span className="text-gray-500 text-sm">({formatTime(recordingTime)})</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div className="bg-green-500 h-1.5 rounded-full" style={{width: '100%'}}></div>
                      </div>
                    </div>
                    
                    <Button
                      type="button"
                      onClick={deleteRecording}
                      variant="ghost"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-full w-10 h-10 p-0"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button 
                onClick={handleSendMessage}
                disabled={sendingMessage || (!message.trim() && !audioBlob)}
                className={`bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 px-6 py-3 rounded-xl ${isBangla ? 'bangla-text' : ''}`}
                data-testid="btn-send-message"
              >
                {sendingMessage ? (
                  <span className={`flex items-center gap-2 ${isBangla ? 'bangla-text' : ''}`}>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.sending}
                  </span>
                ) : (
                  <span className={`flex items-center gap-2 ${isBangla ? 'bangla-text' : ''}`}>
                    <Send className="h-4 w-4" />
                    {t.sendMessage}
                  </span>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Thank You Modal - Theme 2 */}
        <Dialog open={showThankYouModal} onOpenChange={setShowThankYouModal}>
          <DialogContent className="sm:max-w-md rounded-3xl p-0 overflow-hidden border-0 shadow-2xl">
            {/* Green Gradient Header - exact colors from screenshot */}
            <div className="bg-gradient-to-br from-[#009b39] to-[#014932] p-10 text-center text-white relative">
              {/* Glassy Circle with Checkmark */}
              <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-5 backdrop-blur-md border border-white/30 shadow-lg">
                <CheckCircle className="h-14 w-14 text-white drop-shadow-lg" />
              </div>
              <h2 className={`text-3xl font-bold mb-3 drop-shadow-md ${isBangla ? 'bangla-text' : ''}`}>
                {t.thankYouTitle}
              </h2>
              <p className={`text-white/95 text-base leading-relaxed ${isBangla ? 'bangla-text' : ''}`}>
                {t.thankYouMessage}
              </p>
            </div>
            {/* Bottom Section */}
            <div className="p-7 text-center bg-white">
              <div className="flex items-center justify-center gap-3 mb-5 text-gray-700">
                <BookOpen className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className={`text-sm leading-relaxed ${isBangla ? 'bangla-text' : ''}`}>
                  {t.thankYouBlogText}
                </p>
              </div>
              <Button 
                onClick={() => {
                  setShowThankYouModal(false);
                  navigate('/news');
                }}
                className={`bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-10 py-4 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all text-base ${isBangla ? 'bangla-text' : ''}`}
              >
                <FileText className="h-5 w-5 mr-2" />
                {t.visitBlog}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Blog Carousel Section - Above Newsletter */}
      {settings.show_blog_carousel !== false && (
        <BlogCarouselSection isBangla={isBangla} />
      )}

      {/* Newsletter Section */}
      {settings.show_newsletter !== false && (
        <NewsletterSection isBangla={isBangla} />
      )}

      {/* Footer */}
      <Footer />

      {/* Country Details Modal */}
      <Dialog open={showCountryModal} onOpenChange={setShowCountryModal}>
        <DialogContent className="sm:max-w-3xl rounded-2xl max-h-[75vh] overflow-y-auto p-0 top-[58%]">
          {selectedCountryPage && (
            <>
              {/* Modal Hero */}
              <div 
                className="h-40 bg-cover bg-center relative"
                style={{ backgroundImage: `url(${selectedCountryPage.hero_image || 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800'})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                <div className="absolute bottom-4 left-6 text-white">
                  <h2 className={`text-2xl font-bold flex items-center gap-2 ${isBangla ? 'bangla-text' : ''}`}>
                    <MapPin className="h-6 w-6" />
                    {isBangla && selectedCountryPage.hero_title_bn 
                      ? selectedCountryPage.hero_title_bn 
                      : (selectedCountryPage.hero_title || `Study in ${selectedCountryPage.country_name}`)}
                  </h2>
                </div>
              </div>
              
              <div className="p-6">
                {/* Description */}
                {(isBangla ? selectedCountryPage.description_bn : selectedCountryPage.description) && (
                  <p className={`text-gray-600 mb-6 ${isBangla ? 'bangla-text' : ''}`}>
                    {isBangla && selectedCountryPage.description_bn ? selectedCountryPage.description_bn : selectedCountryPage.description}
                  </p>
                )}
                
                {/* Document Checklist */}
                <div className="mb-6">
                  <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 text-gray-800 ${isBangla ? 'bangla-text' : ''}`}>
                    <FileText className="h-5 w-5 text-red-500" />
                    {isBangla ? 'প্রয়োজনীয় কাগজপত্র' : 'Document Checklist'}
                  </h3>
                  {selectedCountryPage.document_checklist && selectedCountryPage.document_checklist.length > 0 ? (
                    <div className="space-y-3">
                      {selectedCountryPage.document_checklist.map((item, index) => (
                        <div 
                          key={item.id || index}
                          className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl"
                        >
                          <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm ${item.required ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-800">{item.title}</h4>
                            {item.description && (
                              <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                            )}
                          </div>
                          {item.required && (
                            <span className={`text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full ${isBangla ? 'bangla-text' : ''}`}>{isBangla ? 'আবশ্যক' : 'Required'}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={`text-gray-500 text-sm ${isBangla ? 'bangla-text' : ''}`}>{isBangla ? 'এখনো কোনো কাগজপত্র তালিকাভুক্ত করা হয়নি।' : 'No documents listed yet.'}</p>
                  )}
                </div>
                
                {/* Video */}
                {selectedCountryPage.video_url && (
                  <div className="mb-6">
                    <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 text-gray-800 ${isBangla ? 'bangla-text' : ''}`}>
                      <Play className="h-5 w-5 text-blue-500" />
                      {isBangla && selectedCountryPage.video_title_bn ? selectedCountryPage.video_title_bn : (selectedCountryPage.video_title || (isBangla ? 'ভিডিও গাইড' : 'Video Guide'))}
                    </h3>
                    <div className="aspect-video rounded-xl overflow-hidden">
                      <iframe
                        src={getYouTubeEmbedUrl(selectedCountryPage.video_url)}
                        title={`${selectedCountryPage.country_name} Study Guide`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                )}
                
                {/* Additional Info */}
                {(isBangla ? selectedCountryPage.additional_info_bn : selectedCountryPage.additional_info) && (
                  <div className="mb-6">
                    <h3 className={`font-bold text-lg mb-2 text-gray-800 ${isBangla ? 'bangla-text' : ''}`}>{isBangla ? 'অতিরিক্ত তথ্য' : 'Additional Information'}</h3>
                    <p className={`text-gray-600 whitespace-pre-wrap ${isBangla ? 'bangla-text' : ''}`}>
                      {isBangla && selectedCountryPage.additional_info_bn ? selectedCountryPage.additional_info_bn : selectedCountryPage.additional_info}
                    </p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-3 justify-end pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCountryModal(false);
                      navigate(`/country/${selectedCountryPage.slug}`);
                    }}
                    className={`rounded-xl ${isBangla ? 'bangla-text' : ''}`}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {isBangla ? 'বিস্তারিত দেখুন' : 'View Full Page'}
                  </Button>
                  <Button
                    onClick={() => setShowCountryModal(false)}
                    className={`bg-red-500 hover:bg-red-600 rounded-xl ${isBangla ? 'bangla-text' : ''}`}
                  >
                    {isBangla ? 'বন্ধ করুন' : 'Close'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin Panel Component
// Admin Login Component
const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      if (res.data.success) {
        sessionStorage.setItem("cst_admin_token", res.data.token);
        sessionStorage.setItem("cst_admin_data", JSON.stringify(res.data.admin));
        // Set default auth header for all subsequent requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
        navigate("/cst/dashboard");
      }
    } catch (err) {
      setError("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-white/20">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Settings className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Admin Access</h1>
            <p className="text-gray-400 text-sm mt-2">Enter your credentials to continue</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <Label className="text-gray-300 text-sm">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-red-500 focus:ring-red-500"
                placeholder="Enter email"
                required
              />
            </div>
            
            <div>
              <Label className="text-gray-300 text-sm">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-gray-500 focus:border-red-500 focus:ring-red-500"
                placeholder="Enter password"
                required
              />
            </div>
            
            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white h-11 font-semibold shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Logging in...
                </>
              ) : (
                "Login"
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Protected Admin Panel wrapper
const ProtectedAdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = sessionStorage.getItem("cst_admin_token");
    if (token && token.startsWith("cst_admin_session_")) {
      // Ensure axios uses this token
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify with backend; if invalid, force re-login
      axios
        .get(`${API}/admin/verify`, { params: { token } })
        .then((res) => {
          if (res.data && res.data.valid) {
            setIsAuthenticated(true);
          } else {
            sessionStorage.removeItem("cst_admin_token");
            sessionStorage.removeItem("cst_admin_data");
            delete axios.defaults.headers.common['Authorization'];
            navigate("/cst");
          }
        })
        .catch(() => {
          sessionStorage.removeItem("cst_admin_token");
          sessionStorage.removeItem("cst_admin_data");
          delete axios.defaults.headers.common['Authorization'];
          navigate("/cst");
        })
        .finally(() => setChecking(false));
    } else {
      navigate("/cst");
      setChecking(false);
    }
  }, [navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="h-8 w-8 animate-spin text-red-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <AdminPanel />;
};

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState("rules");
  const [rules, setRules] = useState([]);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [countryPages, setCountryPages] = useState([]);
  const [selectedCountryPage, setSelectedCountryPage] = useState(null);
  const [showCountryPageModal, setShowCountryPageModal] = useState(false);
  
  // Ads Management States
  const [ads, setAds] = useState([]);
  const [showAdModal, setShowAdModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [adForm, setAdForm] = useState({
    title: "",
    ad_type: "banner",
    image_url: "",
    video_url: "",
    link_url: "",
    placement_type: "country_page",
    country_slugs: [],
    show_delay_seconds: 5,
    is_active: true
  });
  
  // Blog Management States
  const [blogPosts, setBlogPosts] = useState([]);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    feature_image: "",
    video_url: "",
    author: "Admin",
    category: "General",
    tags: [],
    is_published: false
  });
  const [uploadingBlogImage, setUploadingBlogImage] = useState(false);
  
  // Admin Management States
  const [admins, setAdmins] = useState([]);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [adminForm, setAdminForm] = useState({ 
    email: "", 
    password: "", 
    name: "",
    role: "admin",
    permissions: {
      country_rules: false,
      country_pages: false,
      blog_posts: false,
      leads: false,
      ads: false,
      settings: false,
      traffic: false
    }
  });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityStats, setActivityStats] = useState(null);

  useEffect(() => {
    fetchData();
    // Get current admin data
    const adminData = sessionStorage.getItem("cst_admin_data");
    if (adminData) {
      const admin = JSON.parse(adminData);
      setCurrentAdmin(admin);
      
      // Set default active tab based on permissions for moderator
      if (admin.role === 'moderator' && admin.permissions) {
        const tabPermissionMap = [
          { tab: 'rules', permission: 'country_rules' },
          { tab: 'pages', permission: 'country_pages' },
          { tab: 'blog', permission: 'blog_posts' },
          { tab: 'leads', permission: 'leads' },
          { tab: 'ads', permission: 'ads' },
          { tab: 'traffic', permission: 'traffic' },
          { tab: 'settings', permission: 'settings' }
        ];
        
        // Find first tab with permission
        const firstAllowedTab = tabPermissionMap.find(item => admin.permissions[item.permission]);
        if (firstAllowedTab) {
          setActiveTab(firstAllowedTab.tab);
        }
      }
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Main data - these are critical
      const [rulesRes, leadsRes, settingsRes, pagesRes, adsRes, blogRes, adminsRes] = await Promise.all([
        axios.get(`${API}/country-rules`),
        axios.get(`${API}/leads`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/country-pages`),
        axios.get(`${API}/ads`),
        axios.get(`${API}/blog-posts`),
        axios.get(`${API}/admins`)
      ]);
      setRules(rulesRes.data);
      setLeads(leadsRes.data);
      setSettings(settingsRes.data);
      setCountryPages(pagesRes.data);
      setAds(adsRes.data);
      setBlogPosts(blogRes.data);
      setAdmins(adminsRes.data);
      
      // Activity logs - these are optional, don't break if they fail
      try {
        const [logsRes, statsRes] = await Promise.all([
          axios.get(`${API}/activity-logs?limit=100`),
          axios.get(`${API}/activity-logs/stats`)
        ]);
        setActivityLogs(logsRes.data.logs || []);
        setActivityStats(statsRes.data);
      } catch (logError) {
        console.warn("Activity logs not available:", logError);
        setActivityLogs([]);
        setActivityStats(null);
      }
      
      // Newsletter subscribers - optional
      try {
        const newsletterRes = await axios.get(`${API}/newsletter/subscribers`);
        setNewsletterSubscribers(newsletterRes.data.subscribers || []);
        setNewsletterStats({
          total: newsletterRes.data.total || 0,
          active: newsletterRes.data.active || 0,
          unsubscribed: newsletterRes.data.unsubscribed || 0
        });
        // Fetch click stats
        const clickRes = await axios.get(`${API}/newsletter/all-clicks`);
        setClickStats(clickRes.data);
      } catch (nlError) {
        console.warn("Newsletter data not available:", nlError);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      try {
        await axios.post(`${API}/seed`);
        const rulesRes = await axios.get(`${API}/country-rules`);
        setRules(rulesRes.data);
      } catch (seedError) {
        console.error("Error seeding:", seedError);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Admin Management Functions
  const handleCreateAdmin = async () => {
    if (!adminForm.email || !adminForm.password) {
      toast.error("Email and password are required");
      return;
    }
    try {
      const payload = {
        email: adminForm.email,
        password: adminForm.password,
        name: adminForm.name,
        role: adminForm.role,
        permissions: adminForm.role === "moderator" ? adminForm.permissions : null
      };
      await axios.post(`${API}/admins`, payload);
      toast.success(adminForm.role === "moderator" ? "SuperVisor created successfully" : "Admin created successfully");
      setShowAdminModal(false);
      setAdminForm({ 
        email: "", 
        password: "", 
        name: "",
        role: "admin",
        permissions: {
          country_rules: false,
          country_pages: false,
          blog_posts: false,
          leads: false,
          ads: false,
          settings: false,
          traffic: false
        }
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create admin");
    }
  };
  
  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (!currentAdmin?.id) {
      toast.error("Admin not found");
      return;
    }
    try {
      await axios.put(`${API}/admins/${currentAdmin.id}/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success("Password changed successfully");
      setShowPasswordModal(false);
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
  };
  
  const handleDeleteAdmin = async (adminId) => {
    if (!confirm("Are you sure you want to delete this admin?")) return;
    try {
      await axios.delete(`${API}/admins/${adminId}`);
      toast.success("Admin deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete admin");
    }
  };
  
  // Permission Check Helper - for moderator role
  const hasPermission = (permissionKey) => {
    // If currentAdmin not loaded yet, show all tabs (will re-render when loaded)
    if (!currentAdmin) {
      return true;
    }
    // Super admin and admin have all permissions
    if (currentAdmin.role === 'super_admin' || currentAdmin.role === 'admin') {
      return true;
    }
    // Moderator - check specific permission
    if (currentAdmin.role === 'moderator' && currentAdmin.permissions) {
      return currentAdmin.permissions[permissionKey] === true;
    }
    return false;
  };
  
  // Check if user can manage admins (only super_admin and admin)
  const canManageAdmins = () => {
    // If currentAdmin not loaded yet, show tabs (will re-render when loaded)
    if (!currentAdmin) {
      return true;
    }
    return currentAdmin.role === 'super_admin' || currentAdmin.role === 'admin';
  };
  
  // Ads Management Functions
  const resetAdForm = () => {
    setAdForm({
      title: "",
      ad_type: "banner",
      image_url: "",
      video_url: "",
      link_url: "",
      placement_type: "country_page",
      country_slugs: [],
      show_delay_seconds: 5,
      is_active: true
    });
    setEditingAd(null);
  };
  
  const openAdModal = (ad = null) => {
    if (ad) {
      setEditingAd(ad);
      setAdForm({
        title: ad.title || "",
        ad_type: ad.ad_type || "banner",
        image_url: ad.image_url || "",
        video_url: ad.video_url || "",
        link_url: ad.link_url || "",
        placement_type: ad.placement_type || "country_page",
        country_slugs: ad.country_slugs || [],
        show_delay_seconds: ad.show_delay_seconds || 5,
        is_active: ad.is_active !== false
      });
    } else {
      resetAdForm();
    }
    setShowAdModal(true);
  };
  
  const saveAd = async () => {
    if (!adForm.title.trim()) {
      toast.error("Ad title is required");
      return;
    }
    
    if (adForm.ad_type === "video" && !adForm.video_url) {
      toast.error("Video URL is required for video ads");
      return;
    }
    
    if ((adForm.ad_type === "banner" || adForm.ad_type === "banner_link") && !adForm.image_url) {
      toast.error("Image URL is required for banner ads");
      return;
    }
    
    try {
      if (editingAd) {
        await axios.put(`${API}/ads/${editingAd.id}`, adForm);
        toast.success("Ad updated successfully!");
      } else {
        await axios.post(`${API}/ads`, adForm);
        toast.success("Ad created successfully!");
      }
      setShowAdModal(false);
      resetAdForm();
      // Refresh ads list
      const adsRes = await axios.get(`${API}/ads`);
      setAds(adsRes.data);
    } catch (error) {
      console.error("Error saving ad:", error);
      toast.error("Failed to save ad");
    }
  };
  
  const deleteAd = async (adId) => {
    if (!window.confirm("Are you sure you want to delete this ad?")) return;
    
    try {
      await axios.delete(`${API}/ads/${adId}`);
      toast.success("Ad deleted successfully!");
      setAds(ads.filter(a => a.id !== adId));
    } catch (error) {
      console.error("Error deleting ad:", error);
      toast.error("Failed to delete ad");
    }
  };
  
  const toggleAdStatus = async (ad) => {
    try {
      await axios.put(`${API}/ads/${ad.id}`, { is_active: !ad.is_active });
      setAds(ads.map(a => a.id === ad.id ? { ...a, is_active: !a.is_active } : a));
      toast.success(`Ad ${!ad.is_active ? 'activated' : 'deactivated'}!`);
    } catch (error) {
      console.error("Error toggling ad status:", error);
      toast.error("Failed to update ad status");
    }
  };
  
  // Newsletter Management
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [newsletterStats, setNewsletterStats] = useState({ total: 0, active: 0, unsubscribed: 0 });
  const [loadingNewsletter, setLoadingNewsletter] = useState(false);
  const [sendingNewsletter, setSendingNewsletter] = useState({});
  const [showSubscribersModal, setShowSubscribersModal] = useState(false);
  const [subscribersModalFilter, setSubscribersModalFilter] = useState('all'); // 'all', 'active', 'unsubscribed'
  const [showLeadsPreviewModal, setShowLeadsPreviewModal] = useState(false);
  const [leadsForNewsletter, setLeadsForNewsletter] = useState([]);
  const [selectedBlogForNewsletter, setSelectedBlogForNewsletter] = useState(null);
  
  // Newsletter Click Tracking
  const [clickStats, setClickStats] = useState({ total_clicks: 0, total_unique_clickers: 0, by_blog: [] });
  const [showClickersModal, setShowClickersModal] = useState(false);
  const [selectedBlogClickers, setSelectedBlogClickers] = useState({ blog_id: '', blog_title: '', clickers: [] });
  
  const fetchClickStats = async () => {
    try {
      const res = await axios.get(`${API}/newsletter/all-clicks`);
      setClickStats(res.data);
    } catch (error) {
      console.warn("Click stats not available:", error);
    }
  };
  
  const viewBlogClickers = async (blogId, blogTitle) => {
    try {
      const res = await axios.get(`${API}/newsletter/clicks/${blogId}`);
      setSelectedBlogClickers({
        blog_id: blogId,
        blog_title: blogTitle,
        clickers: res.data.clickers || []
      });
      setShowClickersModal(true);
    } catch (error) {
      toast.error("Failed to load click data");
    }
  };
  
  const fetchNewsletterSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/newsletter/subscribers`);
      setNewsletterSubscribers(res.data.subscribers || []);
      setNewsletterStats({
        total: res.data.total || 0,
        active: res.data.active || 0,
        unsubscribed: res.data.unsubscribed || 0
      });
    } catch (error) {
      console.error("Error fetching newsletter subscribers:", error);
    }
  };
  
  const deleteNewsletterSubscriber = async (subscriberId) => {
    if (!window.confirm("Are you sure you want to delete this subscriber?")) return;
    try {
      await axios.delete(`${API}/newsletter/subscribers/${subscriberId}`);
      toast.success("Subscriber deleted!");
      fetchNewsletterSubscribers();
    } catch (error) {
      toast.error("Failed to delete subscriber");
    }
  };
  
  const addLeadToNewsletter = async (lead) => {
    try {
      const res = await axios.post(`${API}/newsletter/add-from-lead/${lead.id}`);
      if (res.data.warning) {
        toast.error(res.data.message, { duration: 6000 });
      } else if (res.data.already_subscribed) {
        toast.info(res.data.message);
      } else {
        toast.success(res.data.message);
        fetchNewsletterSubscribers();
      }
    } catch (error) {
      toast.error("Failed to add to newsletter");
    }
  };
  
  const sendNewsletterForBlog = async (blogId, includeLeads = false) => {
    setSendingNewsletter(prev => ({ ...prev, [blogId]: true }));
    try {
      const res = await axios.post(`${API}/newsletter/send/${blogId}?include_leads=${includeLeads}`);
      toast.success(`Newsletter sent to ${res.data.sent_count} subscribers!`);
      if (res.data.failed_count > 0) {
        toast.warning(`${res.data.failed_count} emails failed to send`);
      }
    } catch (error) {
      toast.error("Failed to send newsletter");
    } finally {
      setSendingNewsletter(prev => ({ ...prev, [blogId]: false }));
    }
  };
  
  // Open subscribers modal with filter
  const openSubscribersModal = (filter) => {
    setSubscribersModalFilter(filter);
    setShowSubscribersModal(true);
  };
  
  // Get filtered subscribers for modal
  const getFilteredSubscribers = () => {
    if (subscribersModalFilter === 'active') {
      return newsletterSubscribers.filter(s => s.is_active);
    } else if (subscribersModalFilter === 'unsubscribed') {
      return newsletterSubscribers.filter(s => !s.is_active);
    }
    return newsletterSubscribers;
  };
  
  // Preview leads before sending newsletter
  const previewLeadsForNewsletter = async (blogId) => {
    setSelectedBlogForNewsletter(blogId);
    try {
      // Get leads that are not already subscribers and not unsubscribed
      const existingEmails = new Set(newsletterSubscribers.map(s => s.email.toLowerCase()));
      const unsubscribedEmails = new Set(
        newsletterSubscribers.filter(s => !s.is_active).map(s => s.email.toLowerCase())
      );
      
      const eligibleLeads = leads.filter(lead => {
        const email = (lead.email || '').toLowerCase();
        return email && !existingEmails.has(email) && !unsubscribedEmails.has(email);
      });
      
      setLeadsForNewsletter(eligibleLeads);
      setShowLeadsPreviewModal(true);
    } catch (error) {
      toast.error("Failed to load leads preview");
    }
  };
  
  // Send newsletter including leads
  const confirmSendWithLeads = async () => {
    if (!selectedBlogForNewsletter) return;
    setShowLeadsPreviewModal(false);
    await sendNewsletterForBlog(selectedBlogForNewsletter, true);
    setSelectedBlogForNewsletter(null);
  };
  
  // Blog Management Functions
  const resetBlogForm = () => {
    setBlogForm({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      feature_image: "",
      video_url: "",
      author: "Admin",
      category: "General",
      tags: [],
      is_published: false
    });
    setEditingBlog(null);
  };
  
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };
  
  const openBlogModal = (post = null) => {
    if (post) {
      setEditingBlog(post);
      setBlogForm({
        title: post.title || "",
        slug: post.slug || "",
        excerpt: post.excerpt || "",
        content: post.content || "",
        feature_image: post.feature_image || "",
        video_url: post.video_url || "",
        author: post.author || "Admin",
        category: post.category || "General",
        tags: post.tags || [],
        is_published: post.is_published || false
      });
    } else {
      resetBlogForm();
    }
    setShowBlogModal(true);
  };
  
  const handleBlogImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingBlogImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/upload-logo`, formData);
      setBlogForm(prev => ({ ...prev, feature_image: res.data.file_url }));
      toast.success("Image uploaded!");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingBlogImage(false);
    }
  };
  
  const saveBlogPost = async () => {
    if (!blogForm.title || !blogForm.slug) {
      toast.error("Title and URL slug are required");
      return;
    }
    
    try {
      if (editingBlog) {
        await axios.put(`${API}/blog-posts/${editingBlog.slug}`, blogForm);
        toast.success("Blog post updated!");
        setBlogPosts(blogPosts.map(p => p.slug === editingBlog.slug ? { ...p, ...blogForm } : p));
      } else {
        const res = await axios.post(`${API}/blog-posts`, blogForm);
        toast.success("Blog post created!");
        const newPost = { ...blogForm, id: res.data.id, created_at: new Date().toISOString() };
        setBlogPosts([newPost, ...blogPosts]);
      }
      setShowBlogModal(false);
      resetBlogForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save blog post");
    }
  };
  
  const deleteBlogPost = async (slug) => {
    if (!window.confirm("Are you sure you want to delete this blog post?")) return;
    
    try {
      await axios.delete(`${API}/blog-posts/${slug}`);
      toast.success("Blog post deleted!");
      setBlogPosts(blogPosts.filter(p => p.slug !== slug));
    } catch (error) {
      toast.error("Failed to delete blog post");
    }
  };
  
  const toggleBlogPublish = async (post) => {
    try {
      await axios.put(`${API}/blog-posts/${post.slug}`, { is_published: !post.is_published });
      setBlogPosts(blogPosts.map(p => p.slug === post.slug ? { ...p, is_published: !p.is_published } : p));
      toast.success(`Post ${!post.is_published ? 'published' : 'unpublished'}!`);
    } catch (error) {
      toast.error("Failed to update post status");
    }
  };

  const addNewRule = () => {
    const newRule = {
      id: `temp-${Date.now()}`,
      country_name: "",
      allowed_subject_groups: ["All"],
      ug_gap_limit: 3,
      pg_gap_limit: 5,
      ielts_required_ug: true,
      ielts_required_pg: true,
      ug_min_ielts_band: 6.0,
      pg_min_ielts_band: 6.5,
      pte_accepted_ug: true,
      pte_accepted_pg: true,
      ug_min_pte_score: 50,
      pg_min_pte_score: 58,
      oietc_accepted_ug: false,
      oietc_accepted_pg: false,
      ug_min_oietc_score: 6,
      pg_min_oietc_score: 6,
      moi_accepted_ug: false,
      moi_accepted_pg: false,
      no_test_required_ug: false,
      no_test_required_pg: false,
      ug_min_gpa: 3.5,
      ug_min_diploma_cgpa: 2.5,
      pg_min_cgpa: 2.5,
      isNew: true
    };
    setRules([...rules, newRule]);
  };

  const updateRule = (index, field, value) => {
    const updated = [...rules];
    updated[index] = { ...updated[index], [field]: value };
    setRules(updated);
  };

  const deleteRule = (index) => {
    const updated = rules.filter((_, i) => i !== index);
    setRules(updated);
  };

  const saveRules = async () => {
    try {
      const rulesToSave = rules.map(r => ({
        country_name: r.country_name,
        allowed_subject_groups: r.allowed_subject_groups,
        ug_gap_limit: parseInt(r.ug_gap_limit),
        pg_gap_limit: parseInt(r.pg_gap_limit),
        ielts_required_ug: r.ielts_required_ug,
        ielts_required_pg: r.ielts_required_pg,
        ug_min_ielts_band: parseFloat(r.ug_min_ielts_band),
        pg_min_ielts_band: parseFloat(r.pg_min_ielts_band),
        pte_accepted_ug: r.pte_accepted_ug !== false,
        pte_accepted_pg: r.pte_accepted_pg !== false,
        ug_min_pte_score: parseInt(r.ug_min_pte_score) || 50,
        pg_min_pte_score: parseInt(r.pg_min_pte_score) || 58,
        oietc_accepted_ug: r.oietc_accepted_ug || false,
        oietc_accepted_pg: r.oietc_accepted_pg || false,
        ug_min_oietc_score: parseFloat(r.ug_min_oietc_score) || 6,
        pg_min_oietc_score: parseFloat(r.pg_min_oietc_score) || 6,
        moi_accepted_ug: r.moi_accepted_ug || false,
        moi_accepted_pg: r.moi_accepted_pg || false,
        no_test_required_ug: r.no_test_required_ug || false,
        no_test_required_pg: r.no_test_required_pg || false,
        ug_min_gpa: parseFloat(r.ug_min_gpa),
        ug_min_diploma_cgpa: parseFloat(r.ug_min_diploma_cgpa) || 2.5,
        pg_min_cgpa: parseFloat(r.pg_min_cgpa)
      }));
      
      await axios.post(`${API}/country-rules/bulk`, rulesToSave);
      toast.success("Rules saved successfully!");
      fetchData();
    } catch (error) {
      console.error("Error saving rules:", error);
      toast.error("Error saving rules");
    }
  };

  const deleteLead = async (leadId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success("Lead deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Error deleting lead");
    }
  };

  const bulkDeleteLeads = async () => {
    if (selectedLeads.length === 0) return;
    try {
      await axios.post(`${API}/leads/bulk-delete`, selectedLeads);
      toast.success(`${selectedLeads.length} lead(s) deleted`);
      setSelectedLeads([]);
      fetchData();
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast.error("Error deleting leads");
    }
  };

  const exportCSV = () => {
    window.open(`${API}/leads/export/csv`, '_blank');
  };

  const updateSettings = async (field, value) => {
    try {
      await axios.put(`${API}/settings`, { [field]: value });
      setSettings(prev => ({ ...prev, [field]: value }));
      toast.success("Settings updated");
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Error updating settings");
    }
  };

  const navigate = useNavigate();
  
  // Logout function
  const handleLogout = async () => {
    const token = sessionStorage.getItem("cst_admin_token");
    try {
      if (token) {
        await axios.post(`${API}/admin/logout`, {});
      }
    } catch (e) {
      // best-effort logout; continue anyway
    }
    sessionStorage.removeItem("cst_admin_token");
    sessionStorage.removeItem("cst_admin_data");
    delete axios.defaults.headers.common['Authorization'];
    navigate("/cst");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-800">Study Eligibility & Lead Manager Pro</h1>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-red-500 hover:text-red-600 font-medium">View Frontend →</Link>
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <X className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Check if moderator has no permissions */}
        {currentAdmin?.role === 'moderator' && currentAdmin?.permissions && 
         !Object.values(currentAdmin.permissions).some(v => v === true) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8 text-center">
            <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Permissions Assigned</h2>
            <p className="text-gray-600 mb-4">
              Your account doesn't have any permissions yet. Please contact the Super Admin to assign permissions.
            </p>
            <p className="text-sm text-gray-500">
              Logged in as: <span className="font-medium">{currentAdmin.email}</span> (Supervisor)
            </p>
          </div>
        )}
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 bg-white p-1 rounded-xl shadow-sm flex-wrap">
            {hasPermission('country_rules') && (
              <TabsTrigger value="rules" className="flex items-center gap-2 px-6 py-3 rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-country-rules">
                <Globe className="h-4 w-4" /> Country Rules
              </TabsTrigger>
            )}
            {hasPermission('country_pages') && (
              <TabsTrigger value="pages" className="flex items-center gap-2 px-6 py-3 rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-country-pages">
                <MapPin className="h-4 w-4" /> Country Pages
              </TabsTrigger>
            )}
            {hasPermission('blog_posts') && (
              <TabsTrigger value="blog" className="flex items-center gap-2 px-6 py-3 rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-blog">
                <Newspaper className="h-4 w-4" /> Blog
              </TabsTrigger>
            )}
            {hasPermission('leads') && (
              <TabsTrigger value="leads" className="flex items-center gap-2 px-6 py-3 rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-leads">
                <Users className="h-4 w-4" /> Leads
              </TabsTrigger>
            )}
            {hasPermission('ads') && (
              <TabsTrigger value="ads" className="flex items-center gap-2 px-6 py-3 rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-ads">
                <FileText className="h-4 w-4" /> Ads
              </TabsTrigger>
            )}
            {hasPermission('traffic') && (
              <TabsTrigger value="traffic" className="flex items-center gap-2 px-6 py-3 rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-traffic">
                <BarChart3 className="h-4 w-4" /> Traffic
              </TabsTrigger>
            )}
            {hasPermission('settings') && (
              <TabsTrigger value="settings" className="flex items-center gap-2 px-6 py-3 rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-settings">
                <Settings className="h-4 w-4" /> Settings
              </TabsTrigger>
            )}
            {canManageAdmins() && (
              <TabsTrigger value="admins" className="flex items-center gap-2 px-6 py-3 rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-admins">
                <Users className="h-4 w-4" /> Admins
              </TabsTrigger>
            )}
            {canManageAdmins() && (
              <TabsTrigger value="activity" className="flex items-center gap-2 px-6 py-3 rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-activity">
                <FileText className="h-4 w-4" /> Activity Log
              </TabsTrigger>
            )}
            {(canManageAdmins() || currentAdmin?.permissions?.leads) && (
              <TabsTrigger value="newsletter" className="flex items-center gap-2 px-6 py-3 rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-newsletter">
                <Mail className="h-4 w-4" /> Newsletter
              </TabsTrigger>
            )}
            {(canManageAdmins() || currentAdmin?.permissions?.blog) && (
              <TabsTrigger value="comments" className="flex items-center gap-2 px-6 py-3 rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="tab-comments">
                <MessageSquare className="h-4 w-4" /> Comments
              </TabsTrigger>
            )}
            {hasPermission('blog_posts') && settings.enable_ai_writer === true && (
              <TabsTrigger value="ai-writer" className="flex items-center gap-2 px-6 py-3 rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="tab-ai-writer">
                <Sparkles className="h-4 w-4" /> AI Writer
              </TabsTrigger>
            )}
          </TabsList>

          {/* Country Rules Tab */}
          <TabsContent value="rules">
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
                <CardTitle className="text-left">Country Rules ({rules.length})</CardTitle>
                <div className="flex gap-2">
                  {/* Export All Rules */}
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      const exportData = JSON.stringify(rules, null, 2);
                      const blob = new Blob([exportData], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'country_rules_export.json';
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success(`${rules.length} rules exported!`);
                    }}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <Download className="h-4 w-4 mr-2" /> Export All
                  </Button>
                  {/* Import Rules */}
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".json"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const text = await file.text();
                          const importRules = JSON.parse(text);
                          if (!Array.isArray(importRules)) {
                            toast.error("Invalid format! JSON array expected");
                            return;
                          }
                          
                          let imported = 0;
                          for (const rule of importRules) {
                            try {
                              await axios.post(`${API}/country-rules`, rule);
                              imported++;
                            } catch (err) {
                              if (rule.id) {
                                try {
                                  await axios.put(`${API}/country-rules/${rule.id}`, rule);
                                  imported++;
                                } catch (updateErr) {
                                  console.error("Failed to import rule:", rule.country_name);
                                }
                              }
                            }
                          }
                          toast.success(`${imported} rules imported successfully!`);
                          fetchRules();
                        } catch (err) {
                          console.error("Import error:", err);
                          toast.error("Import failed! Invalid JSON file");
                        }
                        e.target.value = '';
                      }}
                    />
                    <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" asChild>
                      <span><Plus className="h-4 w-4 mr-2" /> Import</span>
                    </Button>
                  </label>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">Country</TableHead>
                        <TableHead className="font-semibold">Gap (UG/PG)</TableHead>
                        <TableHead className="font-semibold">IELTS Req</TableHead>
                        <TableHead className="font-semibold">IELTS Min</TableHead>
                        <TableHead className="font-semibold">PTE</TableHead>
                        <TableHead className="font-semibold">PTE Min</TableHead>
                        <TableHead className="font-semibold">OIETC</TableHead>
                        <TableHead className="font-semibold">MOI</TableHead>
                        <TableHead className="font-semibold">No Test</TableHead>
                        <TableHead className="font-semibold">GPA/CGPA</TableHead>
                        <TableHead className="font-semibold">Streams</TableHead>
                        <TableHead className="font-semibold">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rules.map((rule, idx) => (
                        <TableRow key={rule.id || idx} className="hover:bg-gray-50">
                          <TableCell>
                            <Input
                              value={rule.country_name}
                              onChange={(e) => updateRule(idx, "country_name", e.target.value)}
                              className="w-24 h-9"
                              data-testid={`rule-country-${idx}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Input
                                type="number"
                                value={rule.ug_gap_limit}
                                onChange={(e) => updateRule(idx, "ug_gap_limit", e.target.value)}
                                className="w-16 h-9"
                                title="UG Gap"
                              />
                              <Input
                                type="number"
                                value={rule.pg_gap_limit}
                                onChange={(e) => updateRule(idx, "pg_gap_limit", e.target.value)}
                                className="w-16 h-9"
                                title="PG Gap"
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Select 
                                value={rule.ielts_required_ug ? "Y" : "N"}
                                onValueChange={(v) => updateRule(idx, "ielts_required_ug", v === "Y")}
                                disabled={rule.no_test_required_ug}
                              >
                                <SelectTrigger className={`w-14 h-9 ${rule.no_test_required_ug ? 'opacity-50' : ''}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Y">UG</SelectItem>
                                  <SelectItem value="N">-</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select 
                                value={rule.ielts_required_pg ? "Y" : "N"}
                                onValueChange={(v) => updateRule(idx, "ielts_required_pg", v === "Y")}
                                disabled={rule.no_test_required_pg}
                              >
                                <SelectTrigger className={`w-14 h-9 ${rule.no_test_required_pg ? 'opacity-50' : ''}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Y">PG</SelectItem>
                                  <SelectItem value="N">-</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Input
                                type="number"
                                step="0.5"
                                value={rule.ug_min_ielts_band}
                                onChange={(e) => updateRule(idx, "ug_min_ielts_band", e.target.value)}
                                className={`w-16 h-9 ${rule.no_test_required_ug ? 'opacity-50' : ''}`}
                                title="UG IELTS"
                                disabled={rule.no_test_required_ug}
                              />
                              <Input
                                type="number"
                                step="0.5"
                                value={rule.pg_min_ielts_band}
                                onChange={(e) => updateRule(idx, "pg_min_ielts_band", e.target.value)}
                                className={`w-16 h-9 ${rule.no_test_required_pg ? 'opacity-50' : ''}`}
                                title="PG IELTS"
                                disabled={rule.no_test_required_pg}
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Select 
                                value={rule.pte_accepted_ug !== false ? "Y" : "N"}
                                onValueChange={(v) => updateRule(idx, "pte_accepted_ug", v === "Y")}
                                disabled={rule.no_test_required_ug}
                              >
                                <SelectTrigger className={`w-14 h-9 ${rule.no_test_required_ug ? 'opacity-50' : ''}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Y">UG</SelectItem>
                                  <SelectItem value="N">-</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select 
                                value={rule.pte_accepted_pg !== false ? "Y" : "N"}
                                onValueChange={(v) => updateRule(idx, "pte_accepted_pg", v === "Y")}
                                disabled={rule.no_test_required_pg}
                              >
                                <SelectTrigger className={`w-14 h-9 ${rule.no_test_required_pg ? 'opacity-50' : ''}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Y">PG</SelectItem>
                                  <SelectItem value="N">-</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Input
                                type="number"
                                value={rule.ug_min_pte_score || 50}
                                onChange={(e) => updateRule(idx, "ug_min_pte_score", e.target.value)}
                                className={`w-16 h-9 ${rule.no_test_required_ug ? 'opacity-50' : ''}`}
                                title="UG PTE"
                                min="10"
                                max="90"
                                disabled={rule.no_test_required_ug}
                              />
                              <Input
                                type="number"
                                value={rule.pg_min_pte_score || 58}
                                onChange={(e) => updateRule(idx, "pg_min_pte_score", e.target.value)}
                                className={`w-16 h-9 ${rule.no_test_required_pg ? 'opacity-50' : ''}`}
                                title="PG PTE"
                                min="10"
                                max="90"
                                disabled={rule.no_test_required_pg}
                              />
                            </div>
                          </TableCell>
                          {/* OIETC Column */}
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <div className="flex gap-1">
                                <Select 
                                  value={rule.oietc_accepted_ug ? "Y" : "N"}
                                  onValueChange={(v) => updateRule(idx, "oietc_accepted_ug", v === "Y")}
                                  disabled={rule.no_test_required_ug}
                                >
                                  <SelectTrigger className={`w-12 h-8 text-xs ${rule.no_test_required_ug ? 'opacity-50' : ''}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Y">UG</SelectItem>
                                    <SelectItem value="N">-</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Select 
                                  value={rule.oietc_accepted_pg ? "Y" : "N"}
                                  onValueChange={(v) => updateRule(idx, "oietc_accepted_pg", v === "Y")}
                                  disabled={rule.no_test_required_pg}
                                >
                                  <SelectTrigger className={`w-12 h-8 text-xs ${rule.no_test_required_pg ? 'opacity-50' : ''}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Y">PG</SelectItem>
                                    <SelectItem value="N">-</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {(rule.oietc_accepted_ug || rule.oietc_accepted_pg) && (
                                <div className="flex gap-1">
                                  <Input
                                    type="number"
                                    value={rule.ug_min_oietc_score || 6}
                                    onChange={(e) => updateRule(idx, "ug_min_oietc_score", e.target.value)}
                                    className={`w-16 h-8 text-xs ${rule.no_test_required_ug ? 'opacity-50' : ''}`}
                                    placeholder="UG"
                                    title="UG Min Band (0-12)"
                                    min="0"
                                    max="12"
                                    step="0.5"
                                    disabled={rule.no_test_required_ug}
                                  />
                                  <Input
                                    type="number"
                                    value={rule.pg_min_oietc_score || 6}
                                    onChange={(e) => updateRule(idx, "pg_min_oietc_score", e.target.value)}
                                    className={`w-16 h-8 text-xs ${rule.no_test_required_pg ? 'opacity-50' : ''}`}
                                    placeholder="PG"
                                    title="PG Min Band (0-12)"
                                    min="0"
                                    max="12"
                                    step="0.5"
                                    disabled={rule.no_test_required_pg}
                                  />
                                </div>
                              )}
                            </div>
                          </TableCell>
                          {/* MOI Column */}
                          <TableCell>
                            <div className="flex gap-1">
                              <Select 
                                value={rule.moi_accepted_ug ? "Y" : "N"}
                                onValueChange={(v) => updateRule(idx, "moi_accepted_ug", v === "Y")}
                                disabled={rule.no_test_required_ug}
                              >
                                <SelectTrigger className={`w-12 h-8 text-xs ${rule.no_test_required_ug ? 'opacity-50' : ''}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Y">UG</SelectItem>
                                  <SelectItem value="N">-</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select 
                                value={rule.moi_accepted_pg ? "Y" : "N"}
                                onValueChange={(v) => updateRule(idx, "moi_accepted_pg", v === "Y")}
                                disabled={rule.no_test_required_pg}
                              >
                                <SelectTrigger className={`w-12 h-8 text-xs ${rule.no_test_required_pg ? 'opacity-50' : ''}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Y">PG</SelectItem>
                                  <SelectItem value="N">-</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </TableCell>
                          {/* No Test Column */}
                          <TableCell>
                            <div className="flex gap-1">
                              <Select 
                                value={rule.no_test_required_ug ? "Y" : "N"}
                                onValueChange={(v) => updateRule(idx, "no_test_required_ug", v === "Y")}
                              >
                                <SelectTrigger className="w-12 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Y">UG</SelectItem>
                                  <SelectItem value="N">-</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select 
                                value={rule.no_test_required_pg ? "Y" : "N"}
                                onValueChange={(v) => updateRule(idx, "no_test_required_pg", v === "Y")}
                              >
                                <SelectTrigger className="w-12 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Y">PG</SelectItem>
                                  <SelectItem value="N">-</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </TableCell>
                          {/* GPA/CGPA Column - with Diploma CGPA */}
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <div className="flex gap-1">
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={rule.ug_min_gpa}
                                  onChange={(e) => updateRule(idx, "ug_min_gpa", e.target.value)}
                                  className="w-16 h-8 text-xs"
                                  title="UG GPA (HSC)"
                                  placeholder="GPA"
                                />
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={rule.pg_min_cgpa}
                                  onChange={(e) => updateRule(idx, "pg_min_cgpa", e.target.value)}
                                  className="w-16 h-8 text-xs"
                                  title="PG CGPA"
                                  placeholder="CGPA"
                                />
                              </div>
                              <Input
                                type="number"
                                step="0.1"
                                value={rule.ug_min_diploma_cgpa || 2.5}
                                onChange={(e) => updateRule(idx, "ug_min_diploma_cgpa", e.target.value)}
                                className="w-full h-8 text-xs"
                                title="Diploma CGPA"
                                placeholder="Dip CGPA"
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="relative">
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button variant="outline" className="w-28 h-9 justify-between text-left font-normal">
                                    <span className="truncate">
                                      {rule.allowed_subject_groups?.includes("All") 
                                        ? "All" 
                                        : rule.allowed_subject_groups?.length > 0 
                                          ? `${rule.allowed_subject_groups.length} selected`
                                          : "Select..."}
                                    </span>
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-48 p-2">
                                  <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                      <Checkbox 
                                        id={`all-${idx}`}
                                        checked={rule.allowed_subject_groups?.includes("All")}
                                        onCheckedChange={(checked) => {
                                          if (checked) {
                                            updateRule(idx, "allowed_subject_groups", ["All"]);
                                          } else {
                                            updateRule(idx, "allowed_subject_groups", []);
                                          }
                                        }}
                                      />
                                      <label htmlFor={`all-${idx}`} className="text-sm font-medium">All</label>
                                    </div>
                                    <div className="border-t pt-2">
                                      {["Science", "Commerce", "Arts", "Others"].map((group) => (
                                        <div key={group} className="flex items-center space-x-2 py-1">
                                          <Checkbox 
                                            id={`${group}-${idx}`}
                                            checked={rule.allowed_subject_groups?.includes("All") || rule.allowed_subject_groups?.includes(group)}
                                            disabled={rule.allowed_subject_groups?.includes("All")}
                                            onCheckedChange={(checked) => {
                                              const current = rule.allowed_subject_groups?.filter(g => g !== "All") || [];
                                              if (checked) {
                                                updateRule(idx, "allowed_subject_groups", [...current, group]);
                                              } else {
                                                updateRule(idx, "allowed_subject_groups", current.filter(g => g !== group));
                                              }
                                            }}
                                          />
                                          <label htmlFor={`${group}-${idx}`} className="text-sm">{group}</label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteRule(idx)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              data-testid={`delete-rule-${idx}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button onClick={addNewRule} variant="outline" className="rounded-xl" data-testid="btn-add-rule">
                    <Plus className="h-4 w-4 mr-2" /> Add Rule
                  </Button>
                  <Button onClick={saveRules} className="bg-red-500 hover:bg-red-600 rounded-xl" data-testid="btn-save-rules">
                    Save Rules
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
                <CardTitle className="text-left">Blog Posts ({blogPosts.length})</CardTitle>
                <div className="flex gap-2">
                  {/* Export All Blogs */}
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      const exportData = JSON.stringify(blogPosts, null, 2);
                      const blob = new Blob([exportData], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'blog_posts_export.json';
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success(`${blogPosts.length} blogs exported!`);
                    }}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <Download className="h-4 w-4 mr-2" /> Export All
                  </Button>
                  {/* Import Blogs */}
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".json"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const text = await file.text();
                          console.log("Raw file content (first 500 chars):", text.substring(0, 500));
                          
                          let blogs;
                          try {
                            blogs = JSON.parse(text);
                          } catch (parseErr) {
                            console.error("JSON Parse Error:", parseErr);
                            toast.error(`JSON Parse Error: ${parseErr.message}`);
                            e.target.value = '';
                            return;
                          }
                          
                          if (!Array.isArray(blogs)) {
                            toast.error("Invalid format! JSON array expected. Got: " + typeof blogs);
                            e.target.value = '';
                            return;
                          }
                          
                          if (blogs.length === 0) {
                            toast.error("Empty array! No blogs to import.");
                            e.target.value = '';
                            return;
                          }
                          
                          // Check which will be new vs update
                          const existingSlugs = blogPosts.map(b => b.slug);
                          const toCreate = blogs.filter(b => !existingSlugs.includes(b.slug));
                          const toUpdate = blogs.filter(b => existingSlugs.includes(b.slug));
                          
                          const confirmMsg = `Import Summary:\n\n` +
                            `🆕 New Blogs (${toCreate.length}): ${toCreate.map(b => b.title?.substring(0,30) || b.slug).join(', ') || 'None'}\n\n` +
                            `🔄 Will Replace (${toUpdate.length}): ${toUpdate.map(b => b.title?.substring(0,30) || b.slug).join(', ') || 'None'}\n\n` +
                            `Continue?`;
                          
                          if (!window.confirm(confirmMsg)) {
                            e.target.value = '';
                            return;
                          }
                          
                          let created = 0, updated = 0, errors = [];
                          for (const blog of blogs) {
                            const exists = existingSlugs.includes(blog.slug);
                            try {
                              if (exists) {
                                await axios.put(`${API}/blog-posts/${blog.slug}`, blog);
                                updated++;
                              } else {
                                await axios.post(`${API}/blog-posts`, blog);
                                created++;
                              }
                            } catch (err) {
                              console.error("Failed to import:", blog.slug, err);
                              errors.push(`${blog.slug}: ${err.response?.data?.detail || err.message}`);
                            }
                          }
                          
                          if (errors.length > 0) {
                            toast.error(`⚠️ ${errors.length} errors: ${errors[0]}`);
                          }
                          if (created > 0 || updated > 0) {
                            toast.success(`✅ ${created} created, ${updated} replaced!`);
                          }
                          fetchBlogPosts();
                        } catch (err) {
                          console.error("Import error:", err);
                          toast.error(`Import failed: ${err.message}`);
                        }
                        e.target.value = '';
                      }}
                    />
                    <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" asChild>
                      <span><Plus className="h-4 w-4 mr-2" /> Import</span>
                    </Button>
                  </label>
                  <Button onClick={() => openBlogModal()} className="bg-red-500 hover:bg-red-600 rounded-xl">
                    <Plus className="h-4 w-4 mr-2" /> New Post
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {blogPosts.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Newspaper className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">No blog posts yet</p>
                    <p className="text-sm">Create your first blog post to get started</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {blogPosts.map((post) => (
                      <div key={post.id || post.slug} className="bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                        {/* Feature Image */}
                        <div className="h-40 bg-gray-100 relative">
                          {post.feature_image ? (
                            <img src={post.feature_image} alt={post.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <Newspaper className="h-12 w-12" />
                            </div>
                          )}
                          {/* Status Badge */}
                          <span className={`absolute top-2 right-2 px-2 py-1 text-xs font-medium rounded-full ${post.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {post.is_published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                        
                        {/* Content */}
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2 bangla-text">{post.title}</h3>
                          <p className="text-sm text-gray-500 mb-3 line-clamp-2 bangla-text">{post.excerpt || "No excerpt available"}</p>
                          
                          <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                            <span>{post.author}</span>
                            <span>{post.views || 0} views</span>
                          </div>
                          
                          <div className="text-xs text-gray-400 mb-3">
                            <span className="font-medium">URL:</span> /news/{post.slug}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => openBlogModal(post)} className="flex-1">
                              <Edit className="h-3 w-3 mr-1" /> Edit
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => {
                                const exportData = JSON.stringify([post], null, 2);
                                const blob = new Blob([exportData], { type: 'application/json' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `blog_${post.slug}.json`;
                                a.click();
                                URL.revokeObjectURL(url);
                                toast.success(`Blog exported!`);
                              }}
                              className="text-blue-600 border-blue-300"
                              title="Export this blog"
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => toggleBlogPublish(post)} className={post.is_published ? "text-yellow-600" : "text-green-600"}>
                              {post.is_published ? <Eye className="h-3 w-3" /> : <CheckCircle className="h-3 w-3" />}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => deleteBlogPost(post.slug)} className="text-red-600 hover:text-red-700">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Blog Post Modal */}
            <Dialog open={showBlogModal} onOpenChange={setShowBlogModal}>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl">
                <DialogHeader>
                  <DialogTitle>{editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 py-4">
                  {/* Title */}
                  <div className="space-y-2">
                    <Label className="font-medium">Title *</Label>
                    <Input
                      value={blogForm.title}
                      onChange={(e) => {
                        setBlogForm(prev => ({ 
                          ...prev, 
                          title: e.target.value,
                          slug: editingBlog ? prev.slug : generateSlug(e.target.value)
                        }));
                      }}
                      placeholder="Enter blog post title"
                      className="h-11"
                    />
                  </div>
                  
                  {/* URL Slug */}
                  <div className="space-y-2">
                    <Label className="font-medium">URL Slug *</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">/news/</span>
                      <Input
                        value={blogForm.slug}
                        onChange={(e) => setBlogForm(prev => ({ ...prev, slug: generateSlug(e.target.value) }))}
                        placeholder="custom-url-slug"
                        className="flex-1 h-11"
                      />
                    </div>
                    <p className="text-xs text-gray-400">This will be the URL of your blog post</p>
                  </div>
                  
                  {/* Feature Image */}
                  <div className="space-y-2">
                    <Label className="font-medium">Feature Image</Label>
                    <div className="flex items-start gap-4">
                      {blogForm.feature_image && (
                        <img src={blogForm.feature_image} alt="Feature" className="w-32 h-20 object-cover rounded-lg border" />
                      )}
                      <div className="flex-1 space-y-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleBlogImageUpload}
                          disabled={uploadingBlogImage}
                        />
                        <p className="text-xs text-gray-400">Or enter image URL directly:</p>
                        <Input
                          value={blogForm.feature_image}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, feature_image: e.target.value }))}
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Video URL */}
                  <div className="space-y-2">
                    <Label className="font-medium">Video URL (YouTube)</Label>
                    <Input
                      value={blogForm.video_url || ""}
                      onChange={(e) => setBlogForm(prev => ({ ...prev, video_url: e.target.value }))}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                    <p className="text-xs text-gray-400">Add a YouTube video to embed in the blog post</p>
                  </div>
                  
                  {/* Excerpt */}
                  <div className="space-y-2">
                    <Label className="font-medium">Excerpt (Short Description)</Label>
                    <Textarea
                      value={blogForm.excerpt}
                      onChange={(e) => setBlogForm(prev => ({ ...prev, excerpt: e.target.value }))}
                      placeholder="Brief description of the blog post..."
                      rows={2}
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="space-y-2">
                    <Label className="font-medium">Content</Label>
                    <Textarea
                      value={blogForm.content}
                      onChange={(e) => setBlogForm(prev => ({ ...prev, content: e.target.value }))}
                      placeholder="Write your blog post content here..."
                      rows={10}
                      className="font-mono text-sm"
                    />
                  </div>
                  
                  {/* Author & Category */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="font-medium">Author</Label>
                      <Input
                        value={blogForm.author}
                        onChange={(e) => setBlogForm(prev => ({ ...prev, author: e.target.value }))}
                        placeholder="Author name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium">Category</Label>
                      <Input
                        value={blogForm.category}
                        onChange={(e) => setBlogForm(prev => ({ ...prev, category: e.target.value }))}
                        placeholder="e.g., Study Abroad, Tips"
                      />
                    </div>
                  </div>
                  
                  {/* Publish Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div>
                      <Label className="font-medium">Publish Status</Label>
                      <p className="text-sm text-gray-500">{blogForm.is_published ? 'Post is visible to visitors' : 'Post is saved as draft'}</p>
                    </div>
                    <Switch
                      checked={blogForm.is_published}
                      onCheckedChange={(checked) => setBlogForm(prev => ({ ...prev, is_published: checked }))}
                    />
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowBlogModal(false)}>Cancel</Button>
                  <Button onClick={saveBlogPost} className="bg-red-500 hover:bg-red-600">
                    {editingBlog ? 'Update Post' : 'Create Post'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
                <CardTitle className="text-left">Leads ({leads.length})</CardTitle>
                <div className="flex gap-2">
                  {selectedLeads.length > 0 && (
                    <Button variant="destructive" onClick={bulkDeleteLeads} className="rounded-xl" data-testid="btn-bulk-delete">
                      <Trash2 className="h-4 w-4 mr-2" /> Delete Selected ({selectedLeads.length})
                    </Button>
                  )}
                  <Button onClick={exportCSV} variant="outline" className="rounded-xl" data-testid="btn-export-csv">
                    <Download className="h-4 w-4 mr-2" /> Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="w-10">
                          <Checkbox
                            checked={selectedLeads.length === leads.length && leads.length > 0}
                            onCheckedChange={(checked) => {
                              setSelectedLeads(checked ? leads.map(l => l.id) : []);
                            }}
                          />
                        </TableHead>
                        <TableHead className="font-semibold">Name</TableHead>
                        <TableHead className="font-semibold">Contact</TableHead>
                        <TableHead className="font-semibold">Academic</TableHead>
                        <TableHead className="font-semibold">Source</TableHead>
                        <TableHead className="font-semibold">Message</TableHead>
                        <TableHead className="font-semibold">Date</TableHead>
                        <TableHead className="font-semibold">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.map((lead) => (
                        <TableRow key={lead.id} className="hover:bg-gray-50">
                          <TableCell>
                            <Checkbox
                              checked={selectedLeads.includes(lead.id)}
                              onCheckedChange={(checked) => {
                                setSelectedLeads(prev => 
                                  checked 
                                    ? [...prev, lead.id]
                                    : prev.filter(id => id !== lead.id)
                                );
                              }}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{lead.full_name}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{lead.email}</div>
                              <div className="text-gray-500">{lead.phone}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div>{lead.academic_level} • {lead.subject_group}</div>
                              <div className="text-gray-500">{lead.applying_for}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${lead.source === 'contact' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                              {lead.source}
                            </span>
                            {lead.voice_message_url && (
                              <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                🎤 Voice
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm max-w-[150px] truncate">
                              {lead.message || "-"}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedLead(lead);
                                  setShowLeadModal(true);
                                }}
                                className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                              >
                                <Users className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteLead(lead.id)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                data-testid={`delete-lead-${lead.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => addLeadToNewsletter(lead)}
                                title="Add to Newsletter"
                                className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                              >
                                <Mail className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Lead Detail Modal */}
            <Dialog open={showLeadModal} onOpenChange={setShowLeadModal}>
              <DialogContent className="sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold text-left flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-500" />
                    Lead Details
                  </DialogTitle>
                </DialogHeader>
                
                {selectedLead && (
                  <div className="space-y-6 py-4">
                    {/* Personal Info */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        Personal Information
                      </h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Name:</span>
                          <span className="ml-2 font-medium">{selectedLead.full_name}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Phone:</span>
                          <span className="ml-2 font-medium">{selectedLead.phone}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-500">Email:</span>
                          <span className="ml-2 font-medium">{selectedLead.email}</span>
                        </div>
                      </div>
                    </div>

                    {/* Academic Info */}
                    <div className="bg-blue-50 rounded-xl p-4">
                      <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Academic Information
                      </h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Academic Level:</span>
                          <span className="ml-2 font-medium">{selectedLead.academic_level}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Subject Group:</span>
                          <span className="ml-2 font-medium">{selectedLead.subject_group}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Applying For:</span>
                          <span className="ml-2 font-medium">{selectedLead.applying_for}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Year of Passing:</span>
                          <span className="ml-2 font-medium">{selectedLead.year_of_passing}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">GPA/CGPA:</span>
                          <span className="ml-2 font-medium">{selectedLead.gpa_cgpa}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Study Gap:</span>
                          <span className="ml-2 font-medium">{selectedLead.calculated_gap} years</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-500">English Test:</span>
                          <span className="ml-2 font-medium">
                            {selectedLead.english_test_type === 'ielts' 
                              ? `IELTS (Band: ${selectedLead.ielts_band})`
                              : selectedLead.english_test_type === 'pte'
                              ? `PTE (Score: ${selectedLead.pte_score})`
                              : 'No Test'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Eligibility */}
                    <div className="bg-green-50 rounded-xl p-4">
                      <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Eligibility Results
                      </h3>
                      <div className="text-sm">
                        <div className="mb-2">
                          <span className="text-gray-500">Eligible Countries:</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedLead.eligible_countries?.length > 0 
                            ? selectedLead.eligible_countries.map((country, idx) => (
                                <span key={idx} className="px-3 py-1 bg-green-500 text-white rounded-full text-xs font-medium">
                                  {country}
                                </span>
                              ))
                            : <span className="text-gray-500">No eligible countries</span>
                          }
                        </div>
                      </div>
                    </div>

                    {/* Message */}
                    {selectedLead.message && (
                      <div className="bg-yellow-50 rounded-xl p-4">
                        <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                          Text Message
                        </h3>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedLead.message}</p>
                      </div>
                    )}

                    {/* Voice Message */}
                    {selectedLead.voice_message_url && (
                      <div className="bg-purple-50 rounded-xl p-4">
                        <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                          🎤 Voice Message
                        </h3>
                        <audio 
                          controls 
                          className="w-full"
                          src={`${BACKEND_URL}${selectedLead.voice_message_url}`}
                        >
                          Your browser does not support audio playback.
                        </audio>
                      </div>
                    )}

                    {/* Meta Info */}
                    <div className="flex justify-between items-center text-sm text-gray-500 pt-4 border-t">
                      <div>
                        Source: <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedLead.source === 'contact' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                          {selectedLead.source}
                        </span>
                      </div>
                      <div>
                        Created: {selectedLead.created_at ? new Date(selectedLead.created_at).toLocaleString() : "-"}
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Country Pages Tab */}
          <TabsContent value="pages">
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
                <CardTitle className="text-left">Country Pages ({countryPages.length})</CardTitle>
                <div className="flex gap-2">
                  {/* Export All Countries */}
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      const exportData = JSON.stringify(countryPages, null, 2);
                      const blob = new Blob([exportData], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'country_pages_export.json';
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success(`${countryPages.length} countries exported!`);
                    }}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <Download className="h-4 w-4 mr-2" /> Export All
                  </Button>
                  {/* Import Countries */}
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".json"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const text = await file.text();
                          const countries = JSON.parse(text);
                          if (!Array.isArray(countries)) {
                            toast.error("Invalid format! JSON array expected");
                            return;
                          }
                          
                          // Check which will be new vs update
                          const existingSlugs = countryPages.map(c => c.slug);
                          const toCreate = countries.filter(c => !existingSlugs.includes(c.slug));
                          const toUpdate = countries.filter(c => existingSlugs.includes(c.slug));
                          
                          const confirmMsg = `Import Summary:\n\n` +
                            `🆕 New Countries (${toCreate.length}): ${toCreate.map(c => c.country_name || c.slug).join(', ') || 'None'}\n\n` +
                            `🔄 Will Replace (${toUpdate.length}): ${toUpdate.map(c => c.country_name || c.slug).join(', ') || 'None'}\n\n` +
                            `Continue?`;
                          
                          if (!window.confirm(confirmMsg)) {
                            e.target.value = '';
                            return;
                          }
                          
                          let created = 0, updated = 0;
                          for (const country of countries) {
                            const exists = existingSlugs.includes(country.slug);
                            try {
                              if (exists) {
                                await axios.put(`${API}/country-pages/${country.slug}`, country);
                                updated++;
                              } else {
                                await axios.post(`${API}/country-pages`, country);
                                created++;
                              }
                            } catch (err) {
                              console.error("Failed to import:", country.slug, err);
                            }
                          }
                          toast.success(`✅ ${created} created, ${updated} replaced!`);
                          fetchData();
                        } catch (err) {
                          console.error("Import error:", err);
                          toast.error("Import failed! Invalid JSON file");
                        }
                        e.target.value = '';
                      }}
                    />
                    <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" asChild>
                      <span><Plus className="h-4 w-4 mr-2" /> Import</span>
                    </Button>
                  </label>
                  <Button 
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/country-pages/init`);
                        toast.success(`Initialized ${res.data.created.length} country pages`);
                        fetchData();
                      } catch (error) {
                        console.error("Error initializing pages:", error);
                        toast.error("Failed to initialize country pages");
                      }
                    }}
                    variant="outline"
                    className="rounded-xl"
                  >
                    <Plus className="h-4 w-4 mr-2" /> Initialize from Rules
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {countryPages.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium">No country pages yet</p>
                    <p className="text-sm">Click &quot;Initialize from Rules&quot; to create pages for all countries</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {countryPages.map((page) => (
                      <div 
                        key={page.id}
                        className="border rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                      >
                        <div 
                          className="h-32 bg-cover bg-center relative"
                          style={{ backgroundImage: `url(${page.hero_image || 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=400'})` }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                          <div className="absolute bottom-3 left-4 text-white">
                            <h3 className="font-bold text-lg">{page.country_name}</h3>
                          </div>
                          {page.is_active ? (
                            <span className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">Active</span>
                          ) : (
                            <span className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded-full">Inactive</span>
                          )}
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                            <FileText className="h-4 w-4" />
                            {page.document_checklist?.length || 0} documents
                            {page.video_url && (
                              <>
                                <span className="mx-1">•</span>
                                <Play className="h-4 w-4" />
                                Video
                              </>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedCountryPage(page);
                                setShowCountryPageModal(true);
                              }}
                              className="flex-1 bg-blue-500 hover:bg-blue-600 rounded-lg"
                            >
                              <Edit className="h-4 w-4 mr-1" /> Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const exportData = JSON.stringify([page], null, 2);
                                const blob = new Blob([exportData], { type: 'application/json' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `country_${page.slug}.json`;
                                a.click();
                                URL.revokeObjectURL(url);
                                toast.success(`"${page.country_name}" exported!`);
                              }}
                              className="rounded-lg border-blue-300 text-blue-600"
                              title="Export this country"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`/country/${page.slug}`, '_blank')}
                              className="rounded-lg"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Country Page Edit Modal */}
            <Dialog open={showCountryPageModal} onOpenChange={setShowCountryPageModal}>
              <DialogContent className="sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold text-left flex items-center justify-between">
                    <span>Edit: {selectedCountryPage?.country_name}</span>
                  </DialogTitle>
                </DialogHeader>
                
                {selectedCountryPage && (
                  <div className="space-y-6 py-4">
                    {/* Language Toggle */}
                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100">
                      <span className="font-medium text-gray-700">Content Language:</span>
                      <div className="flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm">
                        <button
                          onClick={() => setSelectedCountryPage(prev => ({ ...prev, _editLang: 'en' }))}
                          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                            (selectedCountryPage._editLang || 'en') === 'en' 
                              ? 'bg-blue-500 text-white shadow-md' 
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          🇬🇧 English
                        </button>
                        <button
                          onClick={() => setSelectedCountryPage(prev => ({ ...prev, _editLang: 'bn' }))}
                          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                            selectedCountryPage._editLang === 'bn' 
                              ? 'bg-green-500 text-white shadow-md' 
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          🇧🇩 বাংলা
                        </button>
                      </div>
                    </div>
                    
                    {/* English Content */}
                    {(selectedCountryPage._editLang || 'en') === 'en' && (
                      <>
                        {/* Flag Image URL */}
                        <div className="space-y-2">
                          <Label className="font-medium">🏳️ Flag Image URL (Optional)</Label>
                          <Input
                            value={selectedCountryPage.flag_image || ""}
                            onChange={(e) => setSelectedCountryPage(prev => ({ ...prev, flag_image: e.target.value }))}
                            placeholder="https://flagcdn.com/w80/gb.png (Leave empty for auto-detect)"
                          />
                          <p className="text-xs text-gray-500">Custom flag image URL. If empty, system will use default flag based on country name.</p>
                        </div>
                        
                        {/* Hero Image */}
                        <div className="space-y-2">
                          <Label className="font-medium">Hero Image URL</Label>
                          <Input
                            value={selectedCountryPage.hero_image || ""}
                            onChange={(e) => setSelectedCountryPage(prev => ({ ...prev, hero_image: e.target.value }))}
                            placeholder="https://images.unsplash.com/..."
                          />
                        </div>
                        
                        {/* Hero Title */}
                        <div className="space-y-2">
                          <Label className="font-medium">Hero Title</Label>
                          <Input
                            value={selectedCountryPage.hero_title || ""}
                            onChange={(e) => setSelectedCountryPage(prev => ({ ...prev, hero_title: e.target.value }))}
                            placeholder={`Study in ${selectedCountryPage.country_name}`}
                          />
                        </div>
                        
                        {/* Description */}
                        <div className="space-y-2">
                          <Label className="font-medium">Description</Label>
                          <Textarea
                            value={selectedCountryPage.description || ""}
                            onChange={(e) => setSelectedCountryPage(prev => ({ ...prev, description: e.target.value }))}
                            rows={3}
                            placeholder="Brief description about studying in this country..."
                          />
                        </div>
                      </>
                    )}
                    
                    {/* Bangla Content */}
                    {selectedCountryPage._editLang === 'bn' && (
                      <>
                        {/* Country Name Bangla */}
                        <div className="space-y-2">
                          <Label className="font-medium bangla-text">দেশের নাম (বাংলা)</Label>
                          <Input
                            value={selectedCountryPage.country_name_bn || ""}
                            onChange={(e) => setSelectedCountryPage(prev => ({ ...prev, country_name_bn: e.target.value }))}
                            placeholder="যেমন: যুক্তরাজ্য"
                            className="bangla-text"
                          />
                        </div>
                        
                        {/* Hero Title Bangla */}
                        <div className="space-y-2">
                          <Label className="font-medium bangla-text">হিরো শিরোনাম (বাংলা)</Label>
                          <Input
                            value={selectedCountryPage.hero_title_bn || ""}
                            onChange={(e) => setSelectedCountryPage(prev => ({ ...prev, hero_title_bn: e.target.value }))}
                            placeholder="যেমন: যুক্তরাজ্যে পড়াশোনা করুন"
                            className="bangla-text"
                          />
                        </div>
                        
                        {/* Description Bangla */}
                        <div className="space-y-2">
                          <Label className="font-medium bangla-text">বিবরণ (বাংলা)</Label>
                          <Textarea
                            value={selectedCountryPage.description_bn || ""}
                            onChange={(e) => setSelectedCountryPage(prev => ({ ...prev, description_bn: e.target.value }))}
                            rows={4}
                            placeholder="দেশ সম্পর্কে বাংলায় বিবরণ লিখুন..."
                            className="bangla-text"
                          />
                        </div>
                        
                        {/* Video Title Bangla */}
                        <div className="space-y-2">
                          <Label className="font-medium bangla-text">ভিডিও সেকশন শিরোনাম (বাংলা)</Label>
                          <Input
                            value={selectedCountryPage.video_title_bn || ""}
                            onChange={(e) => setSelectedCountryPage(prev => ({ ...prev, video_title_bn: e.target.value }))}
                            placeholder="ভিডিও গাইড দেখুন"
                            className="bangla-text"
                          />
                        </div>
                        
                        {/* Additional Info Bangla */}
                        <div className="space-y-2">
                          <Label className="font-medium bangla-text">অতিরিক্ত তথ্য (বাংলা)</Label>
                          <Textarea
                            value={selectedCountryPage.additional_info_bn || ""}
                            onChange={(e) => setSelectedCountryPage(prev => ({ ...prev, additional_info_bn: e.target.value }))}
                            rows={3}
                            placeholder="অতিরিক্ত তথ্য বাংলায় লিখুন..."
                            className="bangla-text"
                          />
                        </div>
                      </>
                    )}
                    
                    {/* Common Fields - Always Visible */}
                    <div className="border-t pt-4 mt-4">
                      <p className="text-sm font-medium text-gray-500 mb-4">Common Settings (both languages)</p>
                      
                      {/* Video URL */}
                      <div className="space-y-2 mb-4">
                        <Label className="font-medium">Video URL (YouTube)</Label>
                        <Input
                          value={selectedCountryPage.video_url || ""}
                          onChange={(e) => setSelectedCountryPage(prev => ({ ...prev, video_url: e.target.value }))}
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                      </div>
                      
                      {/* Video Title - Only show in English mode */}
                      {(selectedCountryPage._editLang || 'en') === 'en' && (
                        <div className="space-y-2 mb-4">
                          <Label className="font-medium">Video Section Title (English)</Label>
                          <Input
                            value={selectedCountryPage.video_title || ""}
                            onChange={(e) => setSelectedCountryPage(prev => ({ ...prev, video_title: e.target.value }))}
                            placeholder="Watch Video Guide"
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Document Checklist - Only in English mode */}
                    {(selectedCountryPage._editLang || 'en') === 'en' && (
                    <>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="font-medium">Document Checklist</Label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const newItem = { id: `doc-${Date.now()}`, title: "", description: "", required: true };
                            setSelectedCountryPage(prev => ({
                              ...prev,
                              document_checklist: [...(prev.document_checklist || []), newItem]
                            }));
                          }}
                          className="rounded-lg"
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add Document
                        </Button>
                      </div>
                      
                      <div className="space-y-3">
                        {(selectedCountryPage.document_checklist || []).map((doc, index) => (
                          <div key={doc.id || index} className="p-4 border rounded-xl bg-gray-50">
                            <div className="flex items-start gap-3">
                              <div className="flex-1 space-y-3">
                                <Input
                                  value={doc.title || ""}
                                  onChange={(e) => {
                                    const updated = [...selectedCountryPage.document_checklist];
                                    updated[index].title = e.target.value;
                                    setSelectedCountryPage(prev => ({ ...prev, document_checklist: updated }));
                                  }}
                                  placeholder="Document name (e.g., Passport)"
                                />
                                <Input
                                  value={doc.description || ""}
                                  onChange={(e) => {
                                    const updated = [...selectedCountryPage.document_checklist];
                                    updated[index].description = e.target.value;
                                    setSelectedCountryPage(prev => ({ ...prev, document_checklist: updated }));
                                  }}
                                  placeholder="Description (optional)"
                                />
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={doc.required}
                                    onCheckedChange={(checked) => {
                                      const updated = [...selectedCountryPage.document_checklist];
                                      updated[index].required = checked;
                                      setSelectedCountryPage(prev => ({ ...prev, document_checklist: updated }));
                                    }}
                                  />
                                  <span className="text-sm text-gray-600">Required</span>
                                </div>
                              </div>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => {
                                  const updated = selectedCountryPage.document_checklist.filter((_, i) => i !== index);
                                  setSelectedCountryPage(prev => ({ ...prev, document_checklist: updated }));
                                }}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Additional Info */}
                    <div className="space-y-2">
                      <Label className="font-medium">Additional Information</Label>
                      <Textarea
                        value={selectedCountryPage.additional_info || ""}
                        onChange={(e) => setSelectedCountryPage(prev => ({ ...prev, additional_info: e.target.value }))}
                        rows={4}
                        placeholder="Any additional information about studying in this country..."
                      />
                    </div>
                    </>
                    )}
                    
                    {/* Active Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div>
                        <Label className="font-medium">Page Active</Label>
                        <p className="text-sm text-gray-500">Show this page to users</p>
                      </div>
                      <Switch
                        checked={selectedCountryPage.is_active}
                        onCheckedChange={(checked) => setSelectedCountryPage(prev => ({ ...prev, is_active: checked }))}
                      />
                    </div>
                  </div>
                )}
                
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowCountryPageModal(false)}
                    className="rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/country-pages/${selectedCountryPage.slug}`, {
                          flag_image: selectedCountryPage.flag_image,
                          hero_image: selectedCountryPage.hero_image,
                          hero_title: selectedCountryPage.hero_title,
                          hero_title_bn: selectedCountryPage.hero_title_bn,
                          description: selectedCountryPage.description,
                          description_bn: selectedCountryPage.description_bn,
                          video_url: selectedCountryPage.video_url,
                          video_title: selectedCountryPage.video_title,
                          video_title_bn: selectedCountryPage.video_title_bn,
                          document_checklist: selectedCountryPage.document_checklist,
                          additional_info: selectedCountryPage.additional_info,
                          additional_info_bn: selectedCountryPage.additional_info_bn,
                          is_active: selectedCountryPage.is_active,
                          country_name_bn: selectedCountryPage.country_name_bn
                        });
                        toast.success("Country page updated!");
                        setShowCountryPageModal(false);
                        fetchData();
                      } catch (error) {
                        console.error("Error updating page:", error);
                        toast.error("Failed to update country page");
                      }
                    }}
                    className="bg-red-500 hover:bg-red-600 rounded-xl"
                  >
                    Save Changes
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader className="border-b border-gray-100">
                <CardTitle className="text-left">General Settings</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="adminEmail" className="text-left block font-medium">Admin Notification Email</Label>
                    <Input
                      id="adminEmail"
                      value={settings.admin_email || ""}
                      onChange={(e) => setSettings(prev => ({ ...prev, admin_email: e.target.value }))}
                      onBlur={(e) => updateSettings("admin_email", e.target.value)}
                      className="h-11"
                      data-testid="input-admin-email"
                    />
                    <p className="text-sm text-gray-500">Email for new lead notifications</p>
                  </div>
                  
                  <div className="flex items-center justify-between p-5 border rounded-xl bg-gray-50">
                    <div className="text-left">
                      <Label className="font-medium">Client Auto-Reply</Label>
                      <p className="text-sm text-gray-500">Send automatic thank-you email</p>
                    </div>
                    <Switch
                      checked={settings.client_auto_reply_enabled}
                      onCheckedChange={(checked) => updateSettings("client_auto_reply_enabled", checked)}
                      data-testid="switch-auto-reply"
                    />
                  </div>

                  <div className="flex items-center justify-between p-5 border rounded-xl bg-gray-50">
                    <div className="text-left">
                      <Label className="font-medium">Enable Bangla UI</Label>
                      <p className="text-sm text-gray-500">Show Bengali translations on frontend</p>
                    </div>
                    <Switch
                      checked={settings.enable_bangla_ui}
                      onCheckedChange={(checked) => updateSettings("enable_bangla_ui", checked)}
                      data-testid="switch-bangla-ui"
                    />
                  </div>

                  <div className="flex items-center justify-between p-5 border rounded-xl bg-purple-50 border-purple-200">
                    <div className="text-left">
                      <Label className="font-medium text-purple-700">AI Blog Writer</Label>
                      <p className="text-sm text-purple-500">Enable AI-powered blog writing (Claude Sonnet 4.6)</p>
                    </div>
                    <Switch
                      checked={settings.enable_ai_writer === true}
                      onCheckedChange={(checked) => updateSettings("enable_ai_writer", checked)}
                      data-testid="switch-ai-writer"
                    />
                  </div>

                  <div className="flex items-center justify-between p-5 border rounded-xl bg-blue-50 border-blue-200">
                    <div className="text-left">
                      <Label className="font-medium text-blue-700">Country Click Action</Label>
                      <p className="text-sm text-blue-500">
                        {settings.country_click_action === "page" 
                          ? "Opens country page in new view" 
                          : "Shows country details in modal popup"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${settings.country_click_action === "modal" ? "font-medium text-blue-700" : "text-gray-400"}`}>Modal</span>
                      <Switch
                        checked={settings.country_click_action === "page"}
                        onCheckedChange={(checked) => updateSettings("country_click_action", checked ? "page" : "modal")}
                        data-testid="switch-country-click"
                      />
                      <span className={`text-sm ${settings.country_click_action === "page" ? "font-medium text-blue-700" : "text-gray-400"}`}>Page</span>
                    </div>
                  </div>

                  <div className="p-5 border rounded-xl bg-green-50 border-green-200">
                    <div className="text-left mb-3">
                      <Label className="font-medium text-green-700">WhatsApp Link</Label>
                      <p className="text-sm text-green-500">Link for WhatsApp contact button on country pages</p>
                    </div>
                    <Input
                      value={settings.whatsapp_link || ""}
                      onChange={(e) => setSettings(prev => ({ ...prev, whatsapp_link: e.target.value }))}
                      onBlur={(e) => updateSettings("whatsapp_link", e.target.value)}
                      placeholder="https://wa.me/8801XXXXXXXXX"
                      className="border-green-200 focus:border-green-400"
                    />
                  </div>

                  <div className="p-5 border rounded-xl bg-orange-50 border-orange-200">
                    <div className="text-left mb-3">
                      <Label className="font-medium text-orange-700">Phone Number</Label>
                      <p className="text-sm text-orange-500">Phone number for call button on country pages</p>
                    </div>
                    <Input
                      value={settings.phone_number || ""}
                      onChange={(e) => setSettings(prev => ({ ...prev, phone_number: e.target.value }))}
                      onBlur={(e) => updateSettings("phone_number", e.target.value)}
                      placeholder="+8801XXXXXXXXX"
                      className="border-orange-200 focus:border-orange-400"
                    />
                  </div>

                  <div className="flex items-center justify-between p-5 border rounded-xl bg-red-50 border-red-200">
                    <div className="text-left">
                      <Label className="font-medium text-red-700">🔒 Content Protection</Label>
                      <p className="text-sm text-red-500">Block DevTools, Right-click, Copy, Text Selection (Mobile & Desktop)</p>
                    </div>
                    <Switch
                      checked={settings.disable_dev_tools}
                      onCheckedChange={(checked) => updateSettings("disable_dev_tools", checked)}
                      data-testid="switch-disable-dev-tools"
                    />
                  </div>
                  
                  {/* Newsletter Toggle */}
                  <div className="flex items-center justify-between p-5 border rounded-xl bg-green-50 border-green-200">
                    <div className="text-left">
                      <Label className="font-medium text-green-700">📧 Show Newsletter Form</Label>
                      <p className="text-sm text-green-500">Display newsletter subscription form above footer</p>
                    </div>
                    <Switch
                      checked={settings.show_newsletter !== false}
                      onCheckedChange={(checked) => updateSettings("show_newsletter", checked)}
                      data-testid="switch-newsletter"
                    />
                  </div>
                  
                  {/* Location Tracking Toggle */}
                  <div className="flex items-center justify-between p-5 border rounded-xl bg-blue-50 border-blue-200">
                    <div className="text-left">
                      <Label className="font-medium text-blue-700">📍 Location Tracking</Label>
                      <p className="text-sm text-blue-500">Ask visitors for location permission to track where they're visiting from</p>
                    </div>
                    <Switch
                      checked={settings.location_tracking_enabled || false}
                      onCheckedChange={(checked) => updateSettings("location_tracking_enabled", checked)}
                      data-testid="switch-location-tracking"
                    />
                  </div>
                </div>

                {/* Logo & Menu Management Section */}
                <div className="border-t pt-8">
                  <h3 className="text-lg font-semibold mb-6 text-left">🎯 Header Settings</h3>
                  
                  {/* Logo Upload */}
                  <div className="mb-8 p-5 border rounded-xl bg-purple-50 border-purple-200">
                    <div className="text-left mb-4">
                      <Label className="font-medium text-purple-700">Site Logo</Label>
                      <p className="text-sm text-purple-500">Upload your site logo (PNG, JPG, WebP)</p>
                    </div>
                    <div className="flex items-center gap-4">
                      {settings.logo_url && (
                        <img 
                          src={settings.logo_url} 
                          alt="Current Logo" 
                          className="h-12 w-auto object-contain bg-white rounded-lg p-2 border"
                        />
                      )}
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                const res = await axios.post(`${API}/upload-logo`, formData);
                                updateSettings("logo_url", res.data.file_url);
                                toast.success("Logo uploaded successfully!");
                              } catch (error) {
                                toast.error("Failed to upload logo");
                              }
                            }
                          }}
                          className="border-purple-200 focus:border-purple-400"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Social Share Image (OG Image) */}
                  <div className="mb-8 p-5 border rounded-xl bg-indigo-50 border-indigo-200">
                    <div className="text-left mb-4">
                      <Label className="font-medium text-indigo-700">🔗 Social Share Image</Label>
                      <p className="text-sm text-indigo-500">This image will show when site link is shared on Facebook, WhatsApp, etc. (Recommended: 1200x630px)</p>
                    </div>
                    <div className="flex items-center gap-4">
                      {settings.og_image && (
                        <img 
                          src={settings.og_image} 
                          alt="Social Share Preview" 
                          className="h-20 w-36 object-cover bg-white rounded-lg border"
                        />
                      )}
                      <div className="flex-1 space-y-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                const res = await axios.post(`${API}/upload-logo`, formData);
                                updateSettings("og_image", res.data.file_url);
                                toast.success("Social share image uploaded!");
                              } catch (error) {
                                toast.error("Failed to upload image");
                              }
                            }
                          }}
                          className="border-indigo-200 focus:border-indigo-400"
                        />
                        <Input
                          placeholder="Or paste image URL..."
                          value={settings.og_image || ""}
                          onChange={(e) => updateSettings("og_image", e.target.value)}
                          className="border-indigo-200 focus:border-indigo-400 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Blog Hero Image */}
                  <div className="p-5 border rounded-xl bg-pink-50 border-pink-200">
                    <div className="text-left mb-4">
                      <Label className="font-medium text-pink-700">Blog Page Hero Image</Label>
                      <p className="text-sm text-pink-500">Background image for the News & Blog page header</p>
                    </div>
                    <div className="flex items-center gap-4">
                      {settings.blog_hero_image && (
                        <img 
                          src={settings.blog_hero_image} 
                          alt="Blog Hero" 
                          className="w-32 h-20 object-cover rounded-lg border"
                        />
                      )}
                      <div className="flex-1 space-y-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                const res = await axios.post(`${API}/upload-logo`, formData);
                                updateSettings("blog_hero_image", res.data.file_url);
                                toast.success("Blog hero image uploaded!");
                              } catch (error) {
                                toast.error("Failed to upload image");
                              }
                            }
                          }}
                          className="border-pink-200 focus:border-pink-400"
                        />
                        <p className="text-xs text-pink-400">Or enter image URL:</p>
                        <Input
                          value={settings.blog_hero_image || ""}
                          onChange={(e) => setSettings(prev => ({ ...prev, blog_hero_image: e.target.value }))}
                          onBlur={(e) => updateSettings("blog_hero_image", e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          className="border-pink-200 focus:border-pink-400"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Show Ads in Blog Toggle */}
                  <div className="flex items-center justify-between p-5 border rounded-xl bg-amber-50 border-amber-200">
                    <div className="text-left">
                      <Label className="font-medium text-amber-700">Show Ads in Blog Posts</Label>
                      <p className="text-sm text-amber-500">Display advertisements in blog post pages</p>
                    </div>
                    <Switch
                      checked={settings.show_ads_in_blog ?? true}
                      onCheckedChange={(checked) => {
                        setSettings(prev => ({ ...prev, show_ads_in_blog: checked }));
                        updateSettings("show_ads_in_blog", checked);
                      }}
                    />
                  </div>

                  {/* Show Blog Carousel on Homepage Toggle */}
                  <div className="flex items-center justify-between p-5 border rounded-xl bg-purple-50 border-purple-200">
                    <div className="text-left">
                      <Label className="font-medium text-purple-700">Show Blog Carousel on Homepage</Label>
                      <p className="text-sm text-purple-500">Display latest blogs carousel section above footer</p>
                    </div>
                    <Switch
                      checked={settings.show_blog_carousel ?? true}
                      onCheckedChange={(checked) => {
                        setSettings(prev => ({ ...prev, show_blog_carousel: checked }));
                        updateSettings("show_blog_carousel", checked);
                      }}
                    />
                  </div>
                </div>
                
                {/* Contact Page Settings */}
                <div className="border-t pt-8">
                  <h3 className="text-lg font-semibold mb-6 text-left">📞 Contact Page Settings</h3>
                  
                  {/* Contact Hero Image */}
                  <div className="mb-6 p-5 border rounded-xl bg-cyan-50 border-cyan-200">
                    <div className="text-left mb-4">
                      <Label className="font-medium text-cyan-700">Contact Page Hero Image</Label>
                      <p className="text-sm text-cyan-500">Background image for contact page header</p>
                    </div>
                    <div className="flex items-center gap-4">
                      {settings.contact_hero_image && (
                        <img src={settings.contact_hero_image} alt="Contact Hero" className="w-32 h-20 object-cover rounded-lg border" />
                      )}
                      <div className="flex-1 space-y-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('file', file);
                              try {
                                const res = await axios.post(`${API}/upload-logo`, formData);
                                updateSettings("contact_hero_image", res.data.file_url);
                                toast.success("Contact hero image uploaded!");
                              } catch (error) {
                                toast.error("Failed to upload image");
                              }
                            }
                          }}
                          className="border-cyan-200"
                        />
                        <Input
                          value={settings.contact_hero_image || ""}
                          onChange={(e) => setSettings(prev => ({ ...prev, contact_hero_image: e.target.value }))}
                          onBlur={(e) => updateSettings("contact_hero_image", e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          className="border-cyan-200"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Contact Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-2">
                      <Label className="font-medium">Contact Email</Label>
                      <Input
                        value={settings.contact_email || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                        onBlur={(e) => updateSettings("contact_email", e.target.value)}
                        placeholder="info@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium">Address</Label>
                      <Input
                        value={settings.contact_address || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, contact_address: e.target.value }))}
                        onBlur={(e) => updateSettings("contact_address", e.target.value)}
                        placeholder="Dhaka, Bangladesh"
                      />
                    </div>
                  </div>
                  
                  {/* Phone Numbers */}
                  <div className="p-5 border rounded-xl bg-teal-50 border-teal-200">
                    <div className="text-left mb-4">
                      <Label className="font-medium text-teal-700">Contact Phone Numbers</Label>
                      <p className="text-sm text-teal-500">Phone numbers displayed on contact page</p>
                    </div>
                    
                    <div className="space-y-3">
                      {(settings.contact_phones || []).map((phone, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                          <Input
                            value={phone.label}
                            onChange={(e) => {
                              const newPhones = [...(settings.contact_phones || [])];
                              newPhones[index] = { ...newPhones[index], label: e.target.value };
                              setSettings(prev => ({ ...prev, contact_phones: newPhones }));
                            }}
                            onBlur={() => updateSettings("contact_phones", settings.contact_phones)}
                            placeholder="Label (e.g., Main Office)"
                            className="flex-1 h-9"
                          />
                          <Input
                            value={phone.number}
                            onChange={(e) => {
                              const newPhones = [...(settings.contact_phones || [])];
                              newPhones[index] = { ...newPhones[index], number: e.target.value };
                              setSettings(prev => ({ ...prev, contact_phones: newPhones }));
                            }}
                            onBlur={() => updateSettings("contact_phones", settings.contact_phones)}
                            placeholder="+8801XXXXXXXXX"
                            className="flex-1 h-9"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newPhones = (settings.contact_phones || []).filter((_, i) => i !== index);
                              setSettings(prev => ({ ...prev, contact_phones: newPhones }));
                              updateSettings("contact_phones", newPhones);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          const newPhones = [...(settings.contact_phones || []), { label: "New Phone", number: "+880" }];
                          setSettings(prev => ({ ...prev, contact_phones: newPhones }));
                          updateSettings("contact_phones", newPhones);
                        }}
                        className="w-full border-dashed border-teal-300 text-teal-600 hover:bg-teal-100"
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add Phone Number
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Menu Management Section */}
                <div className="border-t pt-8">
                  <h3 className="text-lg font-semibold mb-6 text-left">🔗 Menu Management</h3>
                  
                  {/* Menu Items Management */}
                  <div className="p-5 border rounded-xl bg-indigo-50 border-indigo-200">
                    <div className="text-left mb-4">
                      <Label className="font-medium text-indigo-700">Menu Items</Label>
                      <p className="text-sm text-indigo-500">Manage header navigation menu</p>
                    </div>
                    
                    <div className="space-y-3">
                      {(settings.menu_items || []).map((item, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                          <Switch
                            checked={item.enabled}
                            onCheckedChange={(checked) => {
                              const newMenuItems = [...settings.menu_items];
                              newMenuItems[index] = { ...newMenuItems[index], enabled: checked };
                              setSettings(prev => ({ ...prev, menu_items: newMenuItems }));
                              updateSettings("menu_items", newMenuItems);
                            }}
                          />
                          <Input
                            value={item.name}
                            onChange={(e) => {
                              const newMenuItems = [...settings.menu_items];
                              newMenuItems[index] = { ...newMenuItems[index], name: e.target.value };
                              setSettings(prev => ({ ...prev, menu_items: newMenuItems }));
                            }}
                            onBlur={() => updateSettings("menu_items", settings.menu_items)}
                            placeholder="Menu Name"
                            className="flex-1 h-9"
                          />
                          <Input
                            value={item.link}
                            onChange={(e) => {
                              const newMenuItems = [...settings.menu_items];
                              newMenuItems[index] = { ...newMenuItems[index], link: e.target.value };
                              setSettings(prev => ({ ...prev, menu_items: newMenuItems }));
                            }}
                            onBlur={() => updateSettings("menu_items", settings.menu_items)}
                            placeholder="/page or https://..."
                            className="flex-1 h-9"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newMenuItems = settings.menu_items.filter((_, i) => i !== index);
                              setSettings(prev => ({ ...prev, menu_items: newMenuItems }));
                              updateSettings("menu_items", newMenuItems);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      
                      <Button
                        variant="outline"
                        onClick={() => {
                          const newMenuItems = [...(settings.menu_items || []), { name: "New Menu", link: "/", enabled: true }];
                          setSettings(prev => ({ ...prev, menu_items: newMenuItems }));
                          updateSettings("menu_items", newMenuItems);
                        }}
                        className="w-full border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-100"
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add Menu Item
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-8">
                  <h3 className="text-lg font-semibold mb-6 text-left">🎨 Theme Selection</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div 
                      onClick={() => updateSettings("active_theme", "theme1")}
                      className={`p-5 border-2 rounded-xl cursor-pointer transition-all ${
                        settings.active_theme === "theme1" 
                          ? "border-red-500 bg-red-50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          settings.active_theme === "theme1" ? "border-red-500" : "border-gray-300"
                        }`}>
                          {settings.active_theme === "theme1" && <div className="w-3 h-3 rounded-full bg-red-500"></div>}
                        </div>
                        <h4 className="font-semibold">Theme 1 - Classic</h4>
                      </div>
                      <p className="text-sm text-gray-500 ml-8">Original design with image hero background and compact form layout</p>
                    </div>
                    
                    <div 
                      onClick={() => updateSettings("active_theme", "theme2")}
                      className={`p-5 border-2 rounded-xl cursor-pointer transition-all ${
                        settings.active_theme === "theme2" 
                          ? "border-red-500 bg-red-50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          settings.active_theme === "theme2" ? "border-red-500" : "border-gray-300"
                        }`}>
                          {settings.active_theme === "theme2" && <div className="w-3 h-3 rounded-full bg-red-500"></div>}
                        </div>
                        <h4 className="font-semibold">Theme 2 - Modern</h4>
                      </div>
                      <p className="text-sm text-gray-500 ml-8">Full-page background with large form card and step indicators</p>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-8">
                  <h3 className="text-lg font-semibold mb-6 text-left">Form Styling</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label className="text-left block font-medium">Primary Color</Label>
                      <div className="flex gap-3 items-center">
                        <Input
                          type="color"
                          value={settings.primary_color || "#1e3a5f"}
                          onChange={(e) => updateSettings("primary_color", e.target.value)}
                          className="w-16 h-11 p-1 cursor-pointer"
                        />
                        <span className="text-sm text-gray-500">{settings.primary_color}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-left block font-medium">Form Card Background</Label>
                      <div className="flex gap-3 items-center">
                        <Input
                          type="color"
                          value={settings.form_card_bg || "#ffffff"}
                          onChange={(e) => updateSettings("form_card_bg", e.target.value)}
                          className="w-16 h-11 p-1 cursor-pointer"
                        />
                        <span className="text-sm text-gray-500">{settings.form_card_bg}</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-left block font-medium">Button Background</Label>
                      <div className="flex gap-3 items-center">
                        <Input
                          type="color"
                          value={settings.button_bg || "#dc3545"}
                          onChange={(e) => updateSettings("button_bg", e.target.value)}
                          className="w-16 h-11 p-1 cursor-pointer"
                        />
                        <span className="text-sm text-gray-500">{settings.button_bg}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={() => toast.success("Settings saved!")}
                  className="bg-red-500 hover:bg-red-600 rounded-xl px-8"
                  data-testid="btn-save-settings"
                >
                  Save Changes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ads Management Tab */}
          <TabsContent value="ads">
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader className="border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-left">Ads Management</CardTitle>
                  <Button 
                    onClick={() => openAdModal()} 
                    className="bg-red-500 hover:bg-red-600 rounded-xl"
                  >
                    <Plus className="h-4 w-4 mr-2" /> Add New Ad
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {ads.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No ads created yet. Click "Add New Ad" to create one.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {ads.map((ad) => (
                      <div 
                        key={ad.id} 
                        className={`border rounded-xl overflow-hidden ${ad.is_active ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50/50 opacity-60'}`}
                      >
                        {/* Ad Preview */}
                        <div className="h-32 bg-gray-100 relative">
                          {ad.ad_type === "video" ? (
                            <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                              <Play className="h-10 w-10" />
                            </div>
                          ) : ad.image_url ? (
                            <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <FileText className="h-10 w-10" />
                            </div>
                          )}
                          {/* Status Badge */}
                          <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${ad.is_active ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}`}>
                            {ad.is_active ? 'Active' : 'Inactive'}
                          </div>
                        </div>
                        
                        {/* Ad Info */}
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-800 mb-2 truncate">{ad.title}</h3>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p className="flex items-center gap-2">
                              <span className="font-medium">Type:</span>
                              <span className="capitalize">{ad.ad_type === "banner_link" ? "Banner + Link" : ad.ad_type}</span>
                            </p>
                            <p className="flex items-center gap-2">
                              <span className="font-medium">Placement:</span>
                              <span className="capitalize">{ad.placement_type === "country_page" ? "Country Page" : "Popup"}</span>
                            </p>
                            <p className="flex items-center gap-2">
                              <span className="font-medium">Delay:</span>
                              <span>{ad.show_delay_seconds}s</span>
                            </p>
                            <p className="flex items-center gap-2">
                              <span className="font-medium">Countries:</span>
                              <span className="truncate">{ad.country_slugs?.length > 0 ? ad.country_slugs.join(", ") : "All"}</span>
                            </p>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openAdModal(ad)}
                              className="flex-1 rounded-lg"
                            >
                              <Edit className="h-3 w-3 mr-1" /> Edit
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => toggleAdStatus(ad)}
                              className={`flex-1 rounded-lg ${ad.is_active ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
                            >
                              {ad.is_active ? 'Disable' : 'Enable'}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => deleteAd(ad.id)}
                              className="text-red-500 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Ad Create/Edit Modal */}
            <Dialog open={showAdModal} onOpenChange={(open) => { setShowAdModal(open); if (!open) resetAdForm(); }}>
              <DialogContent className="sm:max-w-xl rounded-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold">
                    {editingAd ? 'Edit Ad' : 'Create New Ad'}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-5 py-4">
                  {/* Title */}
                  <div className="space-y-2">
                    <Label className="font-medium">Ad Title / Name *</Label>
                    <Input
                      value={adForm.title}
                      onChange={(e) => setAdForm({ ...adForm, title: e.target.value })}
                      placeholder="e.g., Holiday Promo Banner"
                      className="rounded-xl"
                    />
                  </div>
                  
                  {/* Ad Type */}
                  <div className="space-y-2">
                    <Label className="font-medium">Ad Type *</Label>
                    <Select value={adForm.ad_type} onValueChange={(v) => setAdForm({ ...adForm, ad_type: v })}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="banner">🖼️ Banner Image</SelectItem>
                        <SelectItem value="banner_link">🔗 Banner with Link</SelectItem>
                        <SelectItem value="video">📹 Video Ad</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Image URL (for banner types) */}
                  {(adForm.ad_type === "banner" || adForm.ad_type === "banner_link") && (
                    <div className="space-y-2">
                      <Label className="font-medium">Image URL *</Label>
                      <Input
                        value={adForm.image_url}
                        onChange={(e) => setAdForm({ ...adForm, image_url: e.target.value })}
                        placeholder="https://example.com/banner.jpg"
                        className="rounded-xl"
                      />
                      {adForm.image_url && (
                        <div className="mt-2 rounded-lg overflow-hidden border">
                          <img src={adForm.image_url} alt="Preview" className="w-full h-32 object-cover" 
                               onError={(e) => e.target.src = 'https://via.placeholder.com/400x150?text=Invalid+Image+URL'} />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Video URL (for video type) */}
                  {adForm.ad_type === "video" && (
                    <div className="space-y-2">
                      <Label className="font-medium">Video URL * (YouTube or direct)</Label>
                      <Input
                        value={adForm.video_url}
                        onChange={(e) => setAdForm({ ...adForm, video_url: e.target.value })}
                        placeholder="https://youtube.com/watch?v=... or https://example.com/video.mp4"
                        className="rounded-xl"
                      />
                    </div>
                  )}
                  
                  {/* Link URL (for banner_link type) */}
                  {adForm.ad_type === "banner_link" && (
                    <div className="space-y-2">
                      <Label className="font-medium">Click Link URL</Label>
                      <Input
                        value={adForm.link_url}
                        onChange={(e) => setAdForm({ ...adForm, link_url: e.target.value })}
                        placeholder="https://example.com/landing-page"
                        className="rounded-xl"
                      />
                    </div>
                  )}
                  
                  {/* Placement Type */}
                  <div className="space-y-2">
                    <Label className="font-medium">Placement Type *</Label>
                    <Select value={adForm.placement_type} onValueChange={(v) => setAdForm({ ...adForm, placement_type: v })}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="country_page">📍 Country Page (below video & help sections)</SelectItem>
                        <SelectItem value="popup">💬 Popup (full site)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Country Selection (only for country_page placement) */}
                  {adForm.placement_type === "country_page" && (
                    <div className="space-y-2">
                      <Label className="font-medium">Show on Countries</Label>
                      <p className="text-xs text-gray-500 mb-2">Leave empty to show on ALL country pages</p>
                      <div className="border rounded-xl p-3 max-h-40 overflow-y-auto">
                        <div className="grid grid-cols-2 gap-2">
                          {countryPages.map((page) => (
                            <label key={page.slug} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                              <Checkbox 
                                checked={adForm.country_slugs.includes(page.slug)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setAdForm({ ...adForm, country_slugs: [...adForm.country_slugs, page.slug] });
                                  } else {
                                    setAdForm({ ...adForm, country_slugs: adForm.country_slugs.filter(s => s !== page.slug) });
                                  }
                                }}
                              />
                              {page.country_name}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Show Delay */}
                  <div className="space-y-2">
                    <Label className="font-medium">Show Delay (seconds)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="60"
                      value={adForm.show_delay_seconds}
                      onChange={(e) => setAdForm({ ...adForm, show_delay_seconds: parseInt(e.target.value) || 0 })}
                      className="rounded-xl w-32"
                    />
                    <p className="text-xs text-gray-500">Ad will appear after this many seconds</p>
                  </div>
                  
                  {/* Active Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div>
                      <Label className="font-medium">Active</Label>
                      <p className="text-xs text-gray-500">Enable or disable this ad</p>
                    </div>
                    <Switch 
                      checked={adForm.is_active}
                      onCheckedChange={(checked) => setAdForm({ ...adForm, is_active: checked })}
                    />
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setShowAdModal(false); resetAdForm(); }} className="rounded-xl">
                    Cancel
                  </Button>
                  <Button onClick={saveAd} className="bg-red-500 hover:bg-red-600 rounded-xl">
                    {editingAd ? 'Update Ad' : 'Create Ad'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Traffic Monitoring Tab */}
          <TabsContent value="traffic">
            <TrafficDashboard />
          </TabsContent>

          {/* Admins Management Tab */}
          <TabsContent value="admins">
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader className="border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-left">Admin Management</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowPasswordModal(true)}
                      variant="outline"
                      className="border-blue-200 text-blue-600 hover:bg-blue-50"
                    >
                      Change My Password
                    </Button>
                    <Button
                      onClick={() => setShowAdminModal(true)}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Add Admin
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {/* Current Admin Info */}
                {currentAdmin && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-sm text-blue-600 mb-1">Logged in as:</p>
                    <p className="font-semibold text-blue-800">{currentAdmin.name || currentAdmin.email}</p>
                    <p className="text-sm text-blue-600">
                      {currentAdmin.role === 'super_admin' ? 'Super Admin' : 
                       currentAdmin.role === 'moderator' ? 'SuperVisor' : 'Admin'}
                    </p>
                    {currentAdmin.permissions && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {Object.entries(currentAdmin.permissions).filter(([_, v]) => v).map(([key]) => (
                          <span key={key} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                            {key.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Admins List */}
                <div className="space-y-3">
                  {admins.map((admin) => (
                    <div 
                      key={admin.id} 
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                          admin.role === 'super_admin' ? 'bg-gradient-to-br from-purple-500 to-purple-600' :
                          admin.role === 'moderator' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                          'bg-gradient-to-br from-red-500 to-red-600'
                        }`}>
                          {admin.name?.charAt(0) || admin.email?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{admin.name || 'Admin'}</p>
                          <p className="text-sm text-gray-500">{admin.email}</p>
                          {admin.role === 'moderator' && admin.permissions && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {Object.entries(admin.permissions).filter(([_, v]) => v).map(([key]) => (
                                <span key={key} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-xs rounded">
                                  {key.replace('_', ' ')}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          admin.role === 'super_admin' 
                            ? 'bg-purple-100 text-purple-700' 
                            : admin.role === 'moderator'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {admin.role === 'super_admin' ? 'Super Admin' : admin.role === 'moderator' ? 'SuperVisor' : 'Admin'}
                        </span>
                        {admin.id !== currentAdmin?.id && admin.role !== 'super_admin' && (
                          <Button
                            onClick={() => handleDeleteAdmin(admin.id)}
                            variant="outline"
                            size="sm"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Add Admin Modal */}
            <Dialog open={showAdminModal} onOpenChange={setShowAdminModal}>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add New {adminForm.role === 'moderator' ? 'SuperVisor' : 'Admin'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={adminForm.name}
                      onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                      placeholder="Enter name"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={adminForm.email}
                      onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                      placeholder="admin@example.com"
                      className="mt-1.5"
                      required
                    />
                  </div>
                  <div>
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      value={adminForm.password}
                      onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                      placeholder="Enter password"
                      className="mt-1.5"
                      required
                    />
                  </div>
                  <div>
                    <Label>Role *</Label>
                    <select
                      value={adminForm.role}
                      onChange={(e) => setAdminForm({ ...adminForm, role: e.target.value })}
                      className="mt-1.5 w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="admin">Admin (Full Access)</option>
                      <option value="moderator">SuperVisor (Limited Access)</option>
                    </select>
                  </div>
                  
                  {/* Permissions Section - Only for Moderator */}
                  {adminForm.role === 'moderator' && (
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <Label className="text-blue-700 font-medium mb-3 block">SuperVisor Permissions</Label>
                      <p className="text-sm text-blue-600 mb-3">Select what this SuperVisor can access:</p>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { key: 'country_rules', label: 'Country Rules' },
                          { key: 'country_pages', label: 'Country Pages' },
                          { key: 'blog_posts', label: 'Blog Posts' },
                          { key: 'leads', label: 'Leads' },
                          { key: 'ads', label: 'Ads' },
                          { key: 'settings', label: 'Settings' },
                          { key: 'traffic', label: 'Traffic Dashboard' }
                        ].map(({ key, label }) => (
                          <label key={key} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={adminForm.permissions[key]}
                              onChange={(e) => setAdminForm({
                                ...adminForm,
                                permissions: { ...adminForm.permissions, [key]: e.target.checked }
                              })}
                              className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowAdminModal(false)}>Cancel</Button>
                  <Button onClick={handleCreateAdmin} className="bg-red-500 hover:bg-red-600">
                    Create {adminForm.role === 'moderator' ? 'SuperVisor' : 'Admin'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Change Password Modal */}
            <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Change Password</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Current Password *</Label>
                    <Input
                      type="password"
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                      placeholder="Enter current password"
                      className="mt-1.5"
                      required
                    />
                  </div>
                  <div>
                    <Label>New Password *</Label>
                    <Input
                      type="password"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                      placeholder="Enter new password"
                      className="mt-1.5"
                      required
                    />
                  </div>
                  <div>
                    <Label>Confirm New Password *</Label>
                    <Input
                      type="password"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                      placeholder="Confirm new password"
                      className="mt-1.5"
                      required
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
                  <Button onClick={handleChangePassword} className="bg-red-500 hover:bg-red-600">
                    Change Password
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="activity">
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader className="border-b border-gray-100">
                <CardTitle className="text-left flex items-center gap-2">
                  <FileText className="h-5 w-5 text-red-500" />
                  Activity Log
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {/* Activity Stats */}
                {activityStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                      <p className="text-sm text-blue-600">Last 24 Hours</p>
                      <p className="text-2xl font-bold text-blue-800">{activityStats.recent_24h || 0}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                      <p className="text-sm text-green-600">Total Logins</p>
                      <p className="text-2xl font-bold text-green-800">{activityStats.by_action?.login || 0}</p>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                      <p className="text-sm text-purple-600">Creates</p>
                      <p className="text-2xl font-bold text-purple-800">{activityStats.by_action?.create || 0}</p>
                    </div>
                    <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                      <p className="text-sm text-orange-600">Updates</p>
                      <p className="text-2xl font-bold text-orange-800">{activityStats.by_action?.update || 0}</p>
                    </div>
                  </div>
                )}

                {/* Activity Log Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Time</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">User</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Action</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Resource</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(activityLogs) && activityLogs.map((log) => (
                        <tr key={log.id || Math.random()} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-gray-500">
                            {log.created_at ? new Date(log.created_at).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : '-'}
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-gray-800">{String(log.admin_name || 'Unknown')}</p>
                              <p className="text-xs text-gray-500">{String(log.admin_email || '')}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.action === 'login' ? 'bg-green-100 text-green-700' :
                              log.action === 'create' ? 'bg-blue-100 text-blue-700' :
                              log.action === 'update' ? 'bg-yellow-100 text-yellow-700' :
                              log.action === 'delete' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {String(log.action || 'unknown')}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                              {String(log.resource_type || 'unknown')}
                            </span>
                            {log.resource_name && (
                              <p className="text-xs text-gray-500 mt-1">{String(log.resource_name)}</p>
                            )}
                          </td>
                          <td className="py-3 px-4 text-gray-600 max-w-xs truncate">
                            {String(log.details || '-')}
                          </td>
                        </tr>
                      ))}
                      {(!activityLogs || activityLogs.length === 0) && (
                        <tr>
                          <td colSpan="5" className="py-8 text-center text-gray-500">
                            No activity logs yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Newsletter Tab */}
          <TabsContent value="newsletter">
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader className="border-b border-gray-100">
                <CardTitle className="text-left flex items-center gap-3">
                  <Mail className="h-6 w-6 text-red-500" />
                  Newsletter Management
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {/* Stats Cards - Clickable */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div 
                    className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
                    onClick={() => openSubscribersModal('all')}
                  >
                    <p className="text-blue-100 text-sm">Total Subscribers</p>
                    <p className="text-3xl font-bold">{newsletterStats.total}</p>
                    <p className="text-blue-200 text-xs mt-2">Click to view all →</p>
                  </div>
                  <div 
                    className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
                    onClick={() => openSubscribersModal('active')}
                  >
                    <p className="text-green-100 text-sm">Active Subscribers</p>
                    <p className="text-3xl font-bold">{newsletterStats.active}</p>
                    <p className="text-green-200 text-xs mt-2">Click to view active →</p>
                  </div>
                  <div 
                    className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
                    onClick={() => openSubscribersModal('unsubscribed')}
                  >
                    <p className="text-orange-100 text-sm">Unsubscribed</p>
                    <p className="text-3xl font-bold">{newsletterStats.unsubscribed}</p>
                    <p className="text-orange-200 text-xs mt-2">Click to view list →</p>
                  </div>
                  <div 
                    className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
                    onClick={() => setShowClickersModal(true)}
                  >
                    <p className="text-purple-100 text-sm">📊 Total Clicks</p>
                    <p className="text-3xl font-bold">{clickStats.total_clicks}</p>
                    <p className="text-purple-200 text-xs mt-2">{clickStats.total_unique_clickers} unique readers →</p>
                  </div>
                </div>

                {/* Blog Posts - Send Newsletter */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Newspaper className="h-5 w-5 text-red-500" />
                    Send Newsletter for Published Blog
                  </h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto border rounded-xl p-3">
                    {blogPosts.filter(p => p.is_published).map(post => {
                      const blogClickData = clickStats.by_blog?.find(b => b.blog_id === post.id);
                      const clickCount = blogClickData?.total_clicks || 0;
                      return (
                      <div key={post.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-sm truncate">{post.title}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(post.created_at).toLocaleDateString()}
                            {clickCount > 0 && (
                              <span className="ml-2 text-purple-600 font-medium">
                                📊 {clickCount} clicks
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {/* Share Link Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const shareUrl = `${BACKEND_URL}/api/og/blog/${post.slug}`;
                              navigator.clipboard.writeText(shareUrl);
                              toast.success("Blog Link Copied!");
                            }}
                            className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                            title="Copy shareable link for social media"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Share
                          </Button>
                          {clickCount > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => viewBlogClickers(post.id, post.title)}
                              className="text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                              title="View who clicked"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => sendNewsletterForBlog(post.id, false)}
                            disabled={sendingNewsletter[post.id]}
                            className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                            title={`Send only to ${newsletterStats.active} active subscribers (no leads)`}
                          >
                            {sendingNewsletter[post.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3 mr-1" />}
                            Subscribers Only ({newsletterStats.active})
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => previewLeadsForNewsletter(post.id)}
                            disabled={sendingNewsletter[post.id]}
                            className="text-xs bg-red-500 hover:bg-red-600"
                            title="Preview leads and send to subscribers + leads"
                          >
                            {sendingNewsletter[post.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Users className="h-3 w-3 mr-1" />}
                            + Leads
                          </Button>
                        </div>
                      </div>
                    )})}
                    {blogPosts.filter(p => p.is_published).length === 0 && (
                      <p className="text-gray-500 text-center py-4">No published blogs to send</p>
                    )}
                  </div>
                </div>

                {/* Subscribers Table */}
                <h3 className="text-lg font-semibold mb-3">Subscribers List</h3>
                <div className="overflow-x-auto border rounded-xl">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold">Name</th>
                        <th className="text-left p-3 text-sm font-semibold">Email</th>
                        <th className="text-left p-3 text-sm font-semibold">Source</th>
                        <th className="text-left p-3 text-sm font-semibold">Status</th>
                        <th className="text-left p-3 text-sm font-semibold">Subscribed</th>
                        <th className="text-left p-3 text-sm font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newsletterSubscribers.map((sub, idx) => (
                        <tr key={sub.id || idx} className="border-t hover:bg-gray-50">
                          <td className="p-3 text-sm">{sub.name}</td>
                          <td className="p-3 text-sm">{sub.email}</td>
                          <td className="p-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              sub.source === 'website' ? 'bg-blue-100 text-blue-700' :
                              sub.source === 'lead' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {sub.source}
                            </span>
                          </td>
                          <td className="p-3">
                            {sub.is_active ? (
                              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">Active</span>
                            ) : (
                              <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">Unsubscribed</span>
                            )}
                          </td>
                          <td className="p-3 text-sm text-gray-500">
                            {new Date(sub.subscribed_at).toLocaleDateString()}
                          </td>
                          <td className="p-3">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteNewsletterSubscriber(sub.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {newsletterSubscribers.length === 0 && (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-gray-500">
                            No subscribers yet. Share your newsletter form!
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            
            {/* Subscribers Modal */}
            <Dialog open={showSubscribersModal} onOpenChange={setShowSubscribersModal}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-red-500" />
                    {subscribersModalFilter === 'all' && 'All Subscribers'}
                    {subscribersModalFilter === 'active' && 'Active Subscribers'}
                    {subscribersModalFilter === 'unsubscribed' && 'Unsubscribed Users'}
                    <span className="text-sm font-normal text-gray-500">
                      ({getFilteredSubscribers().length})
                    </span>
                  </DialogTitle>
                </DialogHeader>
                <div className="overflow-y-auto max-h-[60vh]">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold">Name</th>
                        <th className="text-left p-3 text-sm font-semibold">Email</th>
                        <th className="text-left p-3 text-sm font-semibold">Source</th>
                        <th className="text-left p-3 text-sm font-semibold">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredSubscribers().map((sub, idx) => (
                        <tr key={sub.id || idx} className="border-t hover:bg-gray-50">
                          <td className="p-3 text-sm font-medium">{sub.name}</td>
                          <td className="p-3 text-sm">{sub.email}</td>
                          <td className="p-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              sub.source === 'website' ? 'bg-blue-100 text-blue-700' :
                              sub.source === 'lead' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {sub.source}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-gray-500">
                            {subscribersModalFilter === 'unsubscribed' && sub.unsubscribed_at
                              ? new Date(sub.unsubscribed_at).toLocaleDateString()
                              : new Date(sub.subscribed_at).toLocaleDateString()
                            }
                          </td>
                        </tr>
                      ))}
                      {getFilteredSubscribers().length === 0 && (
                        <tr>
                          <td colSpan="4" className="p-8 text-center text-gray-500">
                            No subscribers found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Leads Preview Modal */}
            <Dialog open={showLeadsPreviewModal} onOpenChange={setShowLeadsPreviewModal}>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-red-500" />
                    Leads to Include in Newsletter
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <p className="text-sm text-blue-700">
                      <strong>Note:</strong> These leads will receive the newsletter email. 
                      Leads who have previously unsubscribed will NOT be included.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">{newsletterStats.active}</p>
                      <p className="text-sm text-green-700">Active Subscribers</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-orange-600">{leadsForNewsletter.length}</p>
                      <p className="text-sm text-orange-700">Eligible Leads</p>
                    </div>
                  </div>
                  
                  <div className="overflow-y-auto max-h-[35vh] border rounded-lg">
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2 text-xs font-semibold">Name</th>
                          <th className="text-left p-2 text-xs font-semibold">Email</th>
                          <th className="text-left p-2 text-xs font-semibold">Phone</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leadsForNewsletter.map((lead, idx) => (
                          <tr key={lead.id || idx} className="border-t hover:bg-gray-50">
                            <td className="p-2 text-sm">{lead.full_name}</td>
                            <td className="p-2 text-sm">{lead.email}</td>
                            <td className="p-2 text-sm text-gray-500">{lead.phone}</td>
                          </tr>
                        ))}
                        {leadsForNewsletter.length === 0 && (
                          <tr>
                            <td colSpan="3" className="p-4 text-center text-gray-500 text-sm">
                              No eligible leads found (all may already be subscribers or unsubscribed)
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Total recipients: <strong>{newsletterStats.active + leadsForNewsletter.length}</strong>
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowLeadsPreviewModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={confirmSendWithLeads}
                    className="bg-red-500 hover:bg-red-600"
                    disabled={sendingNewsletter[selectedBlogForNewsletter]}
                  >
                    {sendingNewsletter[selectedBlogForNewsletter] ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send to All ({newsletterStats.active + leadsForNewsletter.length})
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Clickers Stats Modal */}
            <Dialog open={showClickersModal} onOpenChange={setShowClickersModal}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-purple-500" />
                    📊 Newsletter Click Analytics
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-purple-600">{clickStats.total_clicks}</p>
                      <p className="text-sm text-purple-700">Total Clicks</p>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-indigo-600">{clickStats.total_unique_clickers}</p>
                      <p className="text-sm text-indigo-700">Unique Readers</p>
                    </div>
                  </div>
                  
                  {/* By Blog Stats */}
                  {selectedBlogClickers.clickers.length > 0 ? (
                    <>
                      <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                        <Newspaper className="h-4 w-4" />
                        {selectedBlogClickers.blog_title}
                      </h4>
                      <div className="overflow-y-auto max-h-[40vh] border rounded-lg">
                        <table className="w-full">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="text-left p-3 text-sm font-semibold">Name</th>
                              <th className="text-left p-3 text-sm font-semibold">Email</th>
                              <th className="text-left p-3 text-sm font-semibold">Clicked At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedBlogClickers.clickers.map((clicker, idx) => (
                              <tr key={idx} className="border-t hover:bg-gray-50">
                                <td className="p-3 text-sm font-medium">{clicker.name}</td>
                                <td className="p-3 text-sm">{clicker.email}</td>
                                <td className="p-3 text-sm text-gray-500">
                                  {new Date(clicker.clicked_at).toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <>
                      <h4 className="font-semibold text-gray-700">Clicks by Blog Post</h4>
                      <div className="overflow-y-auto max-h-[40vh] border rounded-lg">
                        <table className="w-full">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="text-left p-3 text-sm font-semibold">Blog Title</th>
                              <th className="text-center p-3 text-sm font-semibold">Total Clicks</th>
                              <th className="text-center p-3 text-sm font-semibold">Unique</th>
                              <th className="text-center p-3 text-sm font-semibold">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clickStats.by_blog?.map((blog, idx) => (
                              <tr key={idx} className="border-t hover:bg-gray-50">
                                <td className="p-3 text-sm font-medium">{blog.blog_title}</td>
                                <td className="p-3 text-sm text-center">{blog.total_clicks}</td>
                                <td className="p-3 text-sm text-center">{blog.unique_clickers}</td>
                                <td className="p-3 text-center">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => viewBlogClickers(blog.blog_id, blog.blog_title)}
                                    className="text-xs"
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    View
                                  </Button>
                                </td>
                              </tr>
                            ))}
                            {(!clickStats.by_blog || clickStats.by_blog.length === 0) && (
                              <tr>
                                <td colSpan="4" className="p-8 text-center text-gray-500">
                                  No click data yet. Send newsletters to track engagement!
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
                <DialogFooter>
                  {selectedBlogClickers.clickers.length > 0 && (
                    <Button variant="outline" onClick={() => setSelectedBlogClickers({ blog_id: '', blog_title: '', clickers: [] })}>
                      ← Back to All
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => {
                    setShowClickersModal(false);
                    setSelectedBlogClickers({ blog_id: '', blog_title: '', clickers: [] });
                  }}>
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Comments Management Tab */}
          <TabsContent value="comments">
            <CommentsManagementTab currentAdmin={currentAdmin} />
          </TabsContent>

          {/* AI Blog Writer Tab */}
          <TabsContent value="ai-writer">
            <AIBlogWriterTab blogPosts={blogPosts} setBlogPosts={setBlogPosts} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ===========================================
// AI BLOG WRITER COMPONENT
// ===========================================
const AIBlogWriterTab = ({ blogPosts, setBlogPosts }) => {
  const [topic, setTopic] = useState("");
  const [urls, setUrls] = useState([""]);
  const [additionalPrompt, setAdditionalPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedDraft, setEditedDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const addUrlField = () => setUrls([...urls, ""]);
  const removeUrlField = (index) => setUrls(urls.filter((_, i) => i !== index));
  const updateUrl = (index, value) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    setUrls(newUrls);
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error("টপিক লিখুন!");
      return;
    }
    setGenerating(true);
    setDraft(null);
    setEditMode(false);
    try {
      // Start generation (returns immediately with task_id)
      const res = await axios.post(`${API}/ai-blog-writer/generate`, {
        topic: topic.trim(),
        urls: urls.filter(u => u.trim()),
        additional_prompt: additionalPrompt.trim()
      });
      const taskId = res.data.task_id;
      
      // Poll for result
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/ai-blog-writer/status/${taskId}`);
          if (statusRes.data.status === "completed") {
            clearInterval(pollInterval);
            setDraft(statusRes.data.draft);
            setEditedDraft(statusRes.data.draft);
            setGenerating(false);
            toast.success("ব্লগ ড্রাফট তৈরি হয়েছে!");
          } else if (statusRes.data.status === "failed") {
            clearInterval(pollInterval);
            setGenerating(false);
            toast.error(statusRes.data.error || "Generation ব্যর্থ হয়েছে");
          }
        } catch (pollErr) {
          // Task might not be found if server restarted
          if (pollErr.response?.status === 404) {
            clearInterval(pollInterval);
            setGenerating(false);
            toast.error("Task expired. আবার চেষ্টা করুন।");
          }
        }
      }, 3000); // Poll every 3 seconds
      
      // Timeout after 3 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (generating) {
          setGenerating(false);
          toast.error("Timeout! AI generation অনেক সময় নিচ্ছে। আবার চেষ্টা করুন।");
        }
      }, 180000);
    } catch (err) {
      setGenerating(false);
      toast.error(err.response?.data?.detail || "AI generation শুরু করতে ব্যর্থ");
    }
  };

  const handleSaveDraft = async (publish = false) => {
    if (!editedDraft) return;
    setSaving(true);
    try {
      const postData = {
        title: editedDraft.title,
        slug: editedDraft.slug,
        excerpt: editedDraft.excerpt,
        content: editedDraft.content,
        category: editedDraft.category,
        tags: editedDraft.tags,
        is_published: publish,
        author: "AI Writer (Claude)"
      };
      await axios.post(`${API}/blog-posts`, postData);
      toast.success(publish ? "ব্লগ পাবলিশ হয়েছে!" : "ড্রাফট সেভ হয়েছে!");
      setDraft(null);
      setEditedDraft(null);
      setTopic("");
      setUrls([""]);
      setAdditionalPrompt("");
      // Refresh blog list
      const res = await axios.get(`${API}/blog-posts`);
      setBlogPosts(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "সেভ ব্যর্থ হয়েছে");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="shadow-lg border-0 rounded-2xl">
      <CardHeader className="border-b border-gray-100">
        <CardTitle className="text-left flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Sparkles className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <span className="text-xl font-bold">AI Blog Writer</span>
            <p className="text-sm text-gray-500 font-normal mt-1">Claude Sonnet 4.6 powered</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {/* Input Section */}
        {!draft && (
          <div className="space-y-5">
            {/* Topic */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-purple-500" /> Blog Topic / Title
              </Label>
              <Input
                data-testid="ai-writer-topic"
                placeholder="e.g., জাপানে উচ্চশিক্ষা: সম্পূর্ণ গাইড ২০২৬"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="border-gray-200 focus:border-purple-400 focus:ring-purple-200"
              />
            </div>

            {/* Web URLs */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Link2 className="h-4 w-4 text-blue-500" /> Reference URLs (AI will crawl these)
              </Label>
              {urls.map((url, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    data-testid={`ai-writer-url-${index}`}
                    placeholder="https://example.com/article..."
                    value={url}
                    onChange={(e) => updateUrl(index, e.target.value)}
                    className="border-gray-200 focus:border-blue-400"
                  />
                  {urls.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeUrlField(index)} className="text-red-400 hover:text-red-600">
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addUrlField} className="text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="ai-writer-add-url">
                <Plus className="h-3 w-3 mr-1" /> Add URL
              </Button>
            </div>

            {/* Additional Prompt */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Bot className="h-4 w-4 text-green-500" /> Additional Instructions (Optional)
              </Label>
              <Textarea
                data-testid="ai-writer-prompt"
                placeholder="e.g., scholarship info ektu beshi dao, cost breakdown table add koro..."
                value={additionalPrompt}
                onChange={(e) => setAdditionalPrompt(e.target.value)}
                rows={3}
                className="border-gray-200 focus:border-green-400"
              />
            </div>

            {/* Generate Button */}
            <Button
              data-testid="ai-writer-generate-btn"
              onClick={handleGenerate}
              disabled={generating || !topic.trim()}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-6 text-base font-semibold rounded-xl"
            >
              {generating ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" /> AI Writing... (30-60 sec)
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" /> Generate Blog Draft
                </span>
              )}
            </Button>

            {generating && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center space-y-2">
                <div className="flex justify-center gap-1">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="w-2.5 h-2.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
                <p className="text-purple-700 text-sm font-medium">Claude Sonnet 4.6 আপনার ব্লগ লিখছে...</p>
                <p className="text-purple-500 text-xs">URLs crawl করে তথ্য সংগ্রহ করছে</p>
              </div>
            )}
          </div>
        )}

        {/* Draft Preview / Edit Section */}
        {draft && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" /> Draft Ready
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditMode(!editMode)} data-testid="ai-writer-toggle-edit">
                  <Edit className="h-3 w-3 mr-1" /> {editMode ? "Preview" : "Edit"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setDraft(null); setEditedDraft(null); }} data-testid="ai-writer-discard">
                  <X className="h-3 w-3 mr-1" /> Discard
                </Button>
              </div>
            </div>

            {/* Meta Info */}
            {editMode ? (
              <div className="space-y-3 bg-gray-50 p-4 rounded-xl">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Title</Label>
                  <Input value={editedDraft.title} onChange={(e) => setEditedDraft({...editedDraft, title: e.target.value})} data-testid="ai-writer-edit-title" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Slug (URL)</Label>
                  <Input value={editedDraft.slug} onChange={(e) => setEditedDraft({...editedDraft, slug: e.target.value})} data-testid="ai-writer-edit-slug" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Excerpt</Label>
                  <Textarea value={editedDraft.excerpt} onChange={(e) => setEditedDraft({...editedDraft, excerpt: e.target.value})} rows={2} data-testid="ai-writer-edit-excerpt" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">Category</Label>
                    <Input value={editedDraft.category} onChange={(e) => setEditedDraft({...editedDraft, category: e.target.value})} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-semibold">Tags (comma separated)</Label>
                    <Input value={editedDraft.tags?.join(", ")} onChange={(e) => setEditedDraft({...editedDraft, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean)})} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-semibold">Content (Markdown)</Label>
                  <Textarea 
                    value={editedDraft.content} 
                    onChange={(e) => setEditedDraft({...editedDraft, content: e.target.value})} 
                    rows={20} 
                    className="font-mono text-sm"
                    data-testid="ai-writer-edit-content"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-gray-50 p-4 rounded-xl space-y-2">
                  <p className="text-sm"><span className="font-semibold text-gray-600">Title:</span> {editedDraft.title}</p>
                  <p className="text-sm"><span className="font-semibold text-gray-600">Slug:</span> /{editedDraft.slug}</p>
                  <p className="text-sm"><span className="font-semibold text-gray-600">Category:</span> {editedDraft.category}</p>
                  <p className="text-sm"><span className="font-semibold text-gray-600">Tags:</span> {editedDraft.tags?.join(", ")}</p>
                </div>
                <div className="border border-gray-200 rounded-xl p-6 bg-white max-h-[600px] overflow-y-auto prose prose-sm max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{editedDraft.content}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Save Buttons */}
            <div className="flex gap-3">
              <Button
                data-testid="ai-writer-save-draft"
                onClick={() => handleSaveDraft(false)}
                disabled={saving}
                variant="outline"
                className="flex-1 py-5 border-purple-300 text-purple-700 hover:bg-purple-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                Save as Draft
              </Button>
              <Button
                data-testid="ai-writer-publish"
                onClick={() => handleSaveDraft(true)}
                disabled={saving}
                className="flex-1 py-5 bg-green-600 hover:bg-green-700 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Publish Now
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ===========================================
// ADS COMPONENTS
// ===========================================

// Helper function to get YouTube embed URL
const getYouTubeEmbedUrlHelper = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? `https://www.youtube.com/embed/${match[2]}` : url;
};

// Country Page Ad Section Component
const CountryPageAdSection = ({ slug }) => {
  const [ad, setAd] = useState(null);
  const [showAd, setShowAd] = useState(false);
  const [closed, setClosed] = useState(false);
  
  useEffect(() => {
    const fetchAd = async () => {
      try {
        const res = await axios.get(`${API}/ads/active?placement_type=country_page&country_slug=${slug}`);
        if (res.data && res.data.length > 0) {
          // Pick random ad from available ones
          const randomAd = res.data[Math.floor(Math.random() * res.data.length)];
          setAd(randomAd);
          
          // Show ad after delay
          setTimeout(() => {
            setShowAd(true);
          }, (randomAd.show_delay_seconds || 5) * 1000);
        }
      } catch (error) {
        console.error("Error fetching country page ad:", error);
      }
    };
    
    fetchAd();
  }, [slug]);
  
  if (!ad || !showAd || closed || !['banner', 'banner_link', 'video'].includes(ad.ad_type)) return null;
  
  const handleAdClick = () => {
    if (ad.ad_type === "banner_link" && ad.link_url) {
      window.open(ad.link_url, "_blank");
    }
  };
  
  return (
    <Card className="mt-6 shadow-lg border-0 rounded-2xl overflow-hidden relative">
      {/* Close Button - Glassy Style */}
      <button 
        onClick={() => setClosed(true)}
        className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-lg border border-gray-200 flex items-center justify-center opacity-90 transition-all hover:opacity-100 hover:bg-white hover:shadow-xl hover:scale-110"
      >
        <X className="h-4 w-4 text-gray-600" />
      </button>
      
      {/* Ad Content */}
      <div 
        className={`${ad.ad_type === "banner_link" ? "cursor-pointer" : ""}`}
        onClick={handleAdClick}
      >
        {ad.ad_type === "video" ? (
          <div className="aspect-video">
            {ad.video_url?.includes("youtube") || ad.video_url?.includes("youtu.be") ? (
              <iframe 
                src={getYouTubeEmbedUrlHelper(ad.video_url)} 
                title={ad.title} 
                className="w-full h-full" 
                frameBorder="0" 
                allowFullScreen
              ></iframe>
            ) : (
              <video 
                src={ad.video_url} 
                className="w-full h-full object-cover" 
                controls 
                autoPlay 
                muted
              ></video>
            )}
          </div>
        ) : (
          <img 
            src={ad.image_url} 
            alt={ad.title} 
            className="w-full h-auto object-cover"
          />
        )}
      </div>
      
      {/* Ad Label */}
      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 text-white text-xs rounded">
        Ad
      </div>
    </Card>
  );
};

// Popup Ad Component (used globally)
const PopupAd = () => {
  const [ad, setAd] = useState(null);
  const [showAd, setShowAd] = useState(false);
  
  useEffect(() => {
    // Check if popup was already shown this session
    const popupShown = sessionStorage.getItem('popup_ad_shown');
    if (popupShown) return;
    
    const fetchAd = async () => {
      try {
        const res = await axios.get(`${API}/ads/active?placement_type=popup`);
        if (res.data && res.data.length > 0) {
          // Pick random ad from available ones
          const randomAd = res.data[Math.floor(Math.random() * res.data.length)];
          setAd(randomAd);
          
          // Show ad after delay
          setTimeout(() => {
            setShowAd(true);
          }, (randomAd.show_delay_seconds || 5) * 1000);
        }
      } catch (error) {
        console.error("Error fetching popup ad:", error);
      }
    };
    
    fetchAd();
  }, []);
  
  const closePopup = () => {
    setShowAd(false);
    sessionStorage.setItem('popup_ad_shown', 'true');
  };
  
  if (!ad || !showAd) return null;
  
  const handleAdClick = () => {
    if (ad.ad_type === "banner_link" && ad.link_url) {
      window.open(ad.link_url, "_blank");
    }
  };
  
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative max-w-lg w-full bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Close Button - Glassy Style */}
        <button 
          onClick={closePopup}
          className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg border border-gray-200 flex items-center justify-center opacity-90 transition-all hover:opacity-100 hover:bg-white hover:shadow-xl hover:scale-110"
        >
          <X className="h-5 w-5 text-gray-600" />
        </button>
        
        {/* Ad Content */}
        <div 
          className={`${ad.ad_type === "banner_link" ? "cursor-pointer" : ""}`}
          onClick={handleAdClick}
        >
          {ad.ad_type === "video" ? (
            <div className="aspect-video">
              {ad.video_url?.includes("youtube") || ad.video_url?.includes("youtu.be") ? (
                <iframe 
                  src={getYouTubeEmbedUrlHelper(ad.video_url)} 
                  title={ad.title} 
                  className="w-full h-full" 
                  frameBorder="0" 
                  allowFullScreen
                ></iframe>
              ) : (
                <video 
                  src={ad.video_url} 
                  className="w-full h-full object-cover" 
                  controls 
                  autoPlay 
                  muted
                ></video>
              )}
            </div>
          ) : (
            <img 
              src={ad.image_url} 
              alt={ad.title} 
              className="w-full h-auto object-cover max-h-[70vh]"
            />
          )}
        </div>
        
        {/* Ad Label & Close hint */}
        <div className="p-3 bg-gray-50 flex items-center justify-between">
          <span className="text-xs text-gray-400">Sponsored</span>
          <button 
            onClick={closePopup}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Close Ad
          </button>
        </div>
      </div>
    </div>
  );
};

// ===========================================
// COMMENTS MANAGEMENT TAB COMPONENT (ADMIN)
// ===========================================

const CommentsManagementTab = ({ currentAdmin }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, pending, approved, rejected
  const [processingId, setProcessingId] = useState(null);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const url = filter === "all" 
        ? `${API}/admin/comments` 
        : `${API}/admin/comments?status=${filter}`;
      const res = await axios.get(url);
      setComments(res.data);
    } catch (error) {
      console.error("Error fetching comments:", error);
      toast.error("Failed to load comments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [filter]);

  const handleApprove = async (commentId) => {
    setProcessingId(commentId);
    try {
      await axios.put(`${API}/admin/comments/${commentId}/approve?admin_email=${currentAdmin?.email || 'admin'}`);
      toast.success("Comment approved!");
      fetchComments();
    } catch (error) {
      console.error("Error approving comment:", error);
      toast.error("Failed to approve comment");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (commentId) => {
    setProcessingId(commentId);
    try {
      await axios.put(`${API}/admin/comments/${commentId}/reject`);
      toast.success("Comment rejected");
      fetchComments();
    } catch (error) {
      console.error("Error rejecting comment:", error);
      toast.error("Failed to reject comment");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    
    setProcessingId(commentId);
    try {
      await axios.delete(`${API}/admin/comments/${commentId}`);
      toast.success("Comment deleted");
      fetchComments();
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast.error("Failed to delete comment");
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">Pending</span>;
      case "approved":
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">Approved</span>;
      case "rejected":
        return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">Rejected</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">{status}</span>;
    }
  };

  const pendingCount = comments.filter(c => c.status === "pending").length;

  return (
    <Card className="shadow-lg border-0 rounded-2xl">
      <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
        <CardTitle className="text-left flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-red-500" />
          Comments Management
          {pendingCount > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-yellow-100 text-yellow-700 rounded-full">
              {pendingCount} pending
            </span>
          )}
        </CardTitle>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none"
          >
            <option value="all">All Comments</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <Button variant="outline" onClick={fetchComments} className="border-gray-200">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No comments found</p>
            <p className="text-sm">Comments will appear here when visitors leave them on blog posts</p>
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div 
                key={comment.id} 
                className={`p-4 rounded-xl border ${
                  comment.status === "pending" 
                    ? "border-yellow-200 bg-yellow-50/50" 
                    : comment.status === "rejected"
                    ? "border-red-200 bg-red-50/30"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="font-semibold text-gray-800">{comment.name}</span>
                      <span className="text-xs text-gray-400">({comment.email})</span>
                      {getStatusBadge(comment.status)}
                    </div>
                    <p className="text-gray-600 text-sm mb-2 whitespace-pre-wrap">{comment.comment}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span>On: <span className="font-medium text-gray-600">{comment.blog_title}</span></span>
                      <span>•</span>
                      <span>{formatDate(comment.created_at)}</span>
                      {comment.approved_by && (
                        <>
                          <span>•</span>
                          <span>Approved by: {comment.approved_by}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {comment.status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(comment.id)}
                          disabled={processingId === comment.id}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          {processingId === comment.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(comment.id)}
                          disabled={processingId === comment.id}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(comment.id)}
                      disabled={processingId === comment.id}
                      className="border-gray-200 text-gray-600 hover:bg-gray-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const CHART_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'];

// ===========================================
// BLOG COMMENTS SECTION COMPONENT
// ===========================================

const BlogCommentsSection = ({ slug }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', comment: '' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const res = await axios.get(`${API}/blog-posts/${slug}/comments`);
        setComments(res.data);
      } catch (error) {
        console.error("Error fetching comments:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchComments();
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.comment) {
      toast.error("Please fill all fields");
      return;
    }
    
    // Client-side email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    // Validate comment length
    if (formData.comment.length < 5) {
      toast.error("Comment must be at least 5 characters");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/blog-posts/${slug}/comments`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', comment: '' });
      toast.success("Comment submitted! It will appear after admin approval.");
    } catch (error) {
      console.error("Error submitting comment:", error);
      
      // Parse validation errors from backend
      if (error.response?.status === 422 && error.response?.data?.detail) {
        const details = error.response.data.detail;
        if (Array.isArray(details) && details.length > 0) {
          // Get the first validation error message
          const firstError = details[0];
          const field = firstError.loc?.[1] || 'field';
          const msg = firstError.msg || 'Invalid input';
          toast.error(`${field}: ${msg}`);
        } else {
          toast.error("Please check your input and try again");
        }
      } else if (error.response?.status === 404) {
        toast.error("Blog post not found");
      } else {
        toast.error("Failed to submit comment. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="mt-12 bg-white rounded-2xl shadow-lg p-6 md:p-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <MessageSquare className="h-6 w-6 text-red-500" />
        Comments ({comments.length})
      </h2>

      {/* Comment Form */}
      {!submitted ? (
        <form onSubmit={handleSubmit} className="mb-8 p-6 bg-gray-50 rounded-xl">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Leave a Comment</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                title="Please enter a valid email address (e.g., name@example.com)"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
                required
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-1">Comment *</label>
            <textarea
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              placeholder="Write your comment here..."
              rows={4}
              minLength={5}
              maxLength={2000}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all resize-none"
              required
            />
          </div>
          <Button 
            type="submit" 
            disabled={submitting}
            className="bg-red-500 hover:bg-red-600 text-white px-6"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Submit Comment
              </>
            )}
          </Button>
        </form>
      ) : (
        <div className="mb-8 p-6 bg-green-50 rounded-xl text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-green-700 mb-2">Thank you for your comment!</h3>
          <p className="text-green-600 text-sm">Your comment has been submitted and is awaiting admin approval.</p>
          <Button 
            onClick={() => setSubmitted(false)} 
            variant="outline" 
            className="mt-4 border-green-500 text-green-600 hover:bg-green-50"
          >
            Write Another Comment
          </Button>
        </div>
      )}

      {/* Comments List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : comments.length > 0 ? (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment.id} className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-400 to-orange-400 flex items-center justify-center text-white font-semibold text-sm">
                  {comment.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-800">{comment.name}</span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-400">{formatDate(comment.created_at)}</span>
                  </div>
                  <p className="text-gray-600 text-sm whitespace-pre-wrap">{comment.comment}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No comments yet. Be the first to comment!</p>
        </div>
      )}
    </div>
  );
};

// ===========================================
// BLOG POST PAGE COMPONENT
// ===========================================

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [settings, setSettings] = useState({});
  const [blogAd, setBlogAd] = useState(null);
  
  // Apply content protection based on settings
  useContentProtection(settings.disable_dev_tools);
  
  // Scroll to top when slug changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);
  
  // Parse emoji to images using Twemoji after content loads
  useEffect(() => {
    if (post && window.twemoji) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        const blogContent = document.querySelector('.blog-content');
        if (blogContent) {
          window.twemoji.parse(blogContent, {
            folder: 'svg',
            ext: '.svg'
          });
        }
      }, 100);
    }
  }, [post]);
  
  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const [postRes, allPostsRes, settingsRes, adsRes] = await Promise.all([
          axios.get(`${API}/blog-posts/${slug}`),
          axios.get(`${API}/blog-posts?published_only=true`),
          axios.get(`${API}/settings`),
          axios.get(`${API}/ads`)
        ]);
        setPost(postRes.data);
        setRelatedPosts(allPostsRes.data.filter(p => p.slug !== slug).slice(0, 3));
        setSettings(settingsRes.data);
        
        // Get active ads for blog
        const activeAds = adsRes.data.filter(ad => ad.is_active);
        if (activeAds.length > 0) {
          // Pick a random active ad
          setBlogAd(activeAds[Math.floor(Math.random() * activeAds.length)]);
        }
      } catch (error) {
        console.error("Error fetching post:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);
  
  // Helper function to get YouTube embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes('youtube.com/watch')) {
      return `https://www.youtube.com/embed/${url.split('v=')[1]?.split('&')[0]}`;
    }
    if (url.includes('youtu.be')) {
      return `https://www.youtube.com/embed/${url.split('youtu.be/')[1]}`;
    }
    return url;
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-red-500" />
      </div>
    );
  }
  
  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4">
        <AlertCircle className="h-16 w-16 text-gray-400" />
        <h1 className="text-2xl font-bold text-gray-600">Post Not Found</h1>
        <Button onClick={() => navigate("/news")} className="bg-red-500 hover:bg-red-600">
          Back to Blog
        </Button>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Open Graph Meta Tags for Social Sharing */}
      <Helmet>
        <title>{post.title} | CSBpathway</title>
        <meta name="description" content={post.excerpt || post.title} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.title} />
        {post.feature_image && <meta property="og:image" content={post.feature_image} />}
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="CSBpathway" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || post.title} />
        {post.feature_image && <meta name="twitter:image" content={post.feature_image} />}
      </Helmet>
      
      {/* Hero with Feature Image */}
      <div 
        className="relative h-[300px] md:h-[400px] bg-cover bg-center"
        style={{ 
          backgroundImage: post.feature_image 
            ? `url(${post.feature_image})` 
            : 'linear-gradient(135deg, #dc2626, #ea580c)'
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-4xl mx-auto px-4 pb-8 w-full">
            <button 
              onClick={() => navigate("/news")}
              className="text-white/80 hover:text-white mb-4 flex items-center gap-2 text-sm"
            >
              ← Back to Blog
            </button>
            <span className="inline-block text-xs font-medium text-white bg-red-500 px-3 py-1 rounded-full mb-3">
              {post.category || 'General'}
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-white mb-4 bangla-text">{post.title}</h1>
            <div className="flex items-center gap-4 text-white/80 text-sm">
              <span>By {post.author}</span>
              <span>•</span>
              <span>{new Date(post.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10">
          {post.excerpt && (
            <p className="text-lg text-gray-600 font-medium mb-6 pb-6 border-b border-gray-100 bangla-text">
              {post.excerpt}
            </p>
          )}
          
          {/* Default Ad Section - Shows only if no [AD] shortcode in content */}
          {settings.show_ads_in_blog && blogAd && !post.content.includes('[AD]') && !post.content.includes('{{AD}}') && ['banner', 'video', 'link'].includes(blogAd.ad_type) && (
            <div className="mb-8 rounded-xl overflow-hidden shadow-lg border border-gray-100">
              {blogAd.ad_type === "banner" && blogAd.image_url && (
                <a 
                  href={blogAd.link_url || "#"} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <img 
                    src={blogAd.image_url} 
                    alt={blogAd.title || "Advertisement"} 
                    className="w-full h-auto object-cover"
                  />
                </a>
              )}
              {blogAd.ad_type === "video" && blogAd.video_url && (
                <div className="aspect-video">
                  <iframe
                    src={getYouTubeEmbedUrl(blogAd.video_url)}
                    title={blogAd.title || "Video Ad"}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              {blogAd.ad_type === "link" && (
                <a 
                  href={blogAd.link_url || "#"} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block p-6 bg-gradient-to-r from-red-50 to-orange-50 hover:from-red-100 hover:to-orange-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {blogAd.image_url && (
                      <img src={blogAd.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                    )}
                    <div>
                      <h4 className="font-semibold text-gray-800 bangla-text">{blogAd.title}</h4>
                      <p className="text-sm text-red-600">Learn More →</p>
                    </div>
                  </div>
                </a>
              )}
              <div className="text-[10px] text-gray-400 text-center py-1 bg-gray-50">Sponsored</div>
            </div>
          )}
          
          {/* Video Section */}
          {post.video_url && (
            <div className="mb-8">
              <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
                <iframe 
                  src={post.video_url.includes('youtube.com/watch') 
                    ? `https://www.youtube.com/embed/${post.video_url.split('v=')[1]?.split('&')[0]}` 
                    : post.video_url.includes('youtu.be') 
                      ? `https://www.youtube.com/embed/${post.video_url.split('youtu.be/')[1]}` 
                      : post.video_url}
                  title={post.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}
          
          {/* Blog Ad Component for Shortcode */}
          {(() => {
            const renderBlogAd = () => {
              if (!settings.show_ads_in_blog || !blogAd || !['banner', 'video', 'link'].includes(blogAd.ad_type)) return null;
              return (
                <div className="my-6 rounded-xl overflow-hidden shadow-lg border border-gray-100">
                  {blogAd.ad_type === "banner" && blogAd.image_url && (
                    <a href={blogAd.link_url || "#"} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={blogAd.image_url} alt={blogAd.title || "Advertisement"} className="w-full h-auto object-cover" />
                    </a>
                  )}
                  {blogAd.ad_type === "video" && blogAd.video_url && (
                    <div className="aspect-video">
                      <iframe src={getYouTubeEmbedUrl(blogAd.video_url)} title={blogAd.title || "Video Ad"} className="w-full h-full" frameBorder="0" allowFullScreen />
                    </div>
                  )}
                  {blogAd.ad_type === "link" && (
                    <a href={blogAd.link_url || "#"} target="_blank" rel="noopener noreferrer" className="block p-6 bg-gradient-to-r from-red-50 to-orange-50 hover:from-red-100 hover:to-orange-100 transition-colors">
                      <div className="flex items-center gap-4">
                        {blogAd.image_url && <img src={blogAd.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                        <div>
                          <h4 className="font-semibold text-gray-800 bangla-text">{blogAd.title}</h4>
                          <p className="text-sm text-red-600">Learn More →</p>
                        </div>
                      </div>
                    </a>
                  )}
                  <div className="text-[10px] text-gray-400 text-center py-1 bg-gray-50">Sponsored</div>
                </div>
              );
            };
            
            return null; // Component is rendered inline below
          })()}
          
          {/* Blog Content with Smart Formatting */}
          <div className="blog-content prose prose-lg max-w-none">
            {(() => {
              // Smart content processor - converts plain text format to proper Markdown
              const processContent = (content) => {
                if (!content) return '';
                
                // Check if content already has Markdown headers (##)
                const hasMarkdownHeaders = /^#{1,3}\s/m.test(content);
                
                if (hasMarkdownHeaders) {
                  // Already Markdown formatted
                  return content;
                }
                
                // Convert emoji headers to Markdown H2
                // Pattern: emoji + space + text (at start of line or after newline)
                let processed = content
                  // Convert emoji headers like "🎓 কেন জার্মানি" to "## 🎓 কেন জার্মানি"
                  .replace(/^([🎓📋💰📝🏫✨🌟💡📌🔑✅❌⚠️🎯📊🏆💼🎒✈️🌍🏠📚🎨💻🔬⭐🇰🇷🇩🇪🇬🇧🇺🇸🇨🇦🇦🇺🇯🇵🇫🇷🇮🇹🇪🇸🇳🇱🇸🇪🇳🇴🇩🇰🇫🇮🇨🇭🇦🇹🇧🇪🇵🇱🇮🇪🇳🇿🇸🇬🇲🇾🇹🇭🇻🇳🇮🇳🇧🇩🇵🇰]+)\s+(.+)$/gm, '## $1 $2')
                  // Convert numbered patterns like "১." or "1." at line start to proper list
                  .replace(/^([১২৩৪৫৬৭৮৯০]+)\.\s*/gm, '$1. ')
                  .replace(/^(\d+)\.\s*/gm, '$1. ')
                  // Convert bullet points (•) to Markdown bullets (-)
                  .replace(/^•\s*/gm, '- ')
                  // Bold text between ** remains as is
                  // Add line breaks for better paragraph separation
                  .replace(/\n{3,}/g, '\n\n');
                
                return processed;
              };
              
              const processedContent = processContent(post.content);
              
              return (
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-gray-800 border-b-2 border-red-500 pb-3 mb-6 bangla-text" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 bangla-text" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-3 bangla-text" {...props} />,
                    p: ({node, ...props}) => <p className="text-gray-600 leading-relaxed mb-4 text-base bangla-text" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc pl-6 my-4 space-y-2" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal pl-6 my-4 space-y-2" {...props} />,
                    li: ({node, ...props}) => <li className="text-gray-600 bangla-text" {...props} />,
                    blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-red-500 pl-4 py-2 my-4 bg-red-50 rounded-r-lg italic bangla-text" {...props} />,
                    table: ({node, ...props}) => <div className="overflow-x-auto my-6"><table className="w-full border-collapse rounded-lg overflow-hidden shadow-sm border border-gray-200" {...props} /></div>,
                    thead: ({node, ...props}) => <thead className="bg-gray-100" {...props} />,
                    th: ({node, ...props}) => <th className="px-4 py-3 text-left font-bold text-gray-800 border-b-2 border-gray-300 bangla-text" {...props} />,
                    td: ({node, ...props}) => <td className="border border-gray-200 px-4 py-3 text-gray-700 bangla-text" {...props} />,
                    tr: ({node, ...props}) => <tr className="even:bg-gray-50 hover:bg-gray-100 transition-colors" {...props} />,
                    a: ({node, ...props}) => <a className="text-red-600 hover:underline" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-bold text-gray-800" {...props} />,
                    hr: ({node, ...props}) => <hr className="my-8 border-gray-200" {...props} />,
                    code: ({node, inline, ...props}) => 
                      inline ? <code className="bg-gray-100 px-2 py-1 rounded text-red-600 text-sm" {...props} /> 
                             : <code className="block bg-gray-900 text-green-400 p-4 rounded-lg my-4 overflow-x-auto" {...props} />,
                  }}
                >
                  {processedContent}
                </ReactMarkdown>
              );
            })()}
          </div>
        </div>
        
        {/* Share Section */}
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
            <ExternalLink className="h-5 w-5 text-blue-600" />
            Share this post
          </h3>
          <p className="text-xs text-gray-500 mb-4">সোশ্যাল মিডিয়ায় সুন্দর preview সহ শেয়ার করতে নিচের বাটন ব্যবহার করুন</p>
          <div className="flex flex-wrap gap-3">
            {/* Copy Share Link */}
            <Button
              onClick={async () => {
                const shareUrl = `${BACKEND_URL}/api/og/blog/${slug}`;
                try {
                  // Try modern clipboard API first
                  if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success("Blog Link Copied!");
                  } else {
                    // Fallback for HTTP or older browsers
                    const textArea = document.createElement("textarea");
                    textArea.value = shareUrl;
                    textArea.style.position = "fixed";
                    textArea.style.left = "-999999px";
                    textArea.style.top = "-999999px";
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    document.execCommand('copy');
                    textArea.remove();
                    toast.success("Blog Link Copied!");
                  }
                } catch (err) {
                  // Final fallback - show URL in prompt
                  prompt("Copy this link:", shareUrl);
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Copy Share Link
            </Button>
            
            {/* WhatsApp Share */}
            <Button
              onClick={() => {
                const shareUrl = `${BACKEND_URL}/api/og/blog/${slug}`;
                const text = encodeURIComponent(post.title);
                window.open(`https://wa.me/?text=${text}%20${encodeURIComponent(shareUrl)}`, '_blank');
              }}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </Button>
            
            {/* Facebook Share */}
            <Button
              onClick={() => {
                const shareUrl = `${BACKEND_URL}/api/og/blog/${slug}`;
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
              }}
              className="bg-[#1877f2] hover:bg-[#166fe5] text-white"
            >
              <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </Button>
          </div>
        </div>
        
        {/* Comments Section */}
        <BlogCommentsSection slug={slug} />
        
        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Related Posts</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relPost) => (
                <article 
                  key={relPost.slug}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/news/${relPost.slug}`)}
                >
                  <div className="h-32 bg-gray-100">
                    {relPost.feature_image ? (
                      <img src={relPost.feature_image} alt={relPost.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-100 to-orange-100">
                        <Newspaper className="h-8 w-8 text-red-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-800 line-clamp-2 hover:text-red-600 transition-colors">
                      {relPost.title}
                    </h3>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      {/* Footer */}
      <Footer />
    </div>
  );
};

// ===========================================
// COUNTRY PAGE COMPONENT
// ===========================================

const CountryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  
  // Get user's selected language from sessionStorage (set from form)
  const userLang = sessionStorage.getItem('selected_language') || 'en';
  const isBangla = userLang === 'bn';
  
  // Apply content protection based on settings
  useContentProtection(settings.disable_dev_tools);
  
  // Contact modals
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [message, setMessage] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Voice recording states
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  
  const MAX_RECORDING_TIME = 120;
  
  // Helper function to get localized content
  const getLocalizedContent = (field) => {
    if (!pageData) return '';
    if (isBangla && pageData[`${field}_bn`]) {
      return pageData[`${field}_bn`];
    }
    return pageData[field] || '';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pageRes, settingsRes] = await Promise.all([
          axios.get(`${API}/country-pages/${slug}`),
          axios.get(`${API}/settings`)
        ]);
        setPageData(pageRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Country page not found");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const mimeType = mediaRecorderRef.current.mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start(100);
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_TIME - 1) {
            stopRecording();
            return MAX_RECORDING_TIME;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (error) {
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const deleteRecording = () => {
    if (playingAudio) playingAudio.pause();
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setIsPlaying(false);
  };

  const togglePlayback = () => {
    if (!audioUrl) return;
    if (isPlaying && playingAudio) {
      playingAudio.pause();
      setIsPlaying(false);
    } else {
      const audio = new Audio(audioUrl);
      audio.onended = () => setIsPlaying(false);
      audio.play().then(() => {
        setPlayingAudio(audio);
        setIsPlaying(true);
      });
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Send message
  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error("Please write a message");
      return;
    }
    if (!contactPhone.trim()) {
      toast.error("Please enter your phone number");
      return;
    }
    setSendingMessage(true);
    try {
      await axios.post(`${API}/contact`, {
        full_name: contactName.trim() || "Country Page Visitor",
        phone: contactPhone.trim(),
        email: "",
        academic_level: "",
        subject_group: "",
        year_of_passing: 2024,
        gpa_cgpa: 0,
        applying_for: "",
        english_test_type: "without_any",
        calculated_gap: 0,
        eligible_countries: [pageData.country_name],
        suggestions: {},
        message: `[From ${pageData.country_name} Page] ${message}`
      });
      toast.success("Message sent successfully!");
      setShowMessageModal(false);
      setMessage("");
      setContactName("");
      setContactPhone("");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  // Send voice
  const handleSendVoice = async () => {
    if (!audioBlob) {
      toast.error("Please record a voice message");
      return;
    }
    if (!contactPhone.trim()) {
      toast.error("Please enter your phone number");
      return;
    }
    setSendingMessage(true);
    try {
      // Upload voice first
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice_message.webm');
      const uploadRes = await axios.post(`${API}/upload-voice`, formData);
      
      await axios.post(`${API}/contact`, {
        full_name: contactName.trim() || "Country Page Visitor",
        phone: contactPhone.trim(),
        email: "",
        academic_level: "",
        subject_group: "",
        year_of_passing: 2024,
        gpa_cgpa: 0,
        applying_for: "",
        english_test_type: "without_any",
        calculated_gap: 0,
        eligible_countries: [pageData.country_name],
        suggestions: {},
        message: `[Voice from ${pageData.country_name} Page]`,
        voice_message_url: uploadRes.data.file_url
      });
      toast.success("Voice message sent successfully!");
      setShowVoiceModal(false);
      deleteRecording();
      setContactName("");
      setContactPhone("");
    } catch (error) {
      toast.error("Failed to send voice message");
    } finally {
      setSendingMessage(false);
    }
  };

  // Go back with state preserved
  const handleGoBack = () => {
    if (location.state?.fromEligibility) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? `https://www.youtube.com/embed/${match[2]}` : url;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-red-500" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4">
        <AlertCircle className="h-16 w-16 text-gray-400" />
        <h1 className="text-2xl font-bold text-gray-600">Country Page Not Found</h1>
        <Button onClick={() => navigate("/")} className="bg-red-500 hover:bg-red-600">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - Mobile Optimized */}
      <div 
        className="hero-video-section h-[280px] sm:h-[320px] md:h-[400px] lg:h-[450px] relative"
        style={{ 
          backgroundImage: `url(${pageData.hero_image || 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1920'})`, 
          backgroundSize: 'cover', 
          backgroundPosition: 'center' 
        }}
      >
        <div className="hero-content h-full flex items-center">
          <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6 md:pt-8">
            <Button 
              variant="ghost" 
              onClick={handleGoBack}
              className="mb-3 sm:mb-4 text-white hover:bg-white/20 text-sm sm:text-base px-2 sm:px-4"
            >
              ← {isBangla ? 'যোগ্যতা যাচাইয়ে ফিরুন' : 'Back to Eligibility Check'}
            </Button>
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl flex-shrink-0">
                <MapPin className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <h1 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white drop-shadow-lg leading-tight ${isBangla ? 'bangla-text' : ''}`}>
                {getLocalizedContent('hero_title') || `${isBangla ? '' : 'Study in '}${isBangla && pageData.country_name_bn ? pageData.country_name_bn : pageData.country_name}${isBangla ? 'তে পড়াশোনা করুন' : ''}`}
              </h1>
            </div>
            {getLocalizedContent('description') && (
              <p className={`text-sm sm:text-base md:text-lg text-white/90 max-w-2xl ml-0 sm:ml-12 md:ml-16 line-clamp-3 sm:line-clamp-none ${isBangla ? 'bangla-text' : ''}`}>{getLocalizedContent('description')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Content Section - Mobile Optimized */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 -mt-8 sm:-mt-10 md:-mt-12 relative z-20 pb-8 sm:pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          
          {/* Document Checklist */}
          <div className="lg:col-span-2">
            <Card className="glassy-card shadow-xl border-0 rounded-xl sm:rounded-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-red-500 to-red-600 text-white py-3 sm:py-4 px-4 sm:px-6">
                <CardTitle className={`flex items-center gap-2 sm:gap-3 text-base sm:text-lg md:text-xl ${isBangla ? 'bangla-text' : ''}`}>
                  <FileText className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                  <span className="leading-tight">{isBangla ? `${pageData.country_name_bn || pageData.country_name} এর জন্য প্রয়োজনীয় কাগজপত্র` : `Document Checklist for ${pageData.country_name}`}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 md:p-6">
                {pageData.document_checklist && pageData.document_checklist.length > 0 ? (
                  <div className="space-y-3 sm:space-y-4">
                    {pageData.document_checklist.map((item, index) => (
                      <div key={item.id || index} className="flex items-start gap-2 sm:gap-3 md:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-gray-100 transition-colors">
                        <div className={`flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 text-xs sm:text-sm rounded-full flex items-center justify-center font-medium ${item.required ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-800 text-sm sm:text-base flex flex-wrap items-center gap-1 sm:gap-2">
                            <span className="break-words">{item.title}</span>
                            {item.required && <span className="text-[10px] sm:text-xs bg-red-100 text-red-600 px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap">{isBangla ? 'আবশ্যক' : 'Required'}</span>}
                          </h4>
                          {item.description && <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">{item.description}</p>}
                        </div>
                        <CheckCircle className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${item.required ? 'text-red-400' : 'text-green-400'}`} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 sm:py-12 text-gray-500">
                    <FileText className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 text-gray-300" />
                    <p className="text-sm sm:text-base">{isBangla ? 'এখনো কোনো কাগজপত্র তালিকাভুক্ত করা হয়নি।' : 'No documents listed yet.'}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {getLocalizedContent('additional_info') && (
              <Card className="mt-4 sm:mt-6 shadow-lg border-0 rounded-xl sm:rounded-2xl">
                <CardHeader className="py-3 sm:py-4 px-4 sm:px-6"><CardTitle className={`text-base sm:text-lg ${isBangla ? 'bangla-text' : ''}`}>{isBangla ? 'অতিরিক্ত তথ্য' : 'Additional Information'}</CardTitle></CardHeader>
                <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                  <div className={`prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap text-sm sm:text-base ${isBangla ? 'bangla-text' : ''}`}>{getLocalizedContent('additional_info')}</div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column */}
          <div className="lg:col-span-1">
            {/* Video */}
            {pageData.video_url ? (
              <Card className="shadow-xl border-0 rounded-xl sm:rounded-2xl overflow-hidden lg:sticky lg:top-6">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 sm:py-4 px-4 sm:px-6">
                  <CardTitle className={`flex items-center gap-2 text-base sm:text-lg ${isBangla ? 'bangla-text' : ''}`}>
                    <Play className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" /><span>{getLocalizedContent('video_title') || (isBangla ? "ভিডিও গাইড দেখুন" : "Watch Video Guide")}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="aspect-video">
                    <iframe src={getYouTubeEmbedUrl(pageData.video_url)} title={`${pageData.country_name} Guide`} className="w-full h-full" frameBorder="0" allowFullScreen></iframe>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="shadow-lg border-0 rounded-xl sm:rounded-2xl">
                <CardContent className="p-6 sm:p-8 text-center text-gray-500">
                  <Play className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 text-gray-300" /><p className="text-sm sm:text-base">{isBangla ? 'ভিডিও শীঘ্রই আসছে' : 'Video coming soon'}</p>
                </CardContent>
              </Card>
            )}

            {/* Contact Card with Icons - Mobile Optimized */}
            <Card className="mt-4 sm:mt-6 shadow-lg border-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-gray-800 to-gray-900 text-white overflow-hidden">
              <CardContent className="p-4 sm:p-6">
                <h3 className={`text-lg sm:text-xl font-bold mb-1 sm:mb-2 text-center ${isBangla ? 'bangla-text' : ''}`}>{isBangla ? 'সাহায্য প্রয়োজন?' : 'Need Help?'}</h3>
                <p className={`text-gray-300 text-xs sm:text-sm mb-4 sm:mb-6 text-center ${isBangla ? 'bangla-text' : ''}`}>
                  {isBangla 
                    ? `${pageData.country_name_bn || pageData.country_name}তে পড়াশোনার বিষয়ে আমাদের সাথে যোগাযোগ করুন` 
                    : `Contact us for guidance about studying in ${pageData.country_name}`}
                </p>
                
                {/* Contact Icons */}
                <div className="flex justify-center gap-2 sm:gap-4 mb-2 sm:mb-4">
                  {/* WhatsApp */}
                  <button
                    onClick={() => setShowWhatsAppModal(true)}
                    className="flex flex-col items-center gap-1.5 sm:gap-2 p-2.5 sm:p-4 bg-green-500/20 rounded-lg sm:rounded-xl hover:bg-green-500/30 transition-colors group"
                  >
                    <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-green-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <WhatsAppIcon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                    </div>
                    <span className="text-[10px] sm:text-xs text-green-400">WhatsApp</span>
                  </button>
                  
                  {/* Call - Mobile Only */}
                  <button
                    onClick={() => window.location.href = `tel:${settings.phone_number || '+8801XXXXXXXXX'}`}
                    className="flex md:hidden flex-col items-center gap-1.5 sm:gap-2 p-2.5 sm:p-4 bg-orange-500/20 rounded-lg sm:rounded-xl hover:bg-orange-500/30 transition-colors group"
                  >
                    <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-orange-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Phone className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                    </div>
                    <span className="text-[10px] sm:text-xs text-orange-400">Call</span>
                  </button>
                  
                  {/* Message */}
                  <button
                    onClick={() => setShowMessageModal(true)}
                    className="flex flex-col items-center gap-1.5 sm:gap-2 p-2.5 sm:p-4 bg-blue-500/20 rounded-lg sm:rounded-xl hover:bg-blue-500/30 transition-colors group"
                  >
                    <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-blue-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <MessageCircle className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                    </div>
                    <span className="text-[10px] sm:text-xs text-blue-400">Message</span>
                  </button>
                  
                  {/* Voice */}
                  <button
                    onClick={() => setShowVoiceModal(true)}
                    className="flex flex-col items-center gap-1.5 sm:gap-2 p-2.5 sm:p-4 bg-purple-500/20 rounded-lg sm:rounded-xl hover:bg-purple-500/30 transition-colors group"
                  >
                    <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Mic className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                    </div>
                    <span className="text-[10px] sm:text-xs text-purple-400">Voice</span>
                  </button>
                </div>
              </CardContent>
            </Card>
            
            {/* Country Page Ad Section */}
            <CountryPageAdSection slug={slug} />
          </div>
        </div>
      </div>

      {/* WhatsApp Modal */}
      <Dialog open={showWhatsAppModal} onOpenChange={setShowWhatsAppModal}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WhatsAppIcon className="w-6 h-6 text-green-500" />
              Contact via WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <WhatsAppIcon className="w-10 h-10 text-green-500" />
            </div>
            <p className="text-gray-600 mb-6">Click the button below to start a WhatsApp conversation with our consultant.</p>
            <Button
              onClick={() => window.open(settings.whatsapp_link || "https://wa.me/8801XXXXXXXXX", "_blank")}
              className="w-full bg-green-500 hover:bg-green-600 text-white py-6 text-lg rounded-xl"
            >
              <WhatsAppIcon className="w-5 h-5 mr-2" />
              Open WhatsApp
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="sm:max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-blue-500" />
              Send a Message
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Name and Phone in one row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Your Name</Label>
                <Input
                  placeholder="Enter your name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Phone Number *</Label>
                <Input
                  placeholder="01XXXXXXXXX"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  required
                />
              </div>
            </div>
            {/* Message */}
            <div>
              <Label className="text-sm text-gray-600 mb-1 block">Your Message *</Label>
              <Textarea
                placeholder="Write your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageModal(false)}>Cancel</Button>
            <Button 
              onClick={handleSendMessage}
              disabled={sendingMessage || !message.trim() || !contactPhone.trim()}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {sendingMessage ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Voice Modal */}
      <Dialog open={showVoiceModal} onOpenChange={(open) => {
        setShowVoiceModal(open);
        if (!open) deleteRecording();
      }}>
        <DialogContent className="sm:max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mic className="w-6 h-6 text-purple-500" />
              Record Voice Message
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Name and Phone in one row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Your Name</Label>
                <Input
                  placeholder="Enter your name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Phone Number *</Label>
                <Input
                  placeholder="01XXXXXXXXX"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  required
                />
              </div>
            </div>
            
            {/* Voice Recording */}
            <div className="text-center pt-2">
              {!audioUrl ? (
                <div className="space-y-4">
                  {!isRecording ? (
                    <button
                      onClick={startRecording}
                      className="w-24 h-24 mx-auto rounded-full bg-purple-500 hover:bg-purple-600 flex items-center justify-center transition-colors"
                    >
                      <Mic className="w-10 h-10 text-white" />
                    </button>
                  ) : (
                    <button
                      onClick={stopRecording}
                      className="w-24 h-24 mx-auto rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center animate-pulse"
                    >
                      <Square className="w-8 h-8 text-white" />
                    </button>
                  )}
                  
                  {isRecording ? (
                    <div className="text-red-500 font-medium">
                      Recording... {formatTime(recordingTime)} / {formatTime(MAX_RECORDING_TIME)}
                    </div>
                  ) : (
                    <p className="text-gray-500">Click to start recording (max 2 mins)</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-4 p-4 bg-gray-100 rounded-xl">
                    <button
                      onClick={togglePlayback}
                      className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center"
                    >
                      {isPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-1" />}
                    </button>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-800">Voice recorded</p>
                      <p className="text-sm text-gray-500">{formatTime(recordingTime)}</p>
                    </div>
                    <button onClick={deleteRecording} className="text-red-500 hover:text-red-600">
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoiceModal(false)}>Cancel</Button>
            <Button 
              onClick={handleSendVoice}
              disabled={sendingMessage || !audioBlob || !contactPhone.trim()}
              className="bg-purple-500 hover:bg-purple-600"
            >
              {sendingMessage ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Voice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      {/* Footer */}
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Header />
        <VisitorTracker />
        <PopupAd />
        <FloatingWhatsApp />
        <div className="pt-16 md:pt-20"> {/* Padding for fixed header */}
          <Routes>
            <Route path="/" element={<EligibilityForm />} />
            <Route path="/cst" element={<AdminLogin />} />
            <Route path="/cst/dashboard" element={<ProtectedAdminPanel />} />
            <Route path="/country/:slug" element={<CountryPage />} />
            <Route path="/news" element={<BlogPage />} />
            <Route path="/news/:slug" element={<BlogPostPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </div>
      </BrowserRouter>
    </div>
  );
}

export default App;
